/*!
 * Rich Form Builder , developer storefront template runtime
 * Version 0.0.0 · https://northjstech.com
 *
 * Pure vanilla JavaScript: no dependencies, no framework, no build
 * step required in your theme. Reads its configuration from the
 * app-owned `form` metaobject emitted by snippets/rfb-form-config.liquid
 * and renders into [data-rfbd-form] hosts.
 *
 * Customization ladder (prefer the earliest rung that works):
 *   1. Appearance settings in the Rich Form Builder admin (no code).
 *   2. Override the --rfb-* custom properties on .rfbd in your CSS.
 *   3. Target the rfbd-* class hooks / data-rfbd-el attributes.
 *   4. Listen to the rfb:* DOM events (rfb:before-submit is cancelable).
 *   5. RFBD.define(type, overrides) to replace any element renderer.
 *   6. Fork this file. It is yours.
 */
"use strict";
(() => {
  // dev-template/src/core/conditions.js
  var UNARY_OPERATORS = ["isEmpty", "isNotEmpty"];
  function isEmptyValue(value) {
    if (value === null || value === void 0) return true;
    if (typeof value === "string") return value.trim() === "";
    if (Array.isArray(value)) return value.length === 0;
    if (typeof value === "object") return Object.keys(value).length === 0;
    return false;
  }
  function toComparableList(value) {
    if (Array.isArray(value)) {
      return value.map(
        (entry) => typeof entry === "object" && entry !== null ? String(entry) : entry
      );
    }
    return [value];
  }
  function looseEquals(left, right) {
    if (left === right) return true;
    if (left === null || left === void 0) return false;
    if (right === null || right === void 0) return false;
    return String(left) === String(right);
  }
  function toNumber(value) {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
  function compareNumerically(actual, expected, compare) {
    const actualNumber = toNumber(actual);
    const expectedNumber = toNumber(expected);
    if (actualNumber === null || expectedNumber === null) return false;
    return compare(actualNumber, expectedNumber);
  }
  var OPERATORS = {
    equals: (actual, expected) => toComparableList(actual).some((entry) => looseEquals(entry, expected)),
    notEquals: (actual, expected) => !toComparableList(actual).some((entry) => looseEquals(entry, expected)),
    contains: (actual, expected) => {
      if (Array.isArray(actual)) {
        return toComparableList(actual).some((entry) => looseEquals(entry, expected));
      }
      if (actual === null || actual === void 0 || expected === null) return false;
      return String(actual).toLowerCase().includes(String(expected).toLowerCase());
    },
    notContains: (actual, expected) => !OPERATORS.contains(actual, expected),
    isEmpty: (actual) => isEmptyValue(actual),
    isNotEmpty: (actual) => !isEmptyValue(actual),
    greaterThan: (actual, expected) => compareNumerically(actual, expected, (a, b) => a > b),
    lessThan: (actual, expected) => compareNumerically(actual, expected, (a, b) => a < b)
  };
  function evaluateRule(rule, context) {
    const operator = OPERATORS[rule.operator];
    if (!operator) return true;
    const actual = context[rule.source];
    const expected = UNARY_OPERATORS.includes(rule.operator) ? void 0 : rule.value;
    return operator(actual, expected);
  }
  function evaluateCondition(condition, context) {
    if (!condition) return true;
    if (!("rules" in condition)) return evaluateRule(condition, context);
    if (condition.rules.length === 0) return true;
    return condition.match === "all" ? condition.rules.every((rule) => evaluateRule(rule, context)) : condition.rules.some((rule) => evaluateRule(rule, context));
  }
  function isElementActive(element, values) {
    if (element.hidden) return false;
    return evaluateCondition(element.conditions, values);
  }

  // dev-template/src/core/i18n.js
  var DEFAULT_LOCALE = "en";
  function baseLanguage(locale) {
    const separatorIndex = locale.indexOf("-");
    return separatorIndex === -1 ? locale : locale.slice(0, separatorIndex);
  }
  function resolveTranslatable(value, locale = DEFAULT_LOCALE) {
    if (!value) return "";
    if (typeof value === "string") return value;
    const candidates = [locale, baseLanguage(locale), DEFAULT_LOCALE];
    for (const candidate of candidates) {
      const resolved = value[candidate];
      if (typeof resolved === "string" && resolved !== "") return resolved;
    }
    const firstPopulated = Object.values(value).find(
      (entry) => typeof entry === "string" && entry !== ""
    );
    return firstPopulated ?? "";
  }

  // dev-template/src/core/settings.js
  function readString(bag, id, fallback = "") {
    const value = bag?.[id];
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    return fallback;
  }
  function readNumber(bag, id, fallback) {
    const value = bag?.[id];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return fallback;
  }
  function readOptionalNumber(bag, id) {
    const value = bag?.[id];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
    return null;
  }
  function readBoolean(bag, id, fallback = false) {
    const value = bag?.[id];
    return typeof value === "boolean" ? value : fallback;
  }
  function readStringList(bag, id) {
    const value = bag?.[id];
    if (!Array.isArray(value)) return [];
    return value.filter((entry) => typeof entry === "string");
  }
  function readTranslatable(bag, id) {
    const value = bag?.[id];
    if (typeof value === "string") return { [DEFAULT_LOCALE]: value };
    if (typeof value === "object" && value !== null && !Array.isArray(value)) {
      return value;
    }
    return void 0;
  }
  function readText(bag, id, locale) {
    return resolveTranslatable(readTranslatable(bag, id), locale);
  }
  function readRawOptions(bag, id = "options") {
    const value = bag?.[id];
    return Array.isArray(value) ? value : [];
  }
  function optionSubmittedValue(option) {
    return option.value ?? resolveTranslatable(option.label, DEFAULT_LOCALE);
  }
  function resolveOptions(bag, locale, id = "options") {
    return readRawOptions(bag, id).map((option) => ({
      id: option.id ?? optionSubmittedValue(option),
      label: resolveTranslatable(option.label, locale),
      value: optionSubmittedValue(option),
      isDefault: option.isDefault === true,
      image: typeof option.image === "string" ? option.image : "",
      color: typeof option.color === "string" ? option.color : ""
    }));
  }
  function readOptionsPerLine(bag, fallback = 1) {
    const parsed = Number(readString(bag, "optionsPerLine", String(fallback)));
    return Number.isFinite(parsed) && parsed > 0 ? Math.min(parsed, 5) : fallback;
  }

  // dev-template/src/core/util.js
  function h(tag, attrs, ...children) {
    const node = document.createElement(tag);
    for (const [key, value] of Object.entries(attrs ?? {})) {
      if (value === null || value === void 0 || value === false) continue;
      if (key === "class") {
        node.className = String(value);
      } else if (key === "dataset") {
        for (const [dataKey, dataValue] of Object.entries(value)) {
          if (dataValue !== null && dataValue !== void 0) {
            node.dataset[dataKey] = String(dataValue);
          }
        }
      } else if (key === "style" && typeof value === "object") {
        for (const [prop, propValue] of Object.entries(value)) {
          node.style.setProperty(prop, String(propValue));
        }
      } else if (key.startsWith("on") && typeof value === "function") {
        node.addEventListener(key.slice(2).toLowerCase(), value);
      } else if (value === true) {
        node.setAttribute(key, "");
      } else {
        node.setAttribute(key, String(value));
      }
    }
    for (const child of children.flat(Infinity)) {
      if (child === null || child === void 0 || child === false) continue;
      node.append(typeof child === "string" ? document.createTextNode(child) : child);
    }
    return node;
  }
  function cx(...parts) {
    return parts.filter(Boolean).join(" ");
  }
  function uuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
      const random = Math.random() * 16 | 0;
      const value = char === "x" ? random : random & 3 | 8;
      return value.toString(16);
    });
  }
  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }
  function toNumber2(value) {
    if (typeof value === "number") return Number.isFinite(value) ? value : null;
    if (typeof value === "string" && value.trim() !== "") {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : null;
    }
    return null;
  }
  function parseJsonScript(script) {
    if (!script) return null;
    try {
      return JSON.parse(script.textContent ?? "");
    } catch (error) {
      console.error("[rfbd] config JSON could not be parsed", error);
      return null;
    }
  }
  function formatMessage(template, replacements) {
    return template.replace(
      /\{(\w+)\}/g,
      (match, key) => key in replacements ? String(replacements[key]) : match
    );
  }

  // dev-template/src/runtime/icons.js
  var FIELD_ICONS = {
    user: "M12 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM4 20c0-3.3 3.6-6 8-6s8 2.7 8 6",
    envelope: "M3 7l9 6 9-6M3 7h18v10H3V7z",
    phone: "M6.5 3h3l1.5 4-2 1.5a12.5 12.5 0 0 0 6.5 6.5L17 13l4 1.5v3a2 2 0 0 1-2.2 2A17 17 0 0 1 3 5.2 2 2 0 0 1 5 3h1.5z",
    globe: "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM3 12h18M12 3c2.5 2.4 4 5.5 4 9s-1.5 6.6-4 9c-2.5-2.4-4-5.5-4-9s1.5-6.6 4-9z",
    home: "M4 11l8-7 8 7M6 10v10h12V10",
    calendar: "M4 6h16v14H4V6zM8 3v4M16 3v4M4 10h16",
    tag: "M3 12V4h8l9 9-8 8-9-9zM7.5 7.5h.01",
    cart: "M3 4h2l2.6 11h9.9L20 7H6M9.5 19.5h.01M17 19.5h.01",
    lock: "M6 11h12v9H6v-9zM9 11V8a3 3 0 0 1 6 0v3",
    hash: "M6 9h14M4 15h14M10.5 4l-2 16M17 4l-2 16",
    attachment: "M20.5 11.5l-8.5 8.5a5 5 0 0 1-7-7l9-9a3.5 3.5 0 0 1 5 5l-9 9a2 2 0 0 1-2.8-2.8l8-8",
    chat: "M21 12a8 8 0 0 1-8 8H7l-4 3v-6.5A8 8 0 1 1 21 12z",
    question: "M9.1 9a3 3 0 1 1 4 2.8c-.7.3-1.1 1-1.1 1.7v.5M12 17.5h.01",
    star: "M12 3.5l2.6 5.3 5.9.9-4.3 4.1 1 5.8-5.2-2.7-5.2 2.7 1-5.8L3.5 9.7l5.9-.9L12 3.5z",
    heart: "M12 20.5l-7.4-7.2a4.8 4.8 0 0 1 0-6.9 5 5 0 0 1 7 0l.4.4.4-.4a5 5 0 0 1 7 0 4.8 4.8 0 0 1 0 6.9L12 20.5z",
    "thumb-up": "M7 10.5v10M7 20.5H4.5v-10H7m0 10h9.4a2 2 0 0 0 2-1.6l1.3-6a2 2 0 0 0-2-2.4h-4.5l.9-4.2a1.8 1.8 0 0 0-3.2-1.5L7 10.5",
    "check-circle": "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM8.2 12.3l2.6 2.7 5-5.5",
    "paper-plane": "M21 3.5L3.5 10.7l7 2.8 2.8 7L21 3.5zM10.5 13.5L21 3.5",
    gift: "M4.5 11.5h15M4.5 11.5V20h15v-8.5M3.5 7.5h17v4h-17v-4zM12 7.5V20M12 7.5c-1.7 0-4.2-.6-4.2-2.4 0-1.7 2-2.3 3-1.3 1 .9 1.2 2.5 1.2 3.7zm0 0c1.7 0 4.2-.6 4.2-2.4 0-1.7-2-2.3-3-1.3-1 .9-1.2 2.5-1.2 3.7z",
    sparkles: "M12 4.5l1.8 4.7 4.7 1.8-4.7 1.8L12 17.5l-1.8-4.7-4.7-1.8 4.7-1.8L12 4.5zM19 16.5l.7 1.8 1.8.7-1.8.7-.7 1.8-.7-1.8-1.8-.7 1.8-.7.7-1.8zM5 3l.6 1.6L7.2 5.2l-1.6.6L5 7.4l-.6-1.6-1.6-.6 1.6-.6L5 3z"
  };
  var LAUNCHER_ICONS = {
    envelope: [
      "M1.5 8.67v8.58a3 3 0 0 0 3 3h15a3 3 0 0 0 3-3V8.67l-8.928 5.493a3 3 0 0 1-3.144 0L1.5 8.67Z",
      "M22.5 6.908V6.75a3 3 0 0 0-3-3h-15a3 3 0 0 0-3 3v.158l9.714 5.978a1.5 1.5 0 0 0 1.572 0L22.5 6.908Z"
    ],
    chat: [
      "M4.804 21.644A6.707 6.707 0 0 0 6 21.75a6.721 6.721 0 0 0 3.583-1.029c.774.182 1.584.279 2.417.279 5.322 0 9.75-3.97 9.75-9 0-5.03-4.428-9-9.75-9s-9.75 3.97-9.75 9c0 2.409 1.025 4.587 2.674 6.192.232.226.277.428.254.543a3.73 3.73 0 0 1-.814 1.686.75.75 0 0 0 .44 1.223ZM8.25 10.875a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25ZM10.875 12a1.125 1.125 0 1 1 2.25 0 1.125 1.125 0 0 1-2.25 0Zm4.875-1.125a1.125 1.125 0 1 0 0 2.25 1.125 1.125 0 0 0 0-2.25Z"
    ],
    conversation: [
      "M4.913 2.658c2.075-.27 4.19-.408 6.337-.408 2.147 0 4.262.139 6.337.408 1.922.25 3.291 1.861 3.405 3.727a4.403 4.403 0 0 0-1.032-.211 50.89 50.89 0 0 0-8.42 0c-2.358.196-4.04 2.19-4.04 4.434v4.286a4.47 4.47 0 0 0 2.433 3.984L7.28 21.53A.75.75 0 0 1 6 21v-4.03a48.527 48.527 0 0 1-1.087-.128C2.905 16.58 1.5 14.833 1.5 12.862V6.638c0-1.97 1.405-3.718 3.413-3.979Z",
      "M15.75 7.5c-1.376 0-2.739.057-4.086.169C10.124 7.797 9 9.103 9 10.609v4.285c0 1.507 1.128 2.814 2.67 2.94 1.243.102 2.5.157 3.768.165l2.782 2.781a.75.75 0 0 0 1.28-.53v-2.39l.33-.026c1.542-.125 2.67-1.433 2.67-2.94v-4.286c0-1.505-1.125-2.811-2.664-2.94A49.392 49.392 0 0 0 15.75 7.5Z"
    ],
    send: [
      "M3.478 2.404a.75.75 0 0 0-.926.941l2.432 7.905H13.5a.75.75 0 0 1 0 1.5H4.984l-2.432 7.905a.75.75 0 0 0 .926.94 60.519 60.519 0 0 0 18.445-8.986.75.75 0 0 0 0-1.218A60.517 60.517 0 0 0 3.478 2.404Z"
    ],
    compose: [
      "M21.731 2.269a2.625 2.625 0 0 0-3.712 0l-1.157 1.157 3.712 3.712 1.157-1.157a2.625 2.625 0 0 0 0-3.712ZM19.513 8.199l-3.712-3.712-8.4 8.4a5.25 5.25 0 0 0-1.32 2.214l-.8 2.685a.75.75 0 0 0 .933.933l2.685-.8a5.25 5.25 0 0 0 2.214-1.32l8.4-8.4Z",
      "M5.25 5.25a3 3 0 0 0-3 3v10.5a3 3 0 0 0 3 3h10.5a3 3 0 0 0 3-3V13.5a.75.75 0 0 0-1.5 0v5.25a1.5 1.5 0 0 1-1.5 1.5H5.25a1.5 1.5 0 0 1-1.5-1.5V8.25a1.5 1.5 0 0 1 1.5-1.5h5.25a.75.75 0 0 0 0-1.5H5.25Z"
    ],
    survey: [
      "M7.502 6h7.128A3.375 3.375 0 0 1 18 9.375v9.375a3 3 0 0 0 3-3V6.108c0-1.505-1.125-2.811-2.664-2.94a48.972 48.972 0 0 0-.673-.05A3 3 0 0 0 15 1.5h-1.5a3 3 0 0 0-2.663 1.618c-.225.015-.45.032-.673.05C8.662 3.295 7.554 4.542 7.502 6ZM13.5 3A1.5 1.5 0 0 0 12 4.5h4.5A1.5 1.5 0 0 0 15 3h-1.5Z",
      "M3 9.375C3 8.339 3.84 7.5 4.875 7.5h9.75c1.036 0 1.875.84 1.875 1.875v11.25c0 1.035-.84 1.875-1.875 1.875h-9.75A1.875 1.875 0 0 1 3 20.625V9.375ZM6 12a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V12Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75ZM6 15a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V15Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75ZM6 18a.75.75 0 0 1 .75-.75h.008a.75.75 0 0 1 .75.75v.008a.75.75 0 0 1-.75.75H6.75a.75.75 0 0 1-.75-.75V18Zm2.25 0a.75.75 0 0 1 .75-.75h3.75a.75.75 0 0 1 0 1.5H9a.75.75 0 0 1-.75-.75Z"
    ],
    question: [
      "M2.25 12c0-5.385 4.365-9.75 9.75-9.75s9.75 4.365 9.75 9.75-4.365 9.75-9.75 9.75S2.25 17.385 2.25 12Zm11.378-3.917c-.89-.777-2.366-.777-3.255 0a.75.75 0 0 1-.988-1.129c1.454-1.272 3.776-1.272 5.23 0 1.513 1.324 1.513 3.518 0 4.842a3.75 3.75 0 0 1-.837.552c-.676.328-1.028.774-1.028 1.152v.75a.75.75 0 0 1-1.5 0v-.75c0-1.279 1.06-2.107 1.875-2.502.182-.088.351-.199.503-.331.83-.727.83-1.857 0-2.584ZM12 18a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z"
    ],
    lifebuoy: [
      "M19.449 8.448 16.388 11a4.52 4.52 0 0 1 0 2.002l3.061 2.55a8.275 8.275 0 0 0 0-7.103ZM15.552 19.45 13 16.388a4.52 4.52 0 0 1-2.002 0l-2.55 3.061a8.275 8.275 0 0 0 7.103 0ZM4.55 15.552 7.612 13a4.52 4.52 0 0 1 0-2.002L4.551 8.45a8.275 8.275 0 0 0 0 7.103ZM8.448 4.55 11 7.612a4.52 4.52 0 0 1 2.002 0l2.55-3.061a8.275 8.275 0 0 0-7.103 0Zm8.657-.86a9.776 9.776 0 0 1 1.79 1.415 9.776 9.776 0 0 1 1.414 1.788 9.764 9.764 0 0 1 0 10.211 9.777 9.777 0 0 1-1.415 1.79 9.777 9.777 0 0 1-1.788 1.414 9.764 9.764 0 0 1-10.212 0 9.776 9.776 0 0 1-1.788-1.415 9.776 9.776 0 0 1-1.415-1.788 9.764 9.764 0 0 1 0-10.212 9.774 9.774 0 0 1 1.415-1.788A9.774 9.774 0 0 1 6.894 3.69a9.764 9.764 0 0 1 10.211 0ZM14.121 9.88a2.985 2.985 0 0 0-1.11-.704 3.015 3.015 0 0 0-2.022 0 2.985 2.985 0 0 0-1.11.704c-.326.325-.56.705-.704 1.11a3.015 3.015 0 0 0 0 2.022c.144.405.378.785.704 1.11.325.326.705.56 1.11.704.652.233 1.37.233 2.022 0a2.985 2.985 0 0 0 1.11-.704c.326-.325.56-.705.704-1.11a3.016 3.016 0 0 0 0-2.022 2.985 2.985 0 0 0-.704-1.11Z"
    ],
    phone: [
      "M1.5 4.5a3 3 0 0 1 3-3h1.372c.86 0 1.61.586 1.819 1.42l1.105 4.423a1.875 1.875 0 0 1-.694 1.955l-1.293.97c-.135.101-.164.249-.126.352a11.285 11.285 0 0 0 6.697 6.697c.103.038.25.009.352-.126l.97-1.293a1.875 1.875 0 0 1 1.955-.694l4.423 1.105c.834.209 1.42.959 1.42 1.82V19.5a3 3 0 0 1-3 3h-2.25C8.552 22.5 1.5 15.448 1.5 6.75V4.5Z"
    ],
    calendar: [
      "M12.75 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM7.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM8.25 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM9.75 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM10.5 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM12 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM12.75 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM14.25 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 17.25a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 15.75a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5ZM15 12.75a.75.75 0 1 1-1.5 0 .75.75 0 0 1 1.5 0ZM16.5 13.5a.75.75 0 1 0 0-1.5.75.75 0 0 0 0 1.5Z",
      "M6.75 2.25A.75.75 0 0 1 7.5 3v1.5h9V3A.75.75 0 0 1 18 3v1.5h.75a3 3 0 0 1 3 3v11.25a3 3 0 0 1-3 3H5.25a3 3 0 0 1-3-3V7.5a3 3 0 0 1 3-3H6V3a.75.75 0 0 1 .75-.75Zm13.5 9a1.5 1.5 0 0 0-1.5-1.5H5.25a1.5 1.5 0 0 0-1.5 1.5v7.5a1.5 1.5 0 0 0 1.5 1.5h13.5a1.5 1.5 0 0 0 1.5-1.5v-7.5Z"
    ],
    star: [
      "M10.788 3.21c.448-1.077 1.976-1.077 2.424 0l2.082 5.006 5.404.434c1.164.093 1.636 1.545.749 2.305l-4.117 3.527 1.257 5.273c.271 1.136-.964 2.033-1.96 1.425L12 18.354 7.373 21.18c-.996.608-2.231-.29-1.96-1.425l1.257-5.273-4.117-3.527c-.887-.76-.415-2.212.749-2.305l5.404-.434 2.082-5.005Z"
    ],
    heart: [
      "m11.645 20.91-.007-.003-.022-.012a15.247 15.247 0 0 1-.383-.218 25.18 25.18 0 0 1-4.244-3.17C4.688 15.36 2.25 12.174 2.25 8.25 2.25 5.322 4.714 3 7.688 3A5.5 5.5 0 0 1 12 5.052 5.5 5.5 0 0 1 16.313 3c2.973 0 5.437 2.322 5.437 5.25 0 3.925-2.438 7.111-4.739 9.256a25.175 25.175 0 0 1-4.244 3.17 15.247 15.247 0 0 1-.383.219l-.022.012-.007.004-.003.001a.752.752 0 0 1-.704 0l-.003-.001Z"
    ],
    "thumb-up": [
      "M7.493 18.5c-.425 0-.82-.236-.975-.632A7.48 7.48 0 0 1 6 15.125c0-1.75.599-3.358 1.602-4.634.151-.192.373-.309.6-.397.473-.183.89-.514 1.212-.924a9.042 9.042 0 0 1 2.861-2.4c.723-.384 1.35-.956 1.653-1.715a4.498 4.498 0 0 0 .322-1.672V2.75A.75.75 0 0 1 15 2a2.25 2.25 0 0 1 2.25 2.25c0 1.152-.26 2.243-.723 3.218-.266.558.107 1.282.725 1.282h3.126c1.026 0 1.945.694 2.054 1.715.045.422.068.85.068 1.285a11.95 11.95 0 0 1-2.649 7.521c-.388.482-.987.729-1.605.729H14.23c-.483 0-.964-.078-1.423-.23l-3.114-1.04a4.501 4.501 0 0 0-1.423-.23h-.777ZM2.331 10.727a11.969 11.969 0 0 0-.831 4.398 12 12 0 0 0 .52 3.507C2.28 19.482 3.105 20 3.994 20H4.9c.445 0 .72-.498.523-.898a8.963 8.963 0 0 1-.924-3.977c0-1.708.476-3.305 1.302-4.666.245-.403-.028-.959-.5-.959H4.25c-.832 0-1.612.453-1.918 1.227Z"
    ],
    smile: [
      "M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25Zm-2.625 6c-.54 0-.828.419-.936.634a1.96 1.96 0 0 0-.189.866c0 .298.059.605.189.866.108.215.395.634.936.634.54 0 .828-.419.936-.634.13-.26.189-.568.189-.866 0-.298-.059-.605-.189-.866-.108-.215-.395-.634-.936-.634Zm4.314.634c.108-.215.395-.634.936-.634.54 0 .828.419.936.634.13.26.189.568.189.866 0 .298-.059.605-.189.866-.108.215-.395.634-.936.634-.54 0-.828-.419-.936-.634a1.96 1.96 0 0 1-.189-.866c0-.298.059-.605.189-.866Zm2.023 6.828a.75.75 0 1 0-1.06-1.06 3.75 3.75 0 0 1-5.304 0 .75.75 0 0 0-1.06 1.06 5.25 5.25 0 0 0 7.424 0Z"
    ],
    megaphone: [
      "M16.881 4.345A23.112 23.112 0 0 1 8.25 6H7.5a5.25 5.25 0 0 0-.88 10.427 21.593 21.593 0 0 0 1.378 3.94c.464 1.004 1.674 1.32 2.582.796l.657-.379c.88-.508 1.165-1.593.772-2.468a17.116 17.116 0 0 1-.628-1.607c1.918.258 3.76.75 5.5 1.446A21.727 21.727 0 0 0 18 11.25c0-2.414-.393-4.735-1.119-6.905ZM18.26 3.74a23.22 23.22 0 0 1 1.24 7.51 23.22 23.22 0 0 1-1.41 7.992.75.75 0 1 0 1.409.516 24.555 24.555 0 0 0 1.415-6.43 2.992 2.992 0 0 0 .836-2.078c0-.807-.319-1.54-.836-2.078a24.65 24.65 0 0 0-1.415-6.43.75.75 0 1 0-1.409.516c.059.16.116.321.17.483Z"
    ],
    bell: [
      "M5.85 3.5a.75.75 0 0 0-1.117-1 9.719 9.719 0 0 0-2.348 4.876.75.75 0 0 0 1.479.248A8.219 8.219 0 0 1 5.85 3.5ZM19.267 2.5a.75.75 0 1 0-1.118 1 8.22 8.22 0 0 1 1.987 4.124.75.75 0 0 0 1.48-.248A9.72 9.72 0 0 0 19.266 2.5Z",
      "M12 2.25A6.75 6.75 0 0 0 5.25 9v.75a8.217 8.217 0 0 1-2.119 5.52.75.75 0 0 0 .298 1.206c1.544.57 3.16.99 4.831 1.243a3.75 3.75 0 1 0 7.48 0 24.583 24.583 0 0 0 4.83-1.244.75.75 0 0 0 .298-1.205 8.217 8.217 0 0 1-2.118-5.52V9A6.75 6.75 0 0 0 12 2.25ZM9.75 18c0-.034 0-.067.002-.1a25.05 25.05 0 0 0 4.496 0l.002.1a2.25 2.25 0 1 1-4.5 0Z"
    ],
    gift: [
      "M9.375 3a1.875 1.875 0 0 0 0 3.75h1.875v4.5H3.375A1.875 1.875 0 0 1 1.5 9.375v-.75c0-1.036.84-1.875 1.875-1.875h3.193A3.375 3.375 0 0 1 12 2.753a3.375 3.375 0 0 1 5.432 3.997h3.943c1.035 0 1.875.84 1.875 1.875v.75c0 1.036-.84 1.875-1.875 1.875H12.75v-4.5h1.875a1.875 1.875 0 1 0-1.875-1.875V6.75h-1.5V4.875C11.25 3.839 10.41 3 9.375 3ZM11.25 12.75H3v6.75a2.25 2.25 0 0 0 2.25 2.25h6v-9ZM12.75 12.75v9h6.75a2.25 2.25 0 0 0 2.25-2.25v-6.75h-9Z"
    ],
    tag: [
      "M5.25 2.25a3 3 0 0 0-3 3v4.318a3 3 0 0 0 .879 2.121l9.58 9.581c.92.92 2.39 1.186 3.548.428a18.849 18.849 0 0 0 5.441-5.44c.758-1.16.492-2.629-.428-3.548l-9.58-9.581a3 3 0 0 0-2.122-.879H5.25ZM6.375 7.5a1.125 1.125 0 1 0 0-2.25 1.125 1.125 0 0 0 0 2.25Z"
    ],
    sparkles: [
      "M9 4.5a.75.75 0 0 1 .721.544l.813 2.846a3.75 3.75 0 0 0 2.576 2.576l2.846.813a.75.75 0 0 1 0 1.442l-2.846.813a3.75 3.75 0 0 0-2.576 2.576l-.813 2.846a.75.75 0 0 1-1.442 0l-.813-2.846a3.75 3.75 0 0 0-2.576-2.576l-2.846-.813a.75.75 0 0 1 0-1.442l2.846-.813A3.75 3.75 0 0 0 7.466 7.89l.813-2.846A.75.75 0 0 1 9 4.5ZM18 1.5a.75.75 0 0 1 .728.568l.258 1.036c.236.94.97 1.674 1.91 1.91l1.036.258a.75.75 0 0 1 0 1.456l-1.036.258c-.94.236-1.674.97-1.91 1.91l-.258 1.036a.75.75 0 0 1-1.456 0l-.258-1.036a2.625 2.625 0 0 0-1.91-1.91l-1.036-.258a.75.75 0 0 1 0-1.456l1.036-.258a2.625 2.625 0 0 0 1.91-1.91l.258-1.036A.75.75 0 0 1 18 1.5ZM16.5 15a.75.75 0 0 1 .712.513l.394 1.183c.15.447.5.799.948.948l1.183.395a.75.75 0 0 1 0 1.422l-1.183.395c-.447.15-.799.5-.948.948l-.395 1.183a.75.75 0 0 1-1.422 0l-.395-1.183a1.5 1.5 0 0 0-.948-.948l-1.183-.395a.75.75 0 0 1 0-1.422l1.183-.395c.447-.15.799-.5.948-.948l.395-1.183A.75.75 0 0 1 16.5 15Z"
    ],
    bolt: [
      "M14.615 1.595a.75.75 0 0 1 .359.852L12.982 9.75h7.268a.75.75 0 0 1 .548 1.262l-10.5 11.25a.75.75 0 0 1-1.272-.71l1.992-7.302H3.75a.75.75 0 0 1-.548-1.262l10.5-11.25a.75.75 0 0 1 .913-.143Z"
    ]
  };
  var SUCCESS_ICONS = {
    none: null,
    "check-circle": FIELD_ICONS["check-circle"],
    "thumb-up": FIELD_ICONS["thumb-up"],
    sparkles: FIELD_ICONS.sparkles,
    "paper-plane": FIELD_ICONS["paper-plane"],
    gift: FIELD_ICONS.gift,
    heart: FIELD_ICONS.heart,
    star: FIELD_ICONS.star,
    chat: FIELD_ICONS.chat
  };
  var RATING_ICONS = {
    star: FIELD_ICONS.star,
    heart: FIELD_ICONS.heart,
    "thumb-up": FIELD_ICONS["thumb-up"]
  };
  function svgIcon(paths, { size = 20, filled = false } = {}) {
    const NS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(NS, "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("fill", "none");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    svg.classList.add("rfbd-icon");
    for (const d of Array.isArray(paths) ? paths : [paths]) {
      const path = document.createElementNS(NS, "path");
      path.setAttribute("d", d);
      if (filled) {
        path.setAttribute("fill", "currentColor");
        path.setAttribute("fill-rule", "evenodd");
        path.setAttribute("clip-rule", "evenodd");
      } else {
        path.setAttribute("stroke", "currentColor");
        path.setAttribute("stroke-width", "1.8");
        path.setAttribute("stroke-linecap", "round");
        path.setAttribute("stroke-linejoin", "round");
      }
      svg.append(path);
    }
    return svg;
  }

  // dev-template/src/runtime/registry.js
  var definitions = /* @__PURE__ */ Object.create(null);
  function define(type, definition) {
    definitions[type] = { ...definitions[type], ...definition };
  }
  function getDefinition(type) {
    return definitions[type];
  }

  // dev-template/src/runtime/chrome.js
  function readChrome(element, locale) {
    const settings = element.settings ?? {};
    return {
      label: readText(settings, "label", locale),
      placeholder: readText(settings, "placeholder", locale),
      description: readText(settings, "description", locale),
      defaultValue: readString(settings, "defaultValue"),
      isRequired: readBoolean(settings, "required"),
      isLabelHidden: readBoolean(settings, "hideLabel"),
      showsRequiredNote: readBoolean(settings, "showRequiredNote"),
      columnWidth: readString(settings, "columnWidth", "100"),
      customClass: readString(settings, "customClass"),
      icon: readString(settings, "icon", "none")
    };
  }
  function buildContext(element, instance) {
    const settings = element.settings ?? {};
    const { locale, store } = instance;
    return {
      element,
      settings,
      locale,
      store,
      instance,
      value: store.get(element.id),
      error: store.errors[element.id] ?? "",
      chrome: readChrome(element, locale),
      text: (id, fallback = "") => readText(settings, id, locale) || fallback,
      bool: (id, fallback = false) => readBoolean(settings, id, fallback),
      num: (id, fallback) => readNumber(settings, id, fallback),
      optNum: (id) => readOptionalNumber(settings, id),
      list: (id) => readStringList(settings, id),
      options: (id = "options") => resolveOptions(settings, locale, id),
      emitChange: (value) => store.set(element.id, value),
      requestRender: () => instance.render()
    };
  }
  function renderLabel(ctx, forId) {
    const { chrome } = ctx;
    if (chrome.isLabelHidden && !chrome.showsRequiredNote) return null;
    const icon = FIELD_ICONS[chrome.icon];
    return h(
      "label",
      {
        class: cx("rfbd-field__label", chrome.isLabelHidden && "rfbd-visually-hidden"),
        for: forId
      },
      icon ? iconSvg(icon) : null,
      chrome.label,
      chrome.isRequired ? h("span", { class: "rfbd-field__required", "aria-hidden": "true" }, " *") : null
    );
  }
  function iconSvg(pathData, size = 16) {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("viewBox", "0 0 24 24");
    svg.setAttribute("width", String(size));
    svg.setAttribute("height", String(size));
    svg.setAttribute("fill", "none");
    svg.setAttribute("aria-hidden", "true");
    svg.classList.add("rfbd-icon");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", pathData);
    path.setAttribute("stroke", "currentColor");
    path.setAttribute("stroke-width", "1.8");
    path.setAttribute("stroke-linecap", "round");
    path.setAttribute("stroke-linejoin", "round");
    svg.append(path);
    return svg;
  }
  function renderField(element, instance) {
    if (!isElementActive(element, instance.store.values)) return null;
    const definition = getDefinition(element.type);
    if (!definition) {
      console.warn(`[rfbd] unknown element type "${element.type}" skipped`);
      return null;
    }
    return renderWithChrome(definition, buildContext(element, instance));
  }
  function renderWithChrome(definition, ctx) {
    const { element, instance } = ctx;
    const control = definition.render(ctx);
    if (!control) return null;
    if (definition.isInput === false) return control;
    const { chrome } = ctx;
    const wantsFloating = instance.labelPlacement === "floating" && !definition.selfChrome;
    const wrapper = h(
      "div",
      {
        class: cx(
          "rfbd-field",
          `rfbd-field--${chrome.columnWidth}`,
          ctx.error && "is-invalid",
          chrome.customClass
        ),
        dataset: { rfbdEl: ctx.fieldKey ?? element.id, rfbdType: element.type }
      },
      definition.selfChrome ? null : renderLabel(ctx, `${instance.id}-${element.id}`),
      h("div", { class: "rfbd-field__control" }, control),
      chrome.description ? h("div", { class: "rfbd-field__description" }, chrome.description) : null,
      ctx.error ? h("div", { class: "rfbd-field__error", role: "alert" }, ctx.error) : null
    );
    const chromeControl = wantsFloating ? wrapper.querySelector(`#${CSS.escape(`${instance.id}-${element.id}`)}`) : null;
    const labelsFloat = Boolean(
      chromeControl?.matches(".rfbd-input, .rfbd-select, .rfbd-textarea")
    );
    if (labelsFloat) {
      wrapper.classList.add("rfbd-field--floating");
      const label2 = wrapper.querySelector(".rfbd-field__label");
      const controlBox = wrapper.querySelector(".rfbd-field__control");
      if (label2 && controlBox) controlBox.prepend(label2);
    }
    const label = wrapper.querySelector(".rfbd-field__label[for]");
    if (label && !wrapper.querySelector(`#${CSS.escape(label.htmlFor)}`)) {
      label.removeAttribute("for");
    }
    return wrapper;
  }

  // dev-template/src/core/validation.js
  var DEFAULT_MESSAGES = {
    errorRequired: "This field is required.",
    errorEmail: "Enter a valid email address.",
    errorEmailDomain: "Email addresses from {domain} are not accepted.",
    errorPassword: "Password does not meet the requirements.",
    errorPhone: "Enter a valid phone number.",
    errorUrl: "Enter a valid URL.",
    errorNumber: "Enter a valid number.",
    errorMinLength: "Enter at least {min} characters.",
    errorMaxLength: "Enter no more than {max} characters.",
    errorSubmit: "Something went wrong. Please try again.",
    errorEmailConfirm: "The email addresses don't match.",
    errorPasswordConfirm: "The passwords don't match.",
    errorMinSelections: "Choose at least {min}.",
    errorMaxSelections: "Choose no more than {max}.",
    errorMinTotalQuantity: "Order at least {min} in total.",
    errorMaxTotalQuantity: "Order no more than {max} in total.",
    errorMinPerProduct: "Order at least {min} of each item.",
    errorMaxPerProduct: "Order no more than {max} of each item.",
    errorDateMin: "Choose {min} or later.",
    errorDateMax: "Choose {max} or earlier.",
    errorDateBlocked: "That date isn't available.",
    scheduleNotOpenMessage: "This form isn't open yet.",
    scheduleClosedMessage: "This form is not accepting submissions right now."
  };
  function resolveMessage(instance, key, fallback, replacements = {}) {
    const bag = instance?.document?.panels?.settings ?? {};
    const template = readText(bag, key, instance?.locale);
    return formatMessage(template === "" ? fallback : template, replacements);
  }
  function errorMessage(instance, key, replacements = {}) {
    return resolveMessage(instance, key, DEFAULT_MESSAGES[key] ?? "", replacements);
  }
  var EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
  var E164_PATTERN = /^\+[1-9]\d{7,14}$/;
  var LOOSE_PHONE_PATTERN = /^[+()\-.\s\d]{6,20}$/;
  function isValidEmail(value) {
    return EMAIL_PATTERN.test(value.trim());
  }
  function isValidPhone(value, requireInternational) {
    const trimmed = value.trim();
    if (requireInternational) return E164_PATTERN.test(trimmed);
    return LOOSE_PHONE_PATTERN.test(trimmed) && /\d{6,}/.test(trimmed.replace(/\D/g, ""));
  }
  function isValidUrl(value) {
    const trimmed = value.trim();
    if (trimmed === "") return false;
    const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
    try {
      const parsed = new URL(candidate);
      return (parsed.protocol === "http:" || parsed.protocol === "https:") && parsed.hostname.includes(".");
    } catch {
      return false;
    }
  }
  function isDigitsOnly(value) {
    return /^\d+$/.test(value.trim());
  }
  function isAnswered(value) {
    return !isEmptyValue(value);
  }
  function measureLength(value) {
    if (typeof value === "string") return value.trim().length;
    if (Array.isArray(value)) return value.length;
    if (typeof value === "number") return String(value).length;
    return 0;
  }
  function confirmKeyFor(elementId) {
    return `${elementId}:confirm`;
  }
  function lengthError(element, value, instance) {
    if (!readBoolean(element.settings, "limitCharacters")) return "";
    const length = measureLength(value);
    const minimum = readOptionalNumber(element.settings, "minLength");
    const maximum = readOptionalNumber(element.settings, "maxLength");
    if (minimum !== null && length < minimum) {
      return errorMessage(instance, "errorMinLength", { min: minimum });
    }
    if (maximum !== null && length > maximum) {
      return errorMessage(instance, "errorMaxLength", { max: maximum });
    }
    return "";
  }
  function confirmError(element, value, values, instance) {
    if (!readBoolean(element.settings, "confirmEnabled")) return null;
    if (value === values?.[confirmKeyFor(element.id)]) return null;
    const key = element.type === "password" ? "errorPasswordConfirm" : "errorEmailConfirm";
    return { [confirmKeyFor(element.id)]: errorMessage(instance, key) };
  }
  function validateOne(element, value, values, instance) {
    const definition = getDefinition(element.type);
    if (!definition || definition.isInput === false) return {};
    if (element.type === "repeater") {
      return repeaterErrors(element, value, instance);
    }
    const isRequired = readBoolean(element.settings, "required");
    const answered = isAnswered(value);
    if ((element.type === "consent" || element.type === "switch") && isRequired && value !== true) {
      return { [element.id]: errorMessage(instance, "errorRequired") };
    }
    if (isRequired && !answered) {
      return { [element.id]: errorMessage(instance, "errorRequired") };
    }
    if (!answered) return {};
    if (readBoolean(element.settings, "numbersOnly") && typeof value === "string" && !isDigitsOnly(value)) {
      return { [element.id]: resolveMessage(instance, "errorNumber", "Enter numbers only.") };
    }
    const badLength = lengthError(element, value, instance);
    if (badLength) return { [element.id]: badLength };
    if (typeof definition.validate === "function") {
      const ctx = { ...buildContext(element, instance), value, values };
      const failure = definition.validate(value, ctx);
      if (failure) return { [element.id]: failure };
    }
    return confirmError(element, value, values, instance) ?? {};
  }
  function repeaterErrors(element, value, instance) {
    const entries = Array.isArray(value) ? value.filter((entry) => typeof entry === "object" && entry !== null && !Array.isArray(entry)) : [];
    const minimum = readNumber(element.settings, "minEntries", 0);
    const maximum = readOptionalNumber(element.settings, "maxEntries");
    if (entries.length < minimum) {
      return {
        [element.id]: `Add at least ${minimum} ${minimum === 1 ? "entry" : "entries"}.`
      };
    }
    if (maximum !== null && entries.length > maximum) {
      return {
        [element.id]: `Add no more than ${maximum} ${maximum === 1 ? "entry" : "entries"}.`
      };
    }
    const errors = {};
    entries.forEach((entry, index) => {
      for (const child of element.children ?? []) {
        if (!isElementActive(child, entry)) continue;
        for (const [key, message] of Object.entries(
          validateOne(child, entry[child.id], entry, instance)
        )) {
          errors[`${element.id}.${index}.${key}`] = message;
        }
      }
    });
    return errors;
  }
  function validatePage(instance, pageIndex) {
    const page = instance.document?.pages?.[pageIndex];
    if (!page) return {};
    const values = instance.store.values;
    const errors = {};
    for (const element of page.elements ?? []) {
      if (!isElementActive(element, values)) continue;
      Object.assign(errors, validateOne(element, values[element.id], values, instance));
    }
    return errors;
  }
  function validateAll(instance) {
    const errors = {};
    let firstErrorPage = -1;
    (instance.document?.pages ?? []).forEach((_page, index) => {
      const pageErrors = validatePage(instance, index);
      if (firstErrorPage === -1 && Object.keys(pageErrors).length > 0) {
        firstErrorPage = index;
      }
      Object.assign(errors, pageErrors);
    });
    return { errors, firstErrorPage };
  }

  // dev-template/src/elements/text.js
  function currentText(value) {
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    return "";
  }
  define("text", {
    render(ctx) {
      const numbersOnly = ctx.bool("numbersOnly");
      return h("input", {
        id: `${ctx.instance.id}-${ctx.element.id}`,
        name: ctx.element.id,
        class: "rfbd-input",
        type: "text",
        inputmode: numbersOnly ? "numeric" : null,
        placeholder: ctx.chrome.placeholder || " ",
        value: currentText(ctx.value),
        oninput: (event) => ctx.emitChange(event.target.value)
      });
    }
  });
  define("textarea", {
    render(ctx) {
      return h(
        "textarea",
        {
          id: `${ctx.instance.id}-${ctx.element.id}`,
          name: ctx.element.id,
          class: "rfbd-textarea",
          rows: String(clamp(ctx.num("rows", 4), 2, 20)),
          placeholder: ctx.chrome.placeholder || " ",
          oninput: (event) => ctx.emitChange(event.target.value)
        },
        // A textarea's default value is its text content, not a `value` attr.
        currentText(ctx.value)
      );
    }
  });
  define("url", {
    render(ctx) {
      return h("input", {
        id: `${ctx.instance.id}-${ctx.element.id}`,
        name: ctx.element.id,
        class: "rfbd-input",
        type: "url",
        inputmode: "url",
        autocomplete: "url",
        placeholder: ctx.chrome.placeholder || " ",
        value: currentText(ctx.value),
        oninput: (event) => ctx.emitChange(event.target.value)
      });
    },
    validate(value, ctx) {
      if (typeof value !== "string" || value === "") return "";
      return isValidUrl(value) ? "" : errorMessage(ctx.instance, "errorUrl");
    }
  });

  // dev-template/src/core/country-data.js
  var COUNTRY_DATA = "AF:Afghanistan:93|AX:\xC5land Islands:358|AL:Albania:355|DZ:Algeria:213|AS:American Samoa:1|AD:Andorra:376|AO:Angola:244|AI:Anguilla:1|AQ:Antarctica:672|AG:Antigua & Barbuda:1|AR:Argentina:54|AM:Armenia:374|AW:Aruba:297|AU:Australia:61|AT:Austria:43|AZ:Azerbaijan:994|BS:Bahamas:1|BH:Bahrain:973|BD:Bangladesh:880|BB:Barbados:1|BY:Belarus:375|BE:Belgium:32|BZ:Belize:501|BJ:Benin:229|BM:Bermuda:1|BT:Bhutan:975|BO:Bolivia:591|BQ:Caribbean Netherlands:599|BA:Bosnia & Herzegovina:387|BW:Botswana:267|BV:Bouvet Island:47|BR:Brazil:55|IO:British Indian Ocean Territory:246|BN:Brunei:673|BG:Bulgaria:359|BF:Burkina Faso:226|BI:Burundi:257|CV:Cape Verde:238|KH:Cambodia:855|CM:Cameroon:237|CA:Canada:1|KY:Cayman Islands:1|CF:Central African Republic:236|TD:Chad:235|CL:Chile:56|CN:China:86|CX:Christmas Island:61|CC:Cocos (Keeling) Islands:61|CO:Colombia:57|KM:Comoros:269|CG:Congo - Brazzaville:242|CD:Congo - Kinshasa:243|CK:Cook Islands:682|CR:Costa Rica:506|CI:C\xF4te d\u2019Ivoire:225|HR:Croatia:385|CU:Cuba:53|CW:Cura\xE7ao:599|CY:Cyprus:357|CZ:Czechia:420|DK:Denmark:45|DJ:Djibouti:253|DM:Dominica:1|DO:Dominican Republic:1|EC:Ecuador:593|EG:Egypt:20|SV:El Salvador:503|GQ:Equatorial Guinea:240|ER:Eritrea:291|EE:Estonia:372|SZ:Eswatini:268|ET:Ethiopia:251|FK:Falkland Islands:500|FO:Faroe Islands:298|FJ:Fiji:679|FI:Finland:358|FR:France:33|GF:French Guiana:594|PF:French Polynesia:689|TF:French Southern Territories:262|GA:Gabon:241|GM:Gambia:220|GE:Georgia:995|DE:Germany:49|GH:Ghana:233|GI:Gibraltar:350|GR:Greece:30|GL:Greenland:299|GD:Grenada:1|GP:Guadeloupe:590|GU:Guam:1|GT:Guatemala:502|GG:Guernsey:44|GN:Guinea:224|GW:Guinea-Bissau:245|GY:Guyana:592|HT:Haiti:509|HM:Heard & McDonald Islands:672|VA:Vatican City:39|HN:Honduras:504|HK:Hong Kong SAR:852|HU:Hungary:36|IS:Iceland:354|IN:India:91|ID:Indonesia:62|IR:Iran:98|IQ:Iraq:964|IE:Ireland:353|IM:Isle of Man:44|IL:Israel:972|IT:Italy:39|JM:Jamaica:1|JP:Japan:81|JE:Jersey:44|JO:Jordan:962|KZ:Kazakhstan:7|KE:Kenya:254|KI:Kiribati:686|KP:North Korea:850|KR:South Korea:82|KW:Kuwait:965|KG:Kyrgyzstan:996|LA:Laos:856|LV:Latvia:371|LB:Lebanon:961|LS:Lesotho:266|LR:Liberia:231|LY:Libya:218|LI:Liechtenstein:423|LT:Lithuania:370|LU:Luxembourg:352|MO:Macao SAR:853|MG:Madagascar:261|MW:Malawi:265|MY:Malaysia:60|MV:Maldives:960|ML:Mali:223|MT:Malta:356|MH:Marshall Islands:692|MQ:Martinique:596|MR:Mauritania:222|MU:Mauritius:230|YT:Mayotte:262|MX:Mexico:52|FM:Micronesia:691|MD:Moldova:373|MC:Monaco:377|MN:Mongolia:976|ME:Montenegro:382|MS:Montserrat:1|MA:Morocco:212|MZ:Mozambique:258|MM:Myanmar (Burma):95|NA:Namibia:264|NR:Nauru:674|NP:Nepal:977|NL:Netherlands:31|NC:New Caledonia:687|NZ:New Zealand:64|NI:Nicaragua:505|NE:Niger:227|NG:Nigeria:234|NU:Niue:683|NF:Norfolk Island:672|MK:North Macedonia:389|MP:Northern Mariana Islands:1|NO:Norway:47|OM:Oman:968|PK:Pakistan:92|PW:Palau:680|PS:Palestinian Territories:970|PA:Panama:507|PG:Papua New Guinea:675|PY:Paraguay:595|PE:Peru:51|PH:Philippines:63|PN:Pitcairn Islands:64|PL:Poland:48|PT:Portugal:351|PR:Puerto Rico:1|QA:Qatar:974|RE:R\xE9union:262|RO:Romania:40|RU:Russia:7|RW:Rwanda:250|BL:St. Barth\xE9lemy:590|SH:St. Helena:290|KN:St. Kitts & Nevis:1|LC:St. Lucia:1|MF:St. Martin:590|PM:St. Pierre & Miquelon:508|VC:St. Vincent & Grenadines:1|WS:Samoa:685|SM:San Marino:378|ST:S\xE3o Tom\xE9 & Pr\xEDncipe:239|SA:Saudi Arabia:966|SN:Senegal:221|RS:Serbia:381|SC:Seychelles:248|SL:Sierra Leone:232|SG:Singapore:65|SX:Sint Maarten:1|SK:Slovakia:421|SI:Slovenia:386|SB:Solomon Islands:677|SO:Somalia:252|ZA:South Africa:27|GS:South Georgia & South Sandwich Islands:500|SS:South Sudan:211|ES:Spain:34|LK:Sri Lanka:94|SD:Sudan:249|SR:Suriname:597|SJ:Svalbard & Jan Mayen:47|SE:Sweden:46|CH:Switzerland:41|SY:Syria:963|TW:Taiwan:886|TJ:Tajikistan:992|TZ:Tanzania:255|TH:Thailand:66|TL:Timor-Leste:670|TG:Togo:228|TK:Tokelau:690|TO:Tonga:676|TT:Trinidad & Tobago:1|TN:Tunisia:216|TR:T\xFCrkiye:90|TM:Turkmenistan:993|TC:Turks & Caicos Islands:1|TV:Tuvalu:688|UG:Uganda:256|UA:Ukraine:380|AE:United Arab Emirates:971|GB:United Kingdom:44|US:United States:1|UM:U.S. Outlying Islands:1|UY:Uruguay:598|UZ:Uzbekistan:998|VU:Vanuatu:678|VE:Venezuela:58|VN:Vietnam:84|VG:British Virgin Islands:1|VI:U.S. Virgin Islands:1|WF:Wallis & Futuna:681|EH:Western Sahara:212|YE:Yemen:967|ZM:Zambia:260|ZW:Zimbabwe:263";
  var COUNTRIES = COUNTRY_DATA.split("|").map((entry) => {
    const [code, name, dial] = entry.split(":");
    return [code.toLowerCase(), name, dial];
  });
  var MAIN_COUNTRY_FOR_DIAL = {
    1: "us",
    7: "ru",
    39: "it",
    44: "gb",
    47: "no",
    61: "au",
    64: "nz",
    212: "ma",
    262: "re",
    358: "fi",
    500: "fk",
    590: "gp",
    599: "cw",
    672: "nf"
  };
  var byDialCode = /* @__PURE__ */ new Map();
  for (const country of COUNTRIES) {
    const preferred = MAIN_COUNTRY_FOR_DIAL[country[2]];
    if (preferred ? country[0] === preferred : !byDialCode.has(country[2])) {
      byDialCode.set(country[2], country);
    }
  }
  function flagEmoji(iso2) {
    return String.fromCodePoint(
      ...iso2.toLowerCase().slice(0, 2).split("").map((letter) => 127462 + letter.charCodeAt(0) - 97)
    );
  }
  function byDialCodeLongestMatch(e164) {
    const digits = String(e164 ?? "").replace(/\D/g, "");
    for (let length = 4; length >= 1; length -= 1) {
      const match = byDialCode.get(digits.slice(0, length));
      if (match) return match;
    }
    return null;
  }

  // dev-template/src/elements/contact.js
  function currentText2(value) {
    return typeof value === "string" ? value : "";
  }
  function confirmInput(ctx, inputType) {
    const confirmKey = `${ctx.element.id}:confirm`;
    const confirmId = `${ctx.instance.id}-${ctx.element.id}-confirm`;
    const fallback = ctx.element.type === "password" ? "Confirm password" : "Confirm email";
    return h(
      "div",
      { class: "rfbd-field__confirm" },
      h(
        "label",
        { class: "rfbd-field__label", for: confirmId },
        ctx.text("confirmLabel") || fallback
      ),
      h("input", {
        id: confirmId,
        name: confirmKey,
        class: "rfbd-input",
        type: inputType,
        autocomplete: "off",
        placeholder: " ",
        value: currentText2(ctx.store.get(confirmKey)),
        oninput: (event) => ctx.store.set(confirmKey, event.target.value)
      })
    );
  }
  function normalizeDomain(domain) {
    const lowered = domain.trim().toLowerCase();
    return lowered.startsWith("www.") ? lowered.slice(4) : lowered;
  }
  define("email", {
    render(ctx) {
      const input = h("input", {
        id: `${ctx.instance.id}-${ctx.element.id}`,
        name: ctx.element.id,
        class: "rfbd-input",
        type: "email",
        inputmode: "email",
        autocomplete: "email",
        placeholder: ctx.chrome.placeholder || " ",
        value: currentText2(ctx.value),
        oninput: (event) => ctx.emitChange(event.target.value)
      });
      if (!ctx.bool("confirmEnabled")) return input;
      return [input, confirmInput(ctx, "email")];
    },
    validate(value, ctx) {
      if (typeof value !== "string" || value === "") return "";
      if (!isValidEmail(value)) {
        return errorMessage(ctx.instance, "errorEmail");
      }
      const rule = ctx.settings.domainRule;
      if (rule !== "block" && rule !== "allow") return "";
      const domains = ctx.list("domainList").map(normalizeDomain).filter((domain2) => domain2 !== "");
      if (domains.length === 0) return "";
      const domain = normalizeDomain(value.split("@")[1] ?? "");
      const isListed = domains.includes(domain);
      if (rule === "block" ? !isListed : isListed) return "";
      return errorMessage(ctx.instance, "errorEmailDomain", { domain });
    }
  });
  define("name", {
    render(ctx) {
      return h("input", {
        id: `${ctx.instance.id}-${ctx.element.id}`,
        name: ctx.element.id,
        class: "rfbd-input",
        type: "text",
        autocomplete: "name",
        placeholder: ctx.chrome.placeholder || " ",
        value: currentText2(ctx.value),
        oninput: (event) => ctx.emitChange(event.target.value)
      });
    }
  });
  var PASSWORD_PRESETS = {
    "letters-numbers": {
      pattern: /^(?=.*[A-Za-z])(?=.*\d)/,
      fallback: "Password must contain letters and numbers."
    },
    "letters-numbers-symbols": {
      pattern: /^(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9])/,
      fallback: "Password must contain letters, numbers and a symbol."
    }
  };
  function passwordMessage(ctx, fallback) {
    const bag = ctx.instance?.document?.panels?.settings ?? {};
    return readText(bag, "errorPassword", ctx.locale) || fallback;
  }
  define("password", {
    render(ctx) {
      const input = h("input", {
        id: `${ctx.instance.id}-${ctx.element.id}`,
        name: ctx.element.id,
        class: "rfbd-input",
        type: "password",
        // Asks the password manager to offer a generated secret rather than
        // filling the shopper's existing login for this shop.
        autocomplete: "new-password",
        placeholder: ctx.chrome.placeholder || " ",
        value: currentText2(ctx.value),
        oninput: (event) => ctx.emitChange(event.target.value)
      });
      if (!ctx.bool("confirmEnabled")) return input;
      return [input, confirmInput(ctx, "password")];
    },
    validate(value, ctx) {
      if (typeof value !== "string" || value === "") return "";
      const rule = ctx.settings.validationRule;
      if (rule === "custom") {
        const source = (ctx.settings.customPattern ?? "").toString().trim();
        if (source === "") return "";
        let pattern;
        try {
          pattern = new RegExp(source);
        } catch {
          return "";
        }
        return pattern.test(value) ? "" : errorMessage(ctx.instance, "errorPassword");
      }
      const preset = PASSWORD_PRESETS[rule];
      if (!preset || preset.pattern.test(value)) return "";
      return passwordMessage(ctx, preset.fallback);
    }
  });
  define("country", {
    render(ctx) {
      const controlId = `${ctx.instance.id}-${ctx.element.id}`;
      const selected = currentText2(ctx.value);
      const optionsFor = (query) => {
        const needle = query.trim().toLowerCase();
        return [
          h("option", { value: "" }, ctx.chrome.placeholder || "Maak een keuze"),
          COUNTRIES.filter(
            ([code, name]) => (
              // The chosen country always stays listed, or the select would
              // silently drop the answer the moment it is filtered out.
              code === selected || needle === "" || name.toLowerCase().includes(needle)
            )
          ).map(
            ([code, name]) => h("option", { value: code, selected: code === selected }, name)
          )
        ];
      };
      const select = h(
        "select",
        {
          id: controlId,
          name: ctx.element.id,
          class: "rfbd-select",
          onchange: (event) => ctx.emitChange(event.target.value)
        },
        optionsFor("")
      );
      if (!ctx.bool("enableSearch")) return select;
      const search = h("input", {
        id: `${controlId}-search`,
        // A name of its own so the renderer's focus restore can find it after
        // the wholesale re-render a selection triggers.
        name: `${ctx.element.id}::search`,
        class: "rfbd-input",
        type: "text",
        autocomplete: "off",
        placeholder: "Search",
        "aria-label": "Search countries",
        "aria-controls": controlId,
        // Filtering is pure UI, so the option list is rebuilt in place rather
        // than through the store , no answer changed, nothing to re-render.
        oninput: (event) => select.replaceChildren(
          ...optionsFor(event.target.value).flat(Infinity)
        )
      });
      return [search, select];
    }
  });

  // dev-template/src/elements/address.js
  var SUBFIELDS = [
    { id: "address1", label: "Street address", toggle: null, autocomplete: "address-line1", isWide: true },
    { id: "address2", label: "Apartment, suite, etc.", toggle: "showAddress2", autocomplete: "address-line2", isWide: true },
    { id: "city", label: "City", toggle: "showCity", autocomplete: "address-level2", isWide: false },
    { id: "province", label: "State / province", toggle: "showProvince", autocomplete: "address-level1", isWide: false },
    { id: "zip", label: "Postal / ZIP code", toggle: "showZip", autocomplete: "postal-code", isWide: false },
    { id: "country", label: "Country", toggle: "showCountry", autocomplete: "country-name", isWide: false },
    { id: "company", label: "Company", toggle: "showCompany", autocomplete: "organization", isWide: true }
  ];
  function currentParts(value) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      return {};
    }
    const parts = {};
    for (const [key, part] of Object.entries(value)) {
      if (typeof part === "string") parts[key] = part;
    }
    return parts;
  }
  function visibleSubfields(ctx) {
    return SUBFIELDS.filter(
      (subfield) => subfield.toggle === null || ctx.bool(subfield.toggle, subfield.toggle !== "showCompany")
    );
  }
  define("address", {
    selfChrome: true,
    render(ctx) {
      const controlId = `${ctx.instance.id}-${ctx.element.id}`;
      const parts = currentParts(ctx.value);
      const visible = visibleSubfields(ctx);
      const setPart = (id, text) => {
        const next = {};
        for (const subfield of visible) {
          const part = subfield.id === id ? text : parts[subfield.id] ?? "";
          if (typeof part === "string") next[subfield.id] = part;
        }
        const hasContent = Object.values(next).some((part) => part.trim() !== "");
        ctx.emitChange(hasContent ? next : {});
      };
      const partField = (subfield, index) => {
        const partId = index === 0 ? controlId : `${controlId}-${subfield.id}`;
        const shared = {
          id: partId,
          // Unique names, so the renderer's focus restore lands on the exact
          // part being typed into across the wholesale re-render.
          name: `${ctx.element.id}.${subfield.id}`,
          autocomplete: subfield.autocomplete
        };
        const control = subfield.id === "country" ? h(
          "select",
          {
            ...shared,
            class: "rfbd-select",
            onchange: (event) => setPart("country", event.target.value)
          },
          h("option", { value: "" }, ""),
          COUNTRIES.map(
            ([, name]) => (
              // Names, not codes: the other parts are free text and the
              // submission renders as one readable line.
              h(
                "option",
                { value: name, selected: name === (parts.country ?? "") },
                name
              )
            )
          )
        ) : h("input", {
          ...shared,
          class: "rfbd-input",
          type: "text",
          placeholder: " ",
          value: parts[subfield.id] ?? "",
          oninput: (event) => setPart(subfield.id, event.target.value)
        });
        return h(
          "div",
          {
            class: cx(
              "rfbd-address__part",
              subfield.isWide && "rfbd-address__part--wide"
            )
          },
          h(
            "label",
            { class: "rfbd-field__label rfbd-address__label", for: partId },
            subfield.label
          ),
          control
        );
      };
      return h(
        "fieldset",
        { class: "rfbd-address" },
        ctx.chrome.isLabelHidden ? null : h(
          "legend",
          { class: "rfbd-address__label" },
          ctx.chrome.label,
          ctx.chrome.isRequired ? h(
            "span",
            { class: "rfbd-field__required", "aria-hidden": "true" },
            " *"
          ) : null
        ),
        visible.map(partField)
      );
    },
    /**
     * A required address needs its load-bearing parts (street, city, ZIP), but
     * only the ones the merchant turned on , a form without a ZIP field cannot
     * demand one. Ported from form-core's composite-validators.
     */
    validate(value, ctx) {
      if (!ctx.chrome.isRequired) return "";
      if (typeof value !== "object" || value === null || Array.isArray(value)) {
        return "";
      }
      const enabled = new Set(visibleSubfields(ctx).map((s) => s.id));
      const incomplete = ["address1", "city", "zip"].some(
        (part) => enabled.has(part) && (typeof value[part] !== "string" || value[part].trim() === "")
      );
      return incomplete ? errorMessage(ctx.instance, "errorRequired") : "";
    }
  });

  // dev-template/src/elements/selects.js
  function toSelectedList(value) {
    if (Array.isArray(value)) {
      return value.filter((entry) => typeof entry === "string");
    }
    return typeof value === "string" && value !== "" ? [value] : [];
  }
  function uiState(ctx, defaults) {
    var _a, _b;
    const bags = (_a = ctx.instance).elementUi ?? (_a.elementUi = /* @__PURE__ */ Object.create(null));
    return bags[_b = ctx.element.id] ?? (bags[_b] = { ...defaults });
  }
  function searchableDropdown(ctx) {
    const controlId = `${ctx.instance.id}-${ctx.element.id}`;
    const listId = `${controlId}-listbox`;
    const options = ctx.options();
    const selected = typeof ctx.value === "string" ? ctx.value : "";
    const ui = uiState(ctx, {
      query: options.find((option) => option.value === selected)?.label ?? "",
      open: false,
      active: -1
    });
    const filtered = () => {
      const needle = ui.query.trim().toLowerCase();
      return needle === "" ? options : options.filter((option) => option.label.toLowerCase().includes(needle));
    };
    const input = h("input", {
      id: controlId,
      name: ctx.element.id,
      class: "rfbd-input",
      type: "text",
      role: "combobox",
      autocomplete: "off",
      "aria-expanded": "false",
      "aria-controls": listId,
      "aria-autocomplete": "list",
      placeholder: ctx.chrome.placeholder || "Maak een keuze",
      value: ui.query
    });
    const list = h("div", {
      id: listId,
      class: "rfbd-combobox__list",
      role: "listbox",
      hidden: true
    });
    const choose = (option) => {
      ui.query = option.label;
      ui.open = false;
      ui.active = -1;
      input.value = option.label;
      ctx.emitChange(option.value);
      renderList();
    };
    function renderList() {
      const matches = filtered();
      ui.active = Math.min(ui.active, matches.length - 1);
      list.replaceChildren(
        ...matches.map(
          (option, index) => h(
            "div",
            {
              id: `${listId}-${index}`,
              class: cx(
                "rfbd-combobox__option",
                index === ui.active && "is-active"
              ),
              role: "option",
              "aria-selected": option.value === selected ? "true" : "false",
              // mousedown, not click: it fires before the input's blur, so
              // choosing with the mouse does not first close the list.
              onmousedown: (event) => {
                event.preventDefault();
                choose(option);
              }
            },
            option.label
          )
        )
      );
      const isOpen = ui.open && matches.length > 0;
      list.hidden = !isOpen;
      input.setAttribute("aria-expanded", isOpen ? "true" : "false");
      if (isOpen && ui.active >= 0) {
        input.setAttribute("aria-activedescendant", `${listId}-${ui.active}`);
      } else {
        input.removeAttribute("aria-activedescendant");
      }
    }
    input.addEventListener("input", () => {
      ui.query = input.value;
      ui.open = true;
      ui.active = -1;
      const match = options.find(
        (option) => option.label.toLowerCase() === input.value.trim().toLowerCase()
      );
      ctx.emitChange(match ? match.value : "");
      renderList();
    });
    input.addEventListener("focus", () => {
      ui.open = true;
      renderList();
    });
    input.addEventListener("blur", () => {
      ui.open = false;
      renderList();
    });
    input.addEventListener("keydown", (event) => {
      const matches = filtered();
      if (event.key === "ArrowDown") {
        ui.open = true;
        ui.active = Math.min(ui.active + 1, matches.length - 1);
      } else if (event.key === "ArrowUp") {
        ui.active = Math.max(ui.active - 1, 0);
      } else if (event.key === "Enter") {
        if (!ui.open || ui.active < 0) return;
        choose(matches[ui.active]);
      } else if (event.key === "Escape") {
        ui.open = false;
      } else {
        return;
      }
      event.preventDefault();
      renderList();
    });
    renderList();
    return h("div", { class: "rfbd-combobox" }, input, list);
  }
  define("dropdown", {
    render(ctx) {
      if (ctx.bool("enableSearch")) return searchableDropdown(ctx);
      const selected = typeof ctx.value === "string" ? ctx.value : "";
      return h(
        "select",
        {
          id: `${ctx.instance.id}-${ctx.element.id}`,
          name: ctx.element.id,
          class: "rfbd-select",
          onchange: (event) => ctx.emitChange(event.target.value)
        },
        // A disabled first option is what shows the placeholder while still
        // letting "required" reject "nothing chosen".
        h(
          "option",
          { value: "", disabled: true, selected: selected === "" },
          ctx.chrome.placeholder || "Maak een keuze"
        ),
        ctx.options().map(
          (option) => h(
            "option",
            { value: option.value, selected: option.value === selected },
            option.label
          )
        )
      );
    }
  });
  function choiceGroup(ctx, inputType) {
    const controlId = `${ctx.instance.id}-${ctx.element.id}`;
    const options = ctx.options();
    const selected = toSelectedList(ctx.value);
    const isRadio = inputType === "radio";
    const authored = new Set(options.map((option) => option.value));
    const otherValue = selected.find((entry) => !authored.has(entry)) ?? "";
    const withoutOther = selected.filter((entry) => authored.has(entry));
    const ui = uiState(ctx, { otherChecked: false });
    const isOtherOn = ui.otherChecked || otherValue !== "";
    const toggle = (optionValue) => {
      if (isRadio) {
        ui.otherChecked = false;
        ctx.emitChange(optionValue);
        return;
      }
      ctx.emitChange(
        selected.includes(optionValue) ? selected.filter((entry) => entry !== optionValue) : [...selected, optionValue]
      );
    };
    const choice = (option) => {
      const optionId = `${controlId}-${option.id}`;
      return h(
        "label",
        { class: "rfbd-choice", for: optionId },
        h("input", {
          id: optionId,
          class: "rfbd-choice__input",
          type: inputType,
          name: ctx.element.id,
          value: option.value,
          checked: selected.includes(option.value),
          onchange: () => toggle(option.value)
        }),
        h("span", { class: "rfbd-choice__label" }, option.label)
      );
    };
    const otherChoice = () => {
      const otherId = `${controlId}-other`;
      return h(
        "label",
        { class: "rfbd-choice rfbd-choice--other", for: otherId },
        h("input", {
          id: otherId,
          class: "rfbd-choice__input",
          type: inputType,
          name: ctx.element.id,
          checked: isOtherOn,
          onchange: () => {
            if (isOtherOn) {
              ui.otherChecked = false;
              ctx.emitChange(isRadio ? "" : withoutOther);
            } else {
              ui.otherChecked = true;
              if (isRadio) ctx.emitChange("");
            }
            ctx.requestRender();
          }
        }),
        h("span", { class: "rfbd-choice__label" }, "Other"),
        isOtherOn ? h("input", {
          type: "text",
          class: "rfbd-input rfbd-choice__other-input",
          // Its own name, so focus survives the re-render every keystroke
          // triggers. Never submitted , the store holds the real answer.
          name: `${ctx.element.id}::other`,
          value: otherValue,
          "aria-label": "Other",
          oninput: (event) => {
            const text = event.target.value;
            if (isRadio) ctx.emitChange(text);
            else ctx.emitChange(text === "" ? withoutOther : [...withoutOther, text]);
          }
        }) : null
      );
    };
    return h(
      "div",
      {
        class: "rfbd-choices",
        role: isRadio ? "radiogroup" : "group",
        // The grid column count travels as a CSS variable the stylesheet reads.
        style: { "--rfbd-per-line": String(readOptionsPerLine(ctx.settings, 1)) }
      },
      options.map(choice),
      ctx.bool("allowOther") ? otherChoice() : null
    );
  }
  define("radio", {
    render: (ctx) => choiceGroup(ctx, "radio")
  });
  define("checkboxes", {
    render: (ctx) => choiceGroup(ctx, "checkbox"),
    validate(value, ctx) {
      if (!ctx.bool("limitSelections")) return "";
      const count = Array.isArray(value) ? value.length : 0;
      const min = ctx.optNum("minSelections");
      const max = ctx.optNum("maxSelections");
      if (min !== null && count < min) {
        return errorMessage(ctx.instance, "errorMinSelections", { min });
      }
      if (max !== null && count > max) {
        return errorMessage(ctx.instance, "errorMaxSelections", { max });
      }
      return "";
    }
  });
  define("consent", {
    selfChrome: true,
    render(ctx) {
      const controlId = `${ctx.instance.id}-${ctx.element.id}`;
      return h(
        "label",
        { class: "rfbd-choice", for: controlId },
        h("input", {
          id: controlId,
          name: ctx.element.id,
          class: "rfbd-choice__input",
          type: "checkbox",
          checked: ctx.value === true,
          onchange: (event) => ctx.emitChange(event.target.checked)
        }),
        h(
          "span",
          { class: "rfbd-choice__label" },
          ctx.chrome.label,
          ctx.chrome.isRequired ? h("span", { class: "rfbd-field__required", "aria-hidden": "true" }, " *") : null
        )
      );
    }
    // No validate(): required-means-agreed is a shared rule , core/validation.js
    // special-cases consent and switch so `false` fails a required check even
    // though it is a real answer.
  });

  // dev-template/src/elements/toggles.js
  define("switch", {
    render(ctx) {
      const isOn = typeof ctx.value === "boolean" ? ctx.value : ctx.bool("defaultOn");
      const stateLabel = ctx.text(isOn ? "onLabel" : "offLabel");
      return h(
        "div",
        { class: "rfbd-switch" },
        h("input", {
          id: `${ctx.instance.id}-${ctx.element.id}`,
          name: ctx.element.id,
          class: "rfbd-switch__input",
          type: "checkbox",
          role: "switch",
          checked: isOn,
          onchange: (event) => ctx.emitChange(event.target.checked)
        }),
        h("span", { class: "rfbd-switch__track", "aria-hidden": "true" }),
        // The state word is decoration: `role="switch"` already announces on
        // or off, so repeating it would be read twice.
        stateLabel ? h("span", { class: "rfbd-switch__state", "aria-hidden": "true" }, stateLabel) : null
      );
    }
  });
  function toSelected(value) {
    if (Array.isArray(value)) {
      return value.filter((entry) => typeof entry === "string");
    }
    return typeof value === "string" && value !== "" ? [value] : [];
  }
  define("button", {
    render(ctx) {
      const allowsMultiple = ctx.bool("allowMultiple");
      const size = ctx.settings.buttonSize;
      const selected = toSelected(ctx.value);
      const toggle = (optionValue) => {
        if (!allowsMultiple) {
          ctx.emitChange(selected.includes(optionValue) ? "" : optionValue);
          return;
        }
        ctx.emitChange(
          selected.includes(optionValue) ? selected.filter((entry) => entry !== optionValue) : [...selected, optionValue]
        );
      };
      return h(
        "div",
        {
          class: cx(
            "rfbd-buttons",
            `rfbd-buttons--${typeof size === "string" && size ? size : "medium"}`
          ),
          role: "group"
        },
        ctx.options().map(
          (option) => h(
            "button",
            {
              type: "button",
              class: "rfbd-buttons__option",
              "aria-pressed": selected.includes(option.value) ? "true" : "false",
              onclick: () => toggle(option.value)
            },
            option.label
          )
        )
      );
    }
  });

  // dev-template/src/elements/numbers.js
  define("number", {
    render(ctx) {
      return h("input", {
        id: `${ctx.instance.id}-${ctx.element.id}`,
        name: ctx.element.id,
        class: "rfbd-input",
        type: "number",
        inputmode: "decimal",
        min: ctx.optNum("min") ?? null,
        max: ctx.optNum("max") ?? null,
        step: ctx.optNum("step") ?? null,
        placeholder: ctx.chrome.placeholder || " ",
        value: typeof ctx.value === "number" ? String(ctx.value) : "",
        oninput: (event) => ctx.emitChange(toNumber2(event.target.value))
      });
    },
    validate(value, ctx) {
      if (value === null || value === void 0 || value === "") return "";
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return errorMessage(ctx.instance, "errorNumber");
      }
      const min = ctx.optNum("min");
      const max = ctx.optNum("max");
      if (min !== null && value < min) return `Enter ${min} or more.`;
      if (max !== null && value > max) return `Enter ${max} or less.`;
      return "";
    }
  });
  var STEP_PATHS = { decrease: "M5 12h14", increase: "M12 5v14M5 12h14" };
  define("quantity", {
    render(ctx) {
      const min = ctx.num("min", 1);
      const maxSetting = ctx.optNum("max");
      const max = maxSetting ?? Number.MAX_SAFE_INTEGER;
      const step = Math.max(1, ctx.num("step", 1));
      const current = toNumber2(ctx.value) ?? min;
      const nudge = (direction) => ctx.emitChange(clamp(current + direction * step, min, max));
      const stepButton = (direction, key) => h(
        "button",
        {
          type: "button",
          class: "rfbd-quantity__step",
          "aria-label": `${key === "decrease" ? "Decrease" : "Increase"} ${ctx.chrome.label}`,
          disabled: key === "decrease" ? current <= min : current >= max,
          onclick: () => nudge(direction)
        },
        svgIcon(STEP_PATHS[key], { size: 16 })
      );
      return h(
        "div",
        { class: "rfbd-quantity" },
        stepButton(-1, "decrease"),
        h("input", {
          id: `${ctx.instance.id}-${ctx.element.id}`,
          name: ctx.element.id,
          class: "rfbd-input rfbd-quantity__input",
          type: "number",
          inputmode: "numeric",
          min: String(min),
          max: maxSetting !== null ? String(maxSetting) : null,
          step: String(step),
          value: String(current),
          oninput: (event) => ctx.emitChange(toNumber2(event.target.value)),
          // Clamp on blur, not per keystroke , snapping "1" to 5 while the
          // buyer is halfway through typing "12" would fight them.
          onblur: (event) => ctx.emitChange(clamp(toNumber2(event.target.value) ?? min, min, max))
        }),
        stepButton(1, "increase")
      );
    }
  });
  define("range-slider", {
    render(ctx) {
      const min = ctx.num("min", 0);
      const max = ctx.num("max", 100);
      const step = Math.max(1, ctx.num("step", 1));
      const unit = ctx.text("unit");
      const controlId = `${ctx.instance.id}-${ctx.element.id}`;
      const current = clamp(
        toNumber2(ctx.value) ?? ctx.num("defaultValue", min),
        min,
        max
      );
      return h(
        "div",
        { class: "rfbd-range" },
        h("input", {
          id: controlId,
          name: ctx.element.id,
          class: "rfbd-range__input",
          type: "range",
          min: String(min),
          max: String(max),
          step: String(step),
          value: String(current),
          // `aria-valuetext` so the unit is announced with the number; without
          // it a screen reader reads "70" for what the page shows as "70 kg".
          "aria-valuetext": unit ? `${current} ${unit}` : null,
          oninput: (event) => ctx.emitChange(Number(event.target.value))
        }),
        h(
          "output",
          { class: "rfbd-range__value", for: controlId },
          `${current}${unit ? ` ${unit}` : ""}`
        )
      );
    }
  });

  // dev-template/src/elements/hidden.js
  define("hidden", {
    isInput: false,
    render: () => null
  });

  // dev-template/src/core/richtext.js
  var RICH_TEXT_MARKS = ["bold", "italic", "underline", "strike", "code"];
  var HEADING_LEVELS = [2, 3, 4];
  var ALIGNMENTS = ["left", "center", "right"];
  var SAFE_LINK = /^(https?:\/\/|mailto:)/i;
  var SAFE_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i;
  var SAFE_IMAGE = /^https:\/\//i;
  function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }
  function parseSpans(value) {
    if (!Array.isArray(value)) return [];
    const spans = [];
    for (const entry of value) {
      if (!isRecord(entry) || typeof entry.text !== "string") continue;
      if (entry.text === "") continue;
      const marks = Array.isArray(entry.marks) ? RICH_TEXT_MARKS.filter((mark) => entry.marks.includes(mark)) : [];
      const href = typeof entry.href === "string" && SAFE_LINK.test(entry.href.trim()) ? entry.href.trim() : "";
      const color = typeof entry.color === "string" && SAFE_COLOR.test(entry.color.trim()) ? entry.color.trim() : "";
      const highlight = typeof entry.highlight === "string" && SAFE_COLOR.test(entry.highlight.trim()) ? entry.highlight.trim() : "";
      spans.push({ text: entry.text, marks, href, color, highlight });
    }
    return spans;
  }
  function parseBlock(value) {
    if (!isRecord(value)) return null;
    if (value.type === "heading") {
      return {
        type: "heading",
        level: HEADING_LEVELS.includes(value.level) ? value.level : 2,
        spans: parseSpans(value.spans),
        align: ALIGNMENTS.includes(value.align) ? value.align : ""
      };
    }
    if (value.type === "list") {
      const items = Array.isArray(value.items) ? value.items.map(parseSpans).filter((item) => item.length > 0) : [];
      if (items.length === 0) return null;
      return { type: "list", ordered: value.ordered === true, items };
    }
    if (value.type === "image") {
      const src = typeof value.src === "string" ? value.src.trim() : "";
      if (!SAFE_IMAGE.test(src)) return null;
      return { type: "image", src, alt: typeof value.alt === "string" ? value.alt : "" };
    }
    if (value.type === "paragraph") {
      return {
        type: "paragraph",
        spans: parseSpans(value.spans),
        align: ALIGNMENTS.includes(value.align) ? value.align : ""
      };
    }
    return null;
  }
  function fromPlainText(text) {
    return text.split(/\n{2,}/).map((paragraph) => paragraph.trim()).filter((paragraph) => paragraph !== "").map((paragraph) => ({
      type: "paragraph",
      spans: [{ text: paragraph, marks: [], href: "", color: "", highlight: "" }],
      align: ""
    }));
  }
  function parseRichText(value) {
    if (!value || value.trim() === "") return [];
    if (!value.trimStart().startsWith("[")) return fromPlainText(value);
    let decoded;
    try {
      decoded = JSON.parse(value);
    } catch {
      return fromPlainText(value);
    }
    if (!Array.isArray(decoded)) return fromPlainText(value);
    return decoded.map(parseBlock).filter((block) => block !== null);
  }
  function isRichTextEmpty(blocks) {
    return blocks.every((block) => {
      if (block.type === "image") return false;
      if (block.type === "list") return block.items.every((item) => item.length === 0);
      return block.spans.length === 0;
    });
  }
  var TOKEN_PATTERN = /\{\{\s*([\w.-]+)\s*\}\}/g;
  var DATA_TOKEN = /\{\{\s*data\s*\}\}/;
  function builtinVars(instance) {
    const context = instance?.context ?? {};
    const customer = context.customer ?? {};
    const customerName = [customer.firstName, customer.lastName].filter((part) => typeof part === "string" && part !== "").join(" ");
    return {
      "form.name": instance?.config?.formName ?? "",
      "page.title": context.pageTitle ?? (typeof document !== "undefined" ? document.title : ""),
      "page.href": context.pageUrl ?? (typeof window !== "undefined" ? window.location.href : ""),
      "customer.name": customerName,
      "customer.email": typeof customer.email === "string" ? customer.email : ""
    };
  }
  function expandText(text, vars) {
    if (!text.includes("{{")) return text;
    return text.replace(
      TOKEN_PATTERN,
      (token, name) => name in vars ? String(vars[name]) : token
    );
  }
  var STATIC_TYPES = /* @__PURE__ */ new Set(["heading", "paragraph", "html", "map", "divider"]);
  function formatAnswer(value) {
    if (value === null || value === void 0) return "";
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value === "number") return String(value);
    if (typeof value === "string") return value;
    if (Array.isArray(value)) {
      return value.map(
        (entry) => isRecord(entry) && "filename" in entry ? String(entry.filename) : formatAnswer(entry)
      ).filter((entry) => entry !== "").join(", ");
    }
    if (typeof value === "object") {
      return Object.entries(value).map(([key, entry]) => `${key}: ${formatAnswer(entry)}`).join("; ");
    }
    return "";
  }
  function summarizes(element) {
    if (STATIC_TYPES.has(element.type)) return false;
    if (element.type === "password" || element.type === "hidden") return false;
    if (element.type === "consent" && readBoolean(element.settings, "onlyForAgreement")) {
      return false;
    }
    return true;
  }
  function elementLabel(element, locale) {
    return readText(element.settings ?? {}, "label", locale) || element.type;
  }
  function answerSummary(instance) {
    const doc = instance?.document ?? {};
    const values = instance?.store?.values ?? {};
    const locale = instance?.locale;
    const lines = [];
    for (const page of doc.pages ?? []) {
      for (const element of page.elements ?? []) {
        if (!summarizes(element)) continue;
        if (element.children && Array.isArray(values[element.id])) {
          const entryName = readText(element.settings ?? {}, "entryLabel", locale) || "Entry";
          values[element.id].forEach((entry, index) => {
            if (!isRecord(entry)) return;
            for (const child of element.children) {
              if (!summarizes(child)) continue;
              const formatted2 = formatAnswer(entry[child.id]);
              if (formatted2 === "") continue;
              lines.push({
                label: `${entryName} ${index + 1} , ${elementLabel(child, locale)}`,
                value: formatted2
              });
            }
          });
          continue;
        }
        const formatted = formatAnswer(values[element.id]);
        if (formatted === "") continue;
        lines.push({ label: elementLabel(element, locale), value: formatted });
      }
    }
    return lines;
  }
  function expandVariables(blocks, vars, instance) {
    const expanded = blocks.map((block) => {
      if (block.type === "image") return block;
      if (block.type === "list") {
        return {
          ...block,
          items: block.items.map(
            (item) => item.map((span) => ({ ...span, text: expandText(span.text, vars) }))
          )
        };
      }
      return {
        ...block,
        spans: block.spans.map((span) => ({ ...span, text: expandText(span.text, vars) }))
      };
    });
    const needsData = expanded.some(
      (block) => block.type === "paragraph" && block.spans.some((span) => DATA_TOKEN.test(span.text))
    );
    if (!needsData) return expanded;
    const summary = answerSummary(instance);
    return expanded.flatMap((block) => {
      if (block.type !== "paragraph" || !block.spans.some((span) => DATA_TOKEN.test(span.text))) {
        return [block];
      }
      const before = [];
      const after = [];
      let seenToken = false;
      for (const span of block.spans) {
        if (seenToken) {
          after.push(span);
          continue;
        }
        const match = span.text.match(DATA_TOKEN);
        if (!match || match.index === void 0) {
          before.push(span);
          continue;
        }
        seenToken = true;
        const leading = span.text.slice(0, match.index);
        const trailing = span.text.slice(match.index + match[0].length);
        if (leading !== "") before.push({ ...span, text: leading });
        if (trailing !== "") after.push({ ...span, text: trailing });
      }
      const pieces = [];
      if (before.length > 0) pieces.push({ ...block, spans: before });
      if (summary.length > 0) {
        pieces.push({
          type: "list",
          ordered: false,
          items: summary.map((line) => [
            { text: `${line.label}: `, marks: ["bold"], href: "", color: "", highlight: "" },
            { text: line.value, marks: [], href: "", color: "", highlight: "" }
          ])
        });
      }
      if (after.length > 0) pieces.push({ ...block, spans: after });
      return pieces;
    });
  }
  var MARK_ELEMENTS = {
    bold: "strong",
    italic: "em",
    underline: "u",
    strike: "s",
    code: "code"
  };
  function renderSpan(span) {
    let content = span.marks.reduce(
      (children, mark) => h(MARK_ELEMENTS[mark], null, children),
      /** @type {Node | string} */
      span.text
    );
    if (span.color || span.highlight) {
      const style = {};
      if (span.color) style.color = span.color;
      if (span.highlight) style["background-color"] = span.highlight;
      content = h("span", { style }, content);
    }
    if (!span.href) return content;
    return h(
      "a",
      { class: "rfbd-richtext__link", href: span.href, rel: "noopener noreferrer nofollow" },
      content
    );
  }
  function renderBlock(block) {
    if (block.type === "heading") {
      return h(
        `h${block.level}`,
        {
          class: "rfbd-richtext__heading",
          style: block.align ? { "text-align": block.align } : null
        },
        block.spans.map(renderSpan)
      );
    }
    if (block.type === "list") {
      return h(
        block.ordered ? "ol" : "ul",
        { class: "rfbd-richtext__list" },
        block.items.map((item) => h("li", null, item.map(renderSpan)))
      );
    }
    if (block.type === "image") {
      return h("img", {
        class: "rfbd-richtext__image",
        src: block.src,
        alt: block.alt,
        loading: "lazy"
      });
    }
    return h(
      "p",
      {
        class: "rfbd-richtext__paragraph",
        style: block.align ? { "text-align": block.align } : null
      },
      block.spans.map(renderSpan)
    );
  }
  function renderRichText(value, instance, vars = {}) {
    const text = resolveTranslatable(value, instance?.locale);
    const blocks = expandVariables(
      parseRichText(text),
      { ...builtinVars(instance), ...vars },
      instance
    );
    const fragment = document.createDocumentFragment();
    if (isRichTextEmpty(blocks)) return fragment;
    fragment.append(h("div", { class: "rfbd-richtext" }, blocks.map(renderBlock)));
    return fragment;
  }

  // dev-template/src/elements/static.js
  var HEADING_TAGS = ["h2", "h3", "h4"];
  define("heading", {
    isInput: false,
    render(ctx) {
      const level = readString(ctx.settings, "level", "h2");
      return h(
        HEADING_TAGS.includes(level) ? level : "h2",
        {
          class: "rfbd-heading",
          style: { "text-align": readString(ctx.settings, "alignment", "left") }
        },
        ctx.text("label")
      );
    }
  });
  define("paragraph", {
    isInput: false,
    render(ctx) {
      return h(
        "div",
        {
          class: "rfbd-paragraph",
          style: { "text-align": readString(ctx.settings, "alignment", "left") }
        },
        renderRichText(readTranslatable(ctx.settings, "label"), ctx.instance)
      );
    }
  });
  define("html", {
    isInput: false,
    render(ctx) {
      const host = h("div", { class: "rfbd-html" });
      const fragment = document.createRange().createContextualFragment(readString(ctx.settings, "content"));
      for (const script of fragment.querySelectorAll("script")) {
        const executable = document.createElement("script");
        for (const attribute of script.attributes) {
          executable.setAttribute(attribute.name, attribute.value);
        }
        executable.textContent = script.textContent;
        script.replaceWith(executable);
      }
      host.append(fragment);
      return host;
    }
  });
  function isGoogleMapsEmbedUrl(candidate) {
    let parsed;
    try {
      parsed = new URL(candidate);
    } catch {
      return false;
    }
    return parsed.protocol === "https:" && ["www.google.com", "google.com", "maps.google.com"].includes(
      parsed.hostname
    ) && parsed.pathname.startsWith("/maps/embed");
  }
  define("map", {
    isInput: false,
    render(ctx) {
      const embedUrl = readString(ctx.settings, "embedUrl").trim();
      const height = ctx.num("height", 320);
      if (!isGoogleMapsEmbedUrl(embedUrl)) {
        return h(
          "div",
          {
            class: "rfbd-map rfbd-map--placeholder",
            style: { height: `${height}px` }
          },
          "Paste a Google Maps embed URL to show a map here."
        );
      }
      return h("iframe", {
        class: "rfbd-map",
        src: embedUrl,
        style: { height: `${height}px` },
        loading: "lazy",
        referrerpolicy: "no-referrer-when-downgrade",
        allowfullscreen: true,
        title: "Map"
      });
    }
  });
  define("divider", {
    isInput: false,
    render(ctx) {
      return h("hr", {
        class: "rfbd-divider",
        style: { "margin-block": `${ctx.num("spacing", 16)}px` }
      });
    }
  });

  // dev-template/src/elements/phone.js
  var intlState = /* @__PURE__ */ new Map();
  function stateFor(ctx) {
    const key = `${ctx.instance.id}:${ctx.element.id}`;
    if (!intlState.has(key)) intlState.set(key, { iso2: "", open: false, query: "" });
    return intlState.get(key);
  }
  function countryByIso(iso2) {
    return COUNTRIES.find((country) => country[0] === iso2) ?? null;
  }
  function localeRegion(locale) {
    const match = /-([a-zA-Z]{2})$/.exec(locale ?? "");
    return match ? match[1].toLowerCase() : null;
  }
  function resolveCountry(ctx, state) {
    if (typeof ctx.value === "string" && ctx.value.startsWith("+")) {
      const parsed = byDialCodeLongestMatch(ctx.value);
      if (parsed) return parsed;
    }
    if (state.iso2) {
      const chosen = countryByIso(state.iso2);
      if (chosen) return chosen;
    }
    const configured = ctx.text("defaultCountry", "auto") || "auto";
    const iso2 = configured === "auto" ? localeRegion(ctx.instance.locale) : configured;
    return countryByIso(iso2 ?? "") ?? countryByIso("us");
  }
  function nationalPart(value, country) {
    if (typeof value !== "string" || !value.startsWith("+")) {
      return typeof value === "string" ? value : "";
    }
    const digits = value.replace(/\D/g, "");
    return digits.startsWith(country[2]) ? digits.slice(country[2].length) : digits;
  }
  function renderPlain(ctx) {
    return h("input", {
      class: "rfbd-input",
      type: "tel",
      id: `${ctx.instance.id}-${ctx.element.id}`,
      name: ctx.element.id,
      autocomplete: "tel",
      placeholder: ctx.chrome.placeholder || null,
      required: ctx.chrome.isRequired,
      value: typeof ctx.value === "string" ? ctx.value : "",
      oninput: (event) => ctx.emitChange(event.target.value)
    });
  }
  function renderInternational(ctx) {
    const state = stateFor(ctx);
    const country = resolveCountry(ctx, state);
    const separateDialCode = ctx.bool("separateDialCode", true);
    const national = nationalPart(ctx.value, country);
    const store = (digits) => ctx.emitChange(digits === "" ? "" : `+${country[2]}${digits}`);
    const input = h("input", {
      class: "rfbd-input rfbd-intl-phone__input",
      type: "tel",
      id: `${ctx.instance.id}-${ctx.element.id}`,
      name: ctx.element.id,
      autocomplete: "tel-national",
      // `examplePlaceholder` needs per-country example data the template does
      // not carry (see the module comment); the merchant placeholder stands in.
      placeholder: ctx.chrome.placeholder || null,
      required: ctx.chrome.isRequired,
      value: national,
      inputmode: "tel",
      oninput: (event) => store(event.target.value.replace(/\D/g, ""))
    });
    const button = h(
      "button",
      {
        type: "button",
        class: "rfbd-intl-phone__button",
        "aria-haspopup": "listbox",
        "aria-expanded": state.open ? "true" : "false",
        "aria-label": `${country[1]}, +${country[2]} , change country`,
        onclick: () => {
          state.open = !state.open;
          state.query = "";
          ctx.requestRender();
        },
        onkeydown: (event) => {
          if (event.key === "ArrowDown" && !state.open) {
            event.preventDefault();
            state.open = true;
            ctx.requestRender();
          }
        }
      },
      h("span", { class: "rfbd-intl-phone__flag", "aria-hidden": "true" }, flagEmoji(country[0])),
      separateDialCode ? h("span", { class: "rfbd-intl-phone__dial" }, `+${country[2]}`) : null
    );
    const root = h(
      "div",
      { class: cx("rfbd-intl-phone", state.open && "is-open") },
      button,
      input,
      state.open ? renderDropdown(ctx, state, country) : null
    );
    if (state.open) attachOutsideClose(ctx, state, root);
    return root;
  }
  function renderDropdown(ctx, state, current) {
    const close = (focusButton) => {
      state.open = false;
      ctx.requestRender();
      if (focusButton) focusIntlPart(ctx, ".rfbd-intl-phone__button");
    };
    const choose = (country) => {
      state.iso2 = country[0];
      state.open = false;
      const digits = nationalPart(ctx.value, current);
      if (digits !== "") {
        ctx.emitChange(`+${country[2]}${digits}`);
      } else {
        ctx.requestRender();
      }
      focusIntlPart(ctx, ".rfbd-intl-phone__input");
    };
    const needle = state.query.trim().toLowerCase();
    const matches = COUNTRIES.filter(
      ([iso2, name, dial]) => needle === "" || name.toLowerCase().includes(needle) || iso2 === needle || `+${dial}`.startsWith(needle.startsWith("+") ? needle : `+${needle}`)
    );
    const list = h(
      "ul",
      { class: "rfbd-intl-phone__list", role: "listbox", "aria-label": "Country" },
      matches.map(
        (country) => h(
          "li",
          {
            class: cx(
              "rfbd-intl-phone__option",
              country[0] === current[0] && "is-selected"
            ),
            role: "option",
            tabindex: "-1",
            "aria-selected": country[0] === current[0] ? "true" : "false",
            dataset: { iso2: country[0] },
            onclick: () => choose(country),
            onkeydown: (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                choose(country);
              }
            }
          },
          h("span", { "aria-hidden": "true" }, flagEmoji(country[0])),
          h("span", { class: "rfbd-intl-phone__name" }, country[1]),
          h("span", { class: "rfbd-intl-phone__dial" }, `+${country[2]}`)
        )
      )
    );
    const search = h("input", {
      class: "rfbd-input rfbd-intl-phone__search",
      type: "search",
      // The renderer restores focus by [name] after re-renders; keep it unique.
      name: `${ctx.element.id}:country-search`,
      placeholder: "Search countries",
      "aria-label": "Search countries",
      value: state.query,
      oninput: (event) => {
        state.query = event.target.value;
        ctx.requestRender();
      }
    });
    const menu = h(
      "div",
      {
        class: "rfbd-intl-phone__menu",
        onkeydown: (event) => {
          if (event.key === "Escape") {
            event.stopPropagation();
            close(true);
            return;
          }
          if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
          event.preventDefault();
          const options = [...list.querySelectorAll('[role="option"]')];
          const at = options.indexOf(document.activeElement);
          const next = event.key === "ArrowDown" ? options[Math.min(at + 1, options.length - 1)] : at <= 0 ? search : options[at - 1];
          next?.focus();
        }
      },
      search,
      list
    );
    queueMicrotask(() => {
      if (document.activeElement === document.body) search.focus();
    });
    return menu;
  }
  function focusIntlPart(ctx, selector) {
    queueMicrotask(() => {
      ctx.instance.root.querySelector(`[data-rfbd-el="${ctx.element.id}"] ${selector}`)?.focus();
    });
  }
  function attachOutsideClose(ctx, state, root) {
    const onPointerDown = (event) => {
      if (event.composedPath().includes(root)) return;
      document.removeEventListener("pointerdown", onPointerDown);
      if (state.open) {
        state.open = false;
        ctx.requestRender();
      }
    };
    queueMicrotask(
      () => document.addEventListener("pointerdown", onPointerDown, { once: false })
    );
  }
  define("phone", {
    render(ctx) {
      return ctx.bool("internationalInput") ? renderInternational(ctx) : renderPlain(ctx);
    },
    validate(value, ctx) {
      if (typeof value !== "string" || value.trim() === "") return "";
      const requireInternational = ctx.bool("validateInternational") || ctx.bool("internationalInput");
      return isValidPhone(value, requireInternational) ? "" : errorMessage(ctx.instance, "errorPhone");
    }
  });

  // dev-template/src/core/date-bounds.js
  var ISO_DATE = /^\d{4}-\d{2}(-\d{2})?$/;
  var WEEKDAY_INDICES = {
    sunday: 0,
    monday: 1,
    tuesday: 2,
    wednesday: 3,
    thursday: 4,
    friday: 5,
    saturday: 6
  };
  function toIsoDate(date) {
    return date.toISOString().slice(0, 10);
  }
  function addDays(isoToday, days) {
    const date = /* @__PURE__ */ new Date(`${isoToday}T00:00:00Z`);
    date.setUTCDate(date.getUTCDate() + days);
    return toIsoDate(date);
  }
  function answerDatePart(value) {
    if (typeof value !== "string" || value === "") return null;
    const datePart = value.slice(0, 10);
    return ISO_DATE.test(datePart) ? datePart : null;
  }
  function resolveBound(settings, side, readAnswer2, isoToday) {
    const mode = readString(settings, `${side}Mode`, "specificDate");
    if (mode === "today") return isoToday;
    if (mode === "specificDate") {
      const configured = readString(settings, `${side}Date`).trim();
      return ISO_DATE.test(configured) ? configured : null;
    }
    if (mode === "relativeDays") {
      return addDays(isoToday, readNumber(settings, `${side}OffsetDays`, 0));
    }
    if (mode === "otherField") {
      const fieldId = readString(settings, `${side}FieldId`);
      if (fieldId === "") return null;
      return answerDatePart(readAnswer2(fieldId));
    }
    return null;
  }
  function resolveDateBounds(settings, readAnswer2, now = /* @__PURE__ */ new Date()) {
    const isoToday = toIsoDate(now);
    return {
      min: resolveBound(settings, "min", readAnswer2, isoToday),
      max: resolveBound(settings, "max", readAnswer2, isoToday)
    };
  }
  function parseDateList(settings) {
    const mode = readString(settings, "dateListMode", "none");
    if (mode !== "disable" && mode !== "enable") {
      return { mode: "none", ranges: [] };
    }
    const ranges = readString(settings, "dateList").split(",").map((entry) => entry.trim()).filter((entry) => entry !== "").flatMap((entry) => {
      const [start2, end = start2] = entry.split("..").map((part) => part.trim());
      return ISO_DATE.test(start2) && ISO_DATE.test(end) ? [{ start: start2, end }] : [];
    });
    return { mode, ranges };
  }
  function blockedWeekdays(settings) {
    return new Set(
      readStringList(settings, "disabledWeekdays").map((name) => WEEKDAY_INDICES[name.trim().toLowerCase()]).filter((index) => index !== void 0)
    );
  }
  function isInRanges(date, rule) {
    return rule.ranges.some(({ start: start2, end }) => date >= start2 && date <= end);
  }
  function isDateAllowed(date, list, weekdays) {
    if (list.mode === "disable" && isInRanges(date, list)) return false;
    if (list.mode === "enable" && !isInRanges(date, list)) return false;
    if (weekdays.size > 0 && date.length === 10) {
      const day = (/* @__PURE__ */ new Date(`${date}T00:00:00Z`)).getUTCDay();
      if (weekdays.has(day)) return false;
    }
    return true;
  }

  // dev-template/src/elements/datetime.js
  var INPUT_TYPES = {
    date: "date",
    time: "time",
    datetime: "datetime-local",
    month: "month"
  };
  function boundAttr(bound, mode, side) {
    if (!bound) return null;
    if (mode === "month") return bound.slice(0, 7);
    if (mode === "datetime") {
      return side === "min" ? `${bound}T00:00` : `${bound}T23:59`;
    }
    return bound;
  }
  define("datetime", {
    render(ctx) {
      const mode = ctx.text("mode", "date") || "date";
      const settings = ctx.settings;
      const bounds = mode === "time" ? { min: null, max: null } : resolveDateBounds(settings, (elementId) => ctx.store.get(elementId));
      return h(
        "div",
        { class: "rfbd-datetime" },
        h("input", {
          class: "rfbd-input rfbd-datetime__input",
          type: INPUT_TYPES[mode] ?? "date",
          id: `${ctx.instance.id}-${ctx.element.id}`,
          name: ctx.element.id,
          placeholder: ctx.chrome.placeholder || null,
          required: ctx.chrome.isRequired,
          value: typeof ctx.value === "string" ? ctx.value : "",
          min: boundAttr(bounds.min, mode, "min"),
          max: boundAttr(bounds.max, mode, "max"),
          oninput: (event) => ctx.emitChange(event.target.value)
        })
      );
    },
    /**
     * Ports form-core's `validateDatetime` , same order, same comparisons:
     * min, then max, then the date-list/weekday rules (dates only, months are
     * exempt like the app's), each only once there is a parseable date.
     */
    validate(value, ctx) {
      const mode = ctx.text("mode", "date") || "date";
      if (mode === "time") return "";
      const date = answerDatePart(value);
      if (!date) return "";
      const settings = ctx.settings;
      const siblings = ctx.values ?? ctx.store.values;
      const bounds = resolveDateBounds(settings, (elementId) => siblings[elementId]);
      const comparable = (bound) => mode === "month" ? bound.slice(0, 7) : bound;
      if (bounds.min && date < comparable(bounds.min)) {
        return errorMessage(ctx.instance, "errorDateMin", {
          min: comparable(bounds.min)
        });
      }
      if (bounds.max && date > comparable(bounds.max)) {
        return errorMessage(ctx.instance, "errorDateMax", {
          max: comparable(bounds.max)
        });
      }
      if (mode !== "month" && !isDateAllowed(date, parseDateList(settings), blockedWeekdays(settings))) {
        return errorMessage(ctx.instance, "errorDateBlocked");
      }
      return "";
    }
  });

  // dev-template/src/elements/signature.js
  var CANVAS_HEIGHT = 160;
  function prepare(canvas) {
    const ratio = window.devicePixelRatio || 1;
    const width = canvas.clientWidth || canvas.parentElement?.clientWidth || 300;
    if (canvas.width !== width * ratio) {
      canvas.width = width * ratio;
      canvas.height = CANVAS_HEIGHT * ratio;
    }
    const context = canvas.getContext("2d");
    if (!context) return null;
    context.setTransform(ratio, 0, 0, ratio, 0, 0);
    context.lineWidth = 2;
    context.lineCap = "round";
    context.lineJoin = "round";
    context.strokeStyle = getComputedStyle(canvas).color || "#000000";
    return context;
  }
  function restoreDrawing(canvas, dataUrl) {
    const image = new Image();
    image.onload = () => {
      const context = prepare(canvas);
      if (!context) return;
      context.drawImage(image, 0, 0, canvas.clientWidth || 300, CANVAS_HEIGHT);
    };
    image.src = dataUrl;
  }
  define("signature", {
    render(ctx) {
      const value = typeof ctx.value === "string" ? ctx.value : "";
      const isDrawn = value.startsWith("data:");
      const typedId = `${ctx.instance.id}-${ctx.element.id}-typed`;
      let isDrawing = false;
      let hasInk = isDrawn;
      const canvas = h("canvas", {
        class: "rfbd-signature__canvas",
        height: CANVAS_HEIGHT,
        // Announced as an image so a screen reader hears what it is rather
        // than skipping an unlabelled canvas; the typed input below is the
        // control such a user actually operates.
        role: "img",
        "aria-label": `${ctx.chrome.label}: drawing area`,
        style: { "touch-action": "none", height: `${CANVAS_HEIGHT}px` }
      });
      const positionOf = (event) => {
        const bounds = canvas.getBoundingClientRect();
        return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
      };
      canvas.addEventListener("pointerdown", (event) => {
        const context = prepare(canvas);
        if (!context) return;
        isDrawing = true;
        canvas.setPointerCapture(event.pointerId);
        const { x, y } = positionOf(event);
        context.beginPath();
        context.moveTo(x, y);
      });
      canvas.addEventListener("pointermove", (event) => {
        if (!isDrawing) return;
        const context = canvas.getContext("2d");
        if (!context) return;
        const { x, y } = positionOf(event);
        context.lineTo(x, y);
        context.stroke();
        hasInk = true;
      });
      const finish = () => {
        if (!isDrawing) return;
        isDrawing = false;
        if (hasInk) ctx.emitChange(canvas.toDataURL("image/png"));
      };
      canvas.addEventListener("pointerup", finish);
      canvas.addEventListener("pointercancel", finish);
      requestAnimationFrame(() => {
        prepare(canvas);
        if (isDrawn) restoreDrawing(canvas, value);
      });
      return h(
        "div",
        { class: "rfbd-signature" },
        canvas,
        h(
          "div",
          { class: "rfbd-signature__actions" },
          h(
            "button",
            {
              type: "button",
              class: "rfbd-button rfbd-button--secondary",
              disabled: !hasInk,
              onclick: () => {
                const context = prepare(canvas);
                context?.clearRect(0, 0, canvas.width, canvas.height);
                hasInk = false;
                ctx.emitChange("");
              }
            },
            "Clear"
          )
        ),
        // The keyboard route. Typing a name is a legally equivalent mark in
        // most jurisdictions, and the only pointer-free way to sign.
        h(
          "label",
          { class: "rfbd-signature__typed-label", for: typedId },
          ctx.text("typedLabel", "Or type your name")
        ),
        h("input", {
          class: "rfbd-input",
          type: "text",
          id: typedId,
          name: ctx.element.id,
          autocomplete: "name",
          required: ctx.chrome.isRequired,
          value: isDrawn ? "" : value,
          oninput: (event) => ctx.emitChange(event.target.value)
        })
      );
    }
  });

  // dev-template/src/elements/ratings.js
  var FACE_PATHS = [
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 10h.01M15 10h.01M8.5 16a4.5 4.5 0 0 1 7 0",
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 10h.01M15 10h.01M9 15.5a5 5 0 0 1 6 0",
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 10h.01M15 10h.01M9 15h6",
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 10h.01M15 10h.01M9 14.5a5 5 0 0 0 6 0",
    "M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18zM9 10h.01M15 10h.01M8.5 14a4.5 4.5 0 0 0 7 0"
  ];
  function scaleOf(ctx, fallback) {
    return clamp(Math.round(ctx.num("scale", fallback)), 2, 10);
  }
  function renderScale(ctx, { variant, points, renderMark, trailing }) {
    const controlId = `${ctx.instance.id}-${ctx.element.id}`;
    const selected = Number(ctx.value);
    const lowLabel = ctx.text("lowLabel");
    const highLabel = ctx.text("highLabel");
    const row = h(
      "div",
      {
        class: `rfbd-scale rfbd-scale--${variant}`,
        role: "radiogroup",
        "aria-label": ctx.chrome.label || null
      },
      Array.from({ length: points }, (_, index) => {
        const point = index + 1;
        const optionId = `${controlId}-${point}`;
        const suffix = point === 1 && lowLabel ? `, ${lowLabel}` : point === points && highLabel ? `, ${highLabel}` : "";
        return h(
          "label",
          { class: "rfbd-scale__point", for: optionId },
          h("input", {
            id: optionId,
            class: "rfbd-scale__radio",
            type: "radio",
            name: `${controlId}-scale`,
            value: String(point),
            checked: selected === point,
            required: ctx.chrome.isRequired,
            onchange: () => ctx.emitChange(point)
          }),
          h("span", { class: "rfbd-visually-hidden" }, `${point}${suffix}`),
          h(
            "span",
            { class: "rfbd-scale__mark", "aria-hidden": "true" },
            renderMark(point, selected >= point)
          )
        );
      }),
      trailing ?? null
    );
    return h(
      "div",
      {},
      row,
      lowLabel || highLabel ? h(
        "div",
        { class: "rfbd-scale__ends", "aria-hidden": "true" },
        h("span", {}, lowLabel),
        h("span", {}, highLabel)
      ) : null
    );
  }
  define("rating-star", {
    render(ctx) {
      const iconName = ctx.text("ratingIcon", "star") || "star";
      const paths = RATING_ICONS[iconName] ?? RATING_ICONS.star;
      const selected = Number(ctx.value);
      return renderScale(ctx, {
        variant: "star",
        points: scaleOf(ctx, 5),
        renderMark: (_point, isActive) => h(
          "span",
          { class: cx("rfbd-scale__star", isActive && "is-on") },
          svgIcon(paths, { size: 28, filled: isActive })
        ),
        // The radios already announce the value, so the number is decorative.
        trailing: ctx.bool("showNumber") && Number.isFinite(selected) && selected > 0 ? h("span", { class: "rfbd-scale__number", "aria-hidden": "true" }, String(selected)) : null
      });
    }
  });
  define("rating-level", {
    render(ctx) {
      return renderScale(ctx, {
        variant: "level",
        points: scaleOf(ctx, 10),
        renderMark: (point) => String(point)
      });
    }
  });
  define("feedback", {
    render(ctx) {
      const points = scaleOf(ctx, 5);
      return renderScale(ctx, {
        variant: "face",
        points,
        renderMark: (point) => {
          const position = (point - 1) / Math.max(1, points - 1);
          const face = FACE_PATHS[Math.round(position * (FACE_PATHS.length - 1))];
          return svgIcon(face, { size: 28 });
        }
      });
    }
  });

  // dev-template/src/elements/matrix.js
  function readAnswer(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value) ? value : {};
  }
  define("matrix", {
    render(ctx) {
      const rows = ctx.options("rows");
      const columns = ctx.options("options");
      const answer = readAnswer(ctx.value);
      const requiresEveryRow = ctx.bool("requireAllRows");
      return h(
        "div",
        { class: "rfbd-matrix__scroll" },
        h(
          "table",
          { class: "rfbd-matrix" },
          h(
            "thead",
            {},
            h(
              "tr",
              {},
              // Empty corner cell: the row headers below name each row.
              h("td", {}),
              columns.map((column) => h("th", { scope: "col" }, column.label))
            )
          ),
          h(
            "tbody",
            {},
            rows.map(
              (row) => h(
                "tr",
                {},
                h("th", { scope: "row" }, row.label),
                columns.map(
                  (column) => h(
                    "td",
                    {},
                    h("input", {
                      type: "radio",
                      name: `${ctx.element.id}.${row.id}`,
                      value: column.value,
                      checked: answer[row.id] === column.value,
                      required: requiresEveryRow,
                      "aria-label": `${row.label}: ${column.label}`,
                      onchange: () => ctx.emitChange({ ...answer, [row.id]: column.value })
                    })
                  )
                )
              )
            )
          )
        )
      );
    },
    /**
     * Port of form-core's `validateMatrix`: "required" alone only guarantees
     * one row was filled; `requireAllRows` asks for the rest, naming the rows
     * still missing rather than leaving the buyer to scan a grid for the gap.
     */
    validate(value, ctx) {
      if (!ctx.bool("requireAllRows")) return "";
      const answer = readAnswer(value);
      const missing = ctx.options("rows").filter((row) => !answer[row.id]);
      if (missing.length === 0) return "";
      const names = missing.map((row) => row.label).filter((name) => name !== "");
      return names.length > 0 ? `Answer every row: ${names.join(", ")}.` : errorMessage(ctx.instance, "errorRequired");
    }
  });

  // dev-template/src/elements/visual.js
  function toSelected2(value) {
    if (Array.isArray(value)) return value.filter((entry) => typeof entry === "string");
    return typeof value === "string" && value !== "" ? [value] : [];
  }
  function makeChoose(ctx, allowsMultiple, afterSingle) {
    return (optionValue) => {
      if (!allowsMultiple) {
        ctx.emitChange(optionValue);
        afterSingle?.();
        return;
      }
      const selected = toSelected2(ctx.value);
      ctx.emitChange(
        selected.includes(optionValue) ? selected.filter((entry) => entry !== optionValue) : [...selected, optionValue]
      );
    };
  }
  function optionInputs(ctx, { variant, controlId, allowsMultiple, isRequired, choose }) {
    const selected = toSelected2(ctx.value);
    const hidesLabels = ctx.bool("hideOptionLabels");
    return ctx.options().map((option) => {
      const optionId = `${controlId}-${option.id}`;
      return h(
        "label",
        { class: "rfbd-visual__option", for: optionId },
        h("input", {
          id: optionId,
          class: "rfbd-visual__input",
          type: allowsMultiple ? "checkbox" : "radio",
          name: `${controlId}-choice`,
          value: option.value,
          checked: selected.includes(option.value),
          required: isRequired && !allowsMultiple,
          onchange: () => choose(option.value)
        }),
        variant === "image" ? h(
          "span",
          { class: "rfbd-visual__media" },
          option.image ? (
            // Empty alt: the option's own label names it, so describing
            // the picture as well would announce the choice twice.
            h("img", { src: option.image, alt: "", loading: "lazy" })
          ) : h("span", { class: "rfbd-visual__placeholder", "aria-hidden": "true" })
        ) : h("span", {
          class: "rfbd-visual__media rfbd-visual__swatch",
          style: { background: option.color || "transparent" }
        }),
        h(
          "span",
          { class: hidesLabels ? "rfbd-visually-hidden" : "rfbd-visual__label" },
          option.label
        )
      );
    });
  }
  function renderGrid(ctx, variant) {
    const controlId = `${ctx.instance.id}-${ctx.element.id}`;
    return h(
      "div",
      {
        class: `rfbd-visual rfbd-visual--${variant}`,
        role: "group",
        "aria-label": ctx.chrome.label || null,
        style: { "--rfbd-visual-columns": readOptionsPerLine(ctx.settings, 3) }
      },
      optionInputs(ctx, {
        variant,
        controlId,
        allowsMultiple: ctx.bool("allowMultiple"),
        isRequired: ctx.chrome.isRequired,
        choose: makeChoose(ctx, ctx.bool("allowMultiple"))
      })
    );
  }
  var openDropdowns = /* @__PURE__ */ new Set();
  function renderDropdown2(ctx) {
    const key = `${ctx.instance.id}:${ctx.element.id}`;
    const controlId = `${ctx.instance.id}-${ctx.element.id}`;
    const allowsMultiple = ctx.bool("allowMultiple");
    const isOpen = openDropdowns.has(key);
    const selected = toSelected2(ctx.value);
    const chosen = ctx.options().filter((option) => selected.includes(option.value));
    const setOpen = (open) => {
      if (open) openDropdowns.add(key);
      else openDropdowns.delete(key);
      ctx.requestRender();
    };
    const focusTrigger = () => queueMicrotask(
      () => ctx.instance.root.querySelector(`[data-rfbd-el="${ctx.element.id}"] .rfbd-imgdd__button`)?.focus()
    );
    const choose = makeChoose(ctx, allowsMultiple, () => {
      openDropdowns.delete(key);
      focusTrigger();
    });
    const button = h(
      "button",
      {
        type: "button",
        id: controlId,
        class: "rfbd-imgdd__button",
        "aria-expanded": isOpen ? "true" : "false",
        "aria-haspopup": "true",
        onclick: () => setOpen(!isOpen)
      },
      chosen.length === 0 ? h(
        "span",
        { class: "rfbd-imgdd__placeholder" },
        ctx.text("placeholder", "Maak een keuze")
      ) : h(
        "span",
        { class: "rfbd-imgdd__value" },
        chosen[0].image ? h("img", { class: "rfbd-imgdd__thumb", src: chosen[0].image, alt: "", loading: "lazy" }) : null,
        h(
          "span",
          { class: "rfbd-imgdd__value-label" },
          chosen.map((option) => option.label).join(", ")
        )
      ),
      h("span", { class: "rfbd-imgdd__caret", "aria-hidden": "true" })
    );
    const panel = isOpen ? h(
      "div",
      {
        class: "rfbd-imgdd__panel",
        onkeydown: (event) => {
          if (event.key !== "Escape") return;
          event.stopPropagation();
          setOpen(false);
          focusTrigger();
        }
      },
      optionInputs(ctx, {
        variant: "image",
        controlId,
        allowsMultiple,
        isRequired: false,
        choose
      })
    ) : null;
    const root = h("div", { class: cx("rfbd-imgdd", isOpen && "is-open") }, button, panel);
    if (isOpen) {
      queueMicrotask(() => {
        const target = panel?.querySelector("input:checked") ?? panel?.querySelector("input");
        target?.focus();
      });
      const onPointerDown = (event) => {
        const live = ctx.instance.root.querySelector(
          `[data-rfbd-el="${ctx.element.id}"] .rfbd-imgdd`
        );
        if (live && event.composedPath().includes(live)) return;
        document.removeEventListener("pointerdown", onPointerDown);
        if (openDropdowns.has(key)) setOpen(false);
      };
      queueMicrotask(() => document.addEventListener("pointerdown", onPointerDown));
    }
    return root;
  }
  define("image-options", {
    render(ctx) {
      const displayMode = ctx.text("displayMode", "grid") || "grid";
      return displayMode === "dropdown" ? renderDropdown2(ctx) : renderGrid(ctx, "image");
    }
  });
  define("color-swatch", {
    render(ctx) {
      return renderGrid(ctx, "swatch");
    }
  });
  define("color-picker", {
    render(ctx) {
      const current = typeof ctx.value === "string" && ctx.value !== "" ? ctx.value : ctx.text("defaultValue", "#1c1c1c") || "#1c1c1c";
      const controlId = `${ctx.instance.id}-${ctx.element.id}`;
      const colorInput = h("input", {
        id: controlId,
        name: ctx.element.id,
        class: "rfbd-colorpicker__input",
        type: "color",
        value: current,
        required: ctx.chrome.isRequired,
        oninput: (event) => ctx.emitChange(event.target.value)
      });
      const hexInput = h("input", {
        class: "rfbd-input rfbd-colorpicker__value",
        type: "text",
        name: `${ctx.element.id}:hex`,
        "aria-label": "Hex colour value",
        value: current,
        // Only a complete hex triple is stored , emitting "#1c" mid-edit would
        // re-render the swatch to an invalid colour under the shopper's cursor.
        oninput: (event) => {
          const match = event.target.value.trim().match(/^#?([0-9a-fA-F]{6})$/);
          if (match) ctx.emitChange(`#${match[1].toLowerCase()}`);
        }
      });
      return h("div", { class: "rfbd-colorpicker" }, colorInput, hexInput);
    }
  });

  // dev-template/src/elements/repeater.js
  function toEntries(value) {
    return Array.isArray(value) ? value.filter(
      (entry) => typeof entry === "object" && entry !== null && !Array.isArray(entry)
    ) : [];
  }
  function entryStore(ctx, index) {
    const real = ctx.store;
    const repeaterId = ctx.element.id;
    const prefix = `${repeaterId}.${index}.`;
    const entryOf = () => toEntries(real.get(repeaterId))[index] ?? {};
    return {
      get values() {
        return entryOf();
      },
      get errors() {
        const view = {};
        for (const [key, message] of Object.entries(real.errors)) {
          if (key.startsWith(prefix)) view[key.slice(prefix.length)] = message;
        }
        return view;
      },
      get page() {
        return real.page;
      },
      get(id) {
        return entryOf()[id];
      },
      set(id, value) {
        const qualified = `${prefix}${id}`;
        if (real.errors[qualified]) {
          const rest = { ...real.errors };
          delete rest[qualified];
          real.setErrors(rest);
        }
        const entries = toEntries(real.get(repeaterId));
        real.set(
          repeaterId,
          entries.map(
            (existing, at) => at === index ? { ...existing, [id]: value } : existing
          )
        );
      },
      replaceValues: real.replaceValues,
      setErrors: real.setErrors,
      setPage: real.setPage,
      subscribe: real.subscribe
    };
  }
  function buildEntry(children) {
    const entry = {};
    for (const child of children) {
      const initial = readString(child.settings ?? {}, "defaultValue");
      if (initial !== "") entry[child.id] = initial;
    }
    return entry;
  }
  function renderEntry(ctx, entries, index, entryName, removeLabel, canRemove) {
    const entry = entries[index] ?? {};
    const children = ctx.element.children ?? [];
    const fields = h("div", { class: "rfbd-fields" });
    for (const child of children) {
      if (child.type === "repeater") continue;
      if (!isElementActive(child, entry)) continue;
      const definition = getDefinition(child.type);
      if (!definition) {
        console.warn(`[rfbd] unknown element type "${child.type}" skipped`);
        continue;
      }
      const childCtx = buildContext(child, {
        ...ctx.instance,
        id: `${ctx.instance.id}-${ctx.element.id}-${index}`,
        store: entryStore(ctx, index)
      });
      childCtx.fieldKey = `${ctx.element.id}.${index}.${child.id}`;
      const rendered = renderWithChrome(definition, childCtx);
      if (rendered) fields.append(rendered);
    }
    return h(
      "fieldset",
      { class: "rfbd-repeater__entry" },
      h("legend", { class: "rfbd-repeater__entry-title" }, `${entryName} ${index + 1}`),
      fields,
      h(
        "button",
        {
          type: "button",
          class: "rfbd-button rfbd-button--secondary rfbd-repeater__remove",
          disabled: !canRemove,
          onclick: () => ctx.emitChange(entries.filter((_entry, at) => at !== index))
        },
        removeLabel,
        h("span", { class: "rfbd-visually-hidden" }, ` ${entryName} ${index + 1}`)
      )
    );
  }
  define("repeater", {
    // The repeater is its own chrome: a fieldset/legend group, like the widget.
    selfChrome: true,
    render(ctx) {
      const children = ctx.element.children ?? [];
      if (children.length === 0) return null;
      const entries = toEntries(ctx.value);
      const minimum = Math.max(0, ctx.num("minEntries", 1));
      const maximum = ctx.optNum("maxEntries");
      const entryName = ctx.text("entryLabel", "Entry");
      const removeLabel = ctx.text("removeLabel", "Remove");
      const canAdd = maximum === null || entries.length < maximum;
      const canRemove = entries.length > minimum;
      return h(
        "fieldset",
        { class: "rfbd-repeater" },
        h(
          "legend",
          {
            class: cx(
              ctx.chrome.isLabelHidden ? "rfbd-visually-hidden" : "rfbd-field__label"
            )
          },
          ctx.chrome.label
        ),
        entries.map(
          (_entry, index) => renderEntry(ctx, entries, index, entryName, removeLabel, canRemove)
        ),
        canAdd ? h(
          "button",
          {
            type: "button",
            class: "rfbd-button rfbd-button--secondary",
            onclick: () => ctx.emitChange([...entries, buildEntry(children)])
          },
          ctx.text("addLabel", "Add another")
        ) : null
      );
    }
    // No validate(): core/validation.js owns the repeater wholesale , entry
    // count bounds against the repeater's own id, then every child re-checked
    // through the same per-type validators as top-level fields, filed under
    // `repeaterId.index.childId`. A validate here would never be called.
  });

  // dev-template/src/runtime/net.js
  function proxyBase(instance) {
    return instance.context.proxyBase || "/apps/rfb";
  }
  function proxyFetch(instance, path, options = {}) {
    const headers = { ...options.headers ?? {} };
    if (instance.visitorId) headers["X-Visitor-Id"] = instance.visitorId;
    return fetch(`${proxyBase(instance)}${path}`, { ...options, headers });
  }
  async function readJson(response) {
    if (response.headers.get("Content-Type")?.includes("text/html")) {
      const unreachable = new Error(
        "[rfbd] the app proxy did not answer , open the app once so the proxy is registered"
      );
      unreachable.code = "unreachable";
      throw unreachable;
    }
    const body = await response.json().catch(() => null);
    if (!response.ok) {
      const failure = new Error(`[rfbd] proxy request failed with ${response.status}`);
      failure.status = response.status;
      if (body !== null) failure.body = body;
      throw failure;
    }
    return body;
  }
  async function getJson(instance, path) {
    const response = await proxyFetch(instance, path, {
      headers: { Accept: "application/json" }
    });
    return readJson(response);
  }
  async function postJson(instance, path, body) {
    const response = await proxyFetch(instance, path, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    return readJson(response);
  }

  // dev-template/src/elements/file.js
  var GENERIC_FAILURE = "That file could not be uploaded. Please try again.";
  var fieldState = /* @__PURE__ */ new Map();
  function stateFor2(ctx) {
    const key = `${ctx.instance.id}:${ctx.element.id}`;
    if (!fieldState.has(key)) {
      fieldState.set(key, { pending: 0, error: null, previews: /* @__PURE__ */ new Map() });
    }
    return fieldState.get(key);
  }
  function toFileRefs(value) {
    if (!Array.isArray(value)) return [];
    return value.filter(
      (entry) => typeof entry === "object" && entry !== null && typeof entry.filename === "string"
    );
  }
  function fileExtension(filename) {
    const dotIndex = filename.lastIndexOf(".");
    return dotIndex === -1 ? "" : filename.slice(dotIndex + 1).toLowerCase();
  }
  function formatSize(bytes) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
  function limitsOf(ctx) {
    const allowMultiple = ctx.bool("allowMultiple");
    return {
      allowMultiple,
      allowed: ctx.list("allowedExtensions"),
      maxSizeMb: ctx.num("maxFileSizeMb", 20),
      maxFiles: allowMultiple ? ctx.num("maxFiles", 5) : 1
    };
  }
  function rejectionReason(file, limits) {
    const allowedList = limits.allowed.map((ext) => ext.toLowerCase().replace(/^\./, ""));
    if (allowedList.length > 0 && !allowedList.includes(fileExtension(file.name))) {
      return `"${file.name}" is not an accepted file type.`;
    }
    if (file.size > limits.maxSizeMb * 1024 * 1024) {
      return `"${file.name}" is larger than ${limits.maxSizeMb} MB.`;
    }
    return null;
  }
  async function postJson2(ctx, body) {
    const response = await proxyFetch(ctx.instance, "/upload", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
    if (!response.ok && response.status !== 422) {
      return { ok: false, message: GENERIC_FAILURE };
    }
    return response.json();
  }
  async function uploadAttachment(ctx, file) {
    try {
      const staged = await postJson2(ctx, {
        step: "stage",
        handle: ctx.instance.handle,
        elementId: ctx.element.id,
        filename: file.name,
        mimeType: file.type,
        size: file.size
      });
      if (!staged.ok || !staged.target) {
        return { ok: false, message: staged.message ?? GENERIC_FAILURE };
      }
      const form = new FormData();
      for (const parameter of staged.target.parameters ?? []) {
        form.append(parameter.name, parameter.value);
      }
      form.append("file", file);
      const upload = await fetch(staged.target.url, { method: "POST", body: form });
      if (!upload.ok) return { ok: false, message: GENERIC_FAILURE };
      const committed = await postJson2(ctx, {
        step: "commit",
        handle: ctx.instance.handle,
        resourceUrl: staged.target.resourceUrl
      });
      if (!committed.ok || typeof committed.fileId !== "string") {
        return { ok: false, message: committed.message ?? GENERIC_FAILURE };
      }
      return {
        ok: true,
        file: {
          filename: file.name,
          size: file.size,
          mimeType: file.type,
          fileId: committed.fileId
        }
      };
    } catch (error) {
      console.error("[rfbd] upload failed", error);
      return { ok: false, message: GENERIC_FAILURE };
    }
  }
  async function addFiles(ctx, state, incoming) {
    if (!incoming || incoming.length === 0) return;
    const limits = limitsOf(ctx);
    state.error = null;
    const alreadyAttached = toFileRefs(ctx.store.get(ctx.element.id)).length;
    const selected = Array.from(incoming);
    const room = limits.allowMultiple ? Math.max(limits.maxFiles - alreadyAttached, 0) : 1;
    const accepted = [];
    for (const file of selected.slice(0, room)) {
      const reason = rejectionReason(file, limits);
      if (reason) state.error = reason;
      else accepted.push(file);
    }
    if (selected.length > room) {
      state.error = `Only ${limits.maxFiles} ${limits.maxFiles === 1 ? "file" : "files"} can be attached.`;
    }
    state.pending += accepted.length;
    ctx.requestRender();
    for (const file of accepted) {
      const result = await uploadAttachment(ctx, file);
      if (result.ok) {
        if (file.type.startsWith("image/")) {
          state.previews.set(
            `${result.file.filename}:${result.file.size}`,
            URL.createObjectURL(file)
          );
        }
        const recorded = toFileRefs(ctx.store.get(ctx.element.id));
        ctx.emitChange(
          limits.allowMultiple ? [...recorded, result.file] : [result.file]
        );
      } else {
        state.error = result.message;
      }
      state.pending = Math.max(state.pending - 1, 0);
      ctx.requestRender();
    }
  }
  function removeFile(ctx, state, files, index) {
    const removed = files[index];
    if (removed) {
      const key = `${removed.filename}:${removed.size}`;
      const url = state.previews.get(key);
      if (url) {
        URL.revokeObjectURL(url);
        state.previews.delete(key);
      }
    }
    ctx.emitChange(files.filter((_file, at) => at !== index));
  }
  define("file", {
    render(ctx) {
      const state = stateFor2(ctx);
      const limits = limitsOf(ctx);
      const files = toFileRefs(ctx.value);
      const controlId = `${ctx.instance.id}-${ctx.element.id}`;
      const isUploading = state.pending > 0;
      const input = h("input", {
        id: controlId,
        name: ctx.element.id,
        // Visually hidden rather than display:none so it stays focusable and
        // announced; the button below simply forwards its click to it.
        class: "rfbd-visually-hidden",
        type: "file",
        multiple: limits.allowMultiple,
        accept: limits.allowed.length > 0 ? limits.allowed.map((extension) => `.${extension}`).join(",") : null,
        required: ctx.chrome.isRequired && files.length === 0,
        onchange: (event) => void addFiles(ctx, state, event.target.files)
      });
      const zone = h(
        "div",
        {
          class: "rfbd-upload",
          ondragover: (event) => {
            event.preventDefault();
            zone.classList.add("rfbd-upload--dragging");
          },
          ondragleave: () => zone.classList.remove("rfbd-upload--dragging"),
          ondrop: (event) => {
            event.preventDefault();
            zone.classList.remove("rfbd-upload--dragging");
            void addFiles(ctx, state, event.dataTransfer?.files ?? null);
          }
        },
        h("span", {}, ctx.text("placeholder", "Choose file or drag here")),
        ctx.text("hint") ? h("span", { class: "rfbd-upload__hint" }, ctx.text("hint")) : null,
        h(
          "button",
          {
            type: "button",
            class: "rfbd-upload__button",
            disabled: isUploading,
            onclick: () => input.click()
          },
          ctx.text("buttonText", "Browse file")
        ),
        input,
        isUploading ? h(
          "span",
          { class: "rfbd-upload__hint", role: "status" },
          `Uploading ${state.pending} ${state.pending === 1 ? "file" : "files"}\u2026`
        ) : null,
        state.error ? h("span", { class: "rfbd-field__error", role: "alert" }, state.error) : null,
        files.length > 0 ? h(
          "ul",
          { class: "rfbd-upload__list" },
          files.map((file, index) => {
            const preview = state.previews.get(`${file.filename}:${file.size}`);
            return h(
              "li",
              { class: "rfbd-upload__item" },
              h(
                "span",
                { class: "rfbd-upload__file" },
                preview ? h("img", { class: "rfbd-upload__thumb", src: preview, alt: "" }) : null,
                `${file.filename} `,
                h("span", { class: "rfbd-upload__hint" }, formatSize(file.size))
              ),
              h(
                "button",
                {
                  type: "button",
                  class: "rfbd-upload__remove",
                  disabled: isUploading,
                  onclick: () => removeFile(ctx, state, files, index)
                },
                "Remove",
                h("span", { class: "rfbd-visually-hidden" }, ` ${file.filename}`)
              )
            );
          })
        ) : null
      );
      return zone;
    },
    /** Port of form-core's `validateFiles`, wording and order identical. */
    validate(value, ctx) {
      const files = toFileRefs(value);
      if (files.length === 0) return "";
      const limits = limitsOf(ctx);
      if (files.length > limits.maxFiles) {
        return `Upload no more than ${limits.maxFiles} ${limits.maxFiles === 1 ? "file" : "files"}.`;
      }
      const allowedList = limits.allowed.map((ext) => ext.toLowerCase().replace(/^\./, ""));
      const wrongType = files.find(
        (file) => allowedList.length > 0 && !allowedList.includes(fileExtension(file.filename))
      );
      if (wrongType) return `"${wrongType.filename}" is not an accepted file type.`;
      const tooLarge = files.find(
        (file) => file.size > limits.maxSizeMb * 1024 * 1024
      );
      if (tooLarge) {
        return `"${tooLarge.filename}" is larger than ${limits.maxSizeMb} MB.`;
      }
      return "";
    }
  });

  // dev-template/src/core/drafts.js
  var DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1e3;
  var DRAFT_SAVE_DEBOUNCE_MS = 400;
  var MAX_DRAFT_BYTES = 64 * 1024;
  function draftStorageKey(handle) {
    return `rfb:draft:${handle}:v1`;
  }
  var NEVER_SAVED_TYPES = /* @__PURE__ */ new Set(["file", "password"]);
  function pruneEntry(element, entry) {
    const childTypes = new Map(
      (element.children ?? []).map((child) => [child.id, child.type])
    );
    const pruned = {};
    for (const [key, answer] of Object.entries(entry)) {
      const childId = key.endsWith(":confirm") ? key.slice(0, -":confirm".length) : key;
      const childType = childTypes.get(childId);
      if (childType && NEVER_SAVED_TYPES.has(childType)) continue;
      pruned[key] = answer;
    }
    return pruned;
  }
  function pruneDraftValues(formDocument, values) {
    const pruned = {};
    for (const page of formDocument?.pages ?? []) {
      for (const element of page.elements ?? []) {
        if (NEVER_SAVED_TYPES.has(element.type)) continue;
        if (!isElementActive(element, values)) continue;
        const answer = values[element.id];
        if (answer === void 0 || answer === "") continue;
        if (Array.isArray(answer) && answer.length === 0) continue;
        if (element.children && Array.isArray(answer)) {
          pruned[element.id] = answer.filter(
            (entry) => typeof entry === "object" && entry !== null && !Array.isArray(entry)
          ).map((entry) => pruneEntry(element, entry));
          continue;
        }
        pruned[element.id] = answer;
      }
    }
    return pruned;
  }
  function serializeDraft(formDocument, values, now) {
    const pruned = pruneDraftValues(formDocument, values);
    if (Object.keys(pruned).length === 0) return null;
    const json = JSON.stringify({ savedAt: now, values: pruned });
    if (json.length > MAX_DRAFT_BYTES) return null;
    return json;
  }
  function parseDraft(json, now) {
    if (!json) return null;
    let payload;
    try {
      payload = JSON.parse(json);
    } catch {
      return null;
    }
    if (typeof payload !== "object" || payload === null || typeof payload.savedAt !== "number" || typeof payload.values !== "object" || payload.values === null) {
      return null;
    }
    if (now - payload.savedAt > DRAFT_MAX_AGE_MS) return null;
    return payload.values;
  }
  function readStorage(key) {
    try {
      return globalThis.localStorage.getItem(key);
    } catch {
      return null;
    }
  }
  function writeStorage(key, value) {
    try {
      if (value === null) globalThis.localStorage.removeItem(key);
      else globalThis.localStorage.setItem(key, value);
    } catch {
    }
  }
  function clearDraft(instance) {
    writeStorage(draftStorageKey(instance.handle), null);
  }
  function attachDrafts(instance) {
    const key = draftStorageKey(instance.handle);
    const { store } = instance;
    const doc = instance.document;
    let saveTimer;
    const restored = parseDraft(readStorage(key), Date.now());
    if (restored) {
      const applicable = pruneDraftValues(doc, { ...store.values, ...restored });
      if (Object.keys(applicable).length > 0) {
        store.replaceValues({ ...store.values, ...applicable });
      }
    }
    const unsubscribe = store.subscribe((change) => {
      if (change.kind !== "value" && change.kind !== "values") return;
      clearTimeout(saveTimer);
      saveTimer = setTimeout(() => {
        writeStorage(key, serializeDraft(doc, store.values, Date.now()));
      }, DRAFT_SAVE_DEBOUNCE_MS);
    });
    const clearOnSubmit = () => clearDraft(instance);
    instance.host?.addEventListener?.("rfb:submitted", clearOnSubmit);
    return () => {
      clearTimeout(saveTimer);
      unsubscribe();
      instance.host?.removeEventListener?.("rfb:submitted", clearOnSubmit);
    };
  }

  // dev-template/src/core/tracking.js
  var TRACKING_PARAMS = [
    // UTM
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    // Meta ads
    "fbclid",
    "ad_id",
    "adset_id",
    "campaign_id",
    "placement",
    "site_source_name",
    // Google Ads
    "gclid",
    "dclid",
    "wbraid",
    "gbraid",
    "keyword",
    "matchtype",
    "device",
    // TikTok
    "ttclid",
    "tt_source",
    "tt_campaign",
    "tt_adgroup",
    "tt_ad",
    // Email platforms
    "mc_cid",
    "mc_eid",
    "hsCtaTracking",
    "klaviyo_id",
    // Affiliate
    "aff_id",
    "affiliate_id",
    "ref",
    "ref_id",
    "subid",
    "click_id",
    "transaction_id",
    // Experiments / creative
    "variant",
    "media_type",
    "source",
    "campaign",
    "creative",
    "ab_test",
    "experiment_id"
  ];
  var TRACKING_PARAM_SET = new Set(TRACKING_PARAMS);
  var MAX_TRACKING_VALUE_LENGTH = 256;
  function isTrackingParam(name) {
    return TRACKING_PARAM_SET.has(name);
  }
  function currentSearch() {
    return typeof window !== "undefined" ? window.location.search : "";
  }
  function collectTrackingParams(search = currentSearch()) {
    const captured = {};
    for (const [name, rawValue] of new URLSearchParams(search).entries()) {
      if (!TRACKING_PARAM_SET.has(name)) continue;
      const value = String(rawValue).trim().slice(0, MAX_TRACKING_VALUE_LENGTH);
      if (value === "") continue;
      if (!(name in captured)) captured[name] = value;
    }
    return captured;
  }

  // dev-template/src/core/prefill.js
  var CUSTOMER_PREFILL_PATHS = [
    "id",
    "first_name",
    "last_name",
    "name",
    "email",
    "phone",
    "default_address.address1",
    "default_address.address2",
    "default_address.city",
    "default_address.country",
    "default_address.province",
    "default_address.zip",
    "default_address.company"
  ];
  var CUSTOMER_PATH_SET = new Set(CUSTOMER_PREFILL_PATHS);
  function asString(value) {
    if (typeof value === "string") return value;
    if (typeof value === "number") return String(value);
    return "";
  }
  function flattenCustomer(customer) {
    if (!customer || typeof customer !== "object") return {};
    const address = customer.defaultAddress ?? {};
    const name = [asString(customer.firstName), asString(customer.lastName)].filter((part) => part !== "").join(" ");
    return {
      "id": asString(customer.id),
      "first_name": asString(customer.firstName),
      "last_name": asString(customer.lastName),
      name,
      "email": asString(customer.email),
      "phone": asString(customer.phone),
      "default_address.address1": asString(address.address1),
      "default_address.address2": asString(address.address2),
      "default_address.city": asString(address.city),
      "default_address.country": asString(address.countryCode),
      "default_address.province": asString(address.province),
      "default_address.zip": asString(address.zip),
      "default_address.company": asString(address.company)
    };
  }
  function collectQueryParams(search = currentSearch()) {
    const params = {};
    for (const [name, rawValue] of new URLSearchParams(search).entries()) {
      if (name in params) continue;
      params[name] = String(rawValue).trim().slice(0, MAX_TRACKING_VALUE_LENGTH);
    }
    return params;
  }
  function buildPrefillContext(context, search = currentSearch()) {
    return {
      customer: flattenCustomer(context?.customer),
      params: collectQueryParams(search)
    };
  }
  var TOKEN_PATTERN2 = /\{\{\s*([\w.-]+)\s*\}\}/g;
  function resolveTokens(template, prefill) {
    if (typeof template !== "string" || !template.includes("{{")) {
      return typeof template === "string" ? template : "";
    }
    return template.replace(TOKEN_PATTERN2, (token, name) => {
      if (name.startsWith("customer.")) {
        const path = name.slice("customer.".length);
        if (!CUSTOMER_PATH_SET.has(path)) return "";
        return prefill?.customer?.[path] ?? "";
      }
      if (name.startsWith("params.")) {
        const param = name.slice("params.".length);
        return prefill?.params?.[param] ?? "";
      }
      if (isTrackingParam(name)) {
        return prefill?.params?.[name] ?? "";
      }
      return token;
    });
  }

  // dev-template/src/core/initial-values.js
  var STATIC_TYPES2 = /* @__PURE__ */ new Set(["heading", "paragraph", "html", "map", "divider"]);
  var MULTI_VALUE_TYPES = /* @__PURE__ */ new Set(["checkboxes", "file", "product", "repeater", "bulk-order"]);
  var OPTION_TYPES = /* @__PURE__ */ new Set([
    "dropdown",
    "radio",
    "checkboxes",
    "button",
    "matrix",
    "image-options",
    "color-swatch"
  ]);
  function hiddenValue(element, prefill, context, search) {
    const source = readString(element.settings, "source", "fixed");
    if (source === "page") {
      if (typeof context?.pageUrl === "string") return context.pageUrl;
      return typeof window !== "undefined" ? window.location.href : "";
    }
    if (source === "query") {
      const name = readString(element.settings, "queryParam");
      if (name === "") return "";
      return new URLSearchParams(search).get(name) ?? "";
    }
    return resolveTokens(readString(element.settings, "defaultValue"), prefill);
  }
  function matchingOptionValues(element, lines) {
    const needles = lines.map((line) => line.trim().toLowerCase()).filter((line) => line !== "");
    if (needles.length === 0) return [];
    return readRawOptions(element.settings).filter((option) => {
      const submitted = optionSubmittedValue(option).toLowerCase();
      const labels = (typeof option.label === "string" ? [option.label] : Object.values(option.label ?? {})).map((label) => String(label).trim().toLowerCase());
      return needles.some((needle) => needle === submitted || labels.includes(needle));
    }).map(optionSubmittedValue);
  }
  function initialValueFor(element, prefill, context, search) {
    if (STATIC_TYPES2.has(element.type)) return void 0;
    if (element.type === "consent") {
      return readBoolean(element.settings, "checkedByDefault");
    }
    if (element.type === "switch") {
      return readBoolean(element.settings, "defaultOn");
    }
    if (element.type === "hidden") {
      return hiddenValue(element, prefill, context, search);
    }
    if (element.type === "address") {
      if (!readBoolean(element.settings, "prefillFromCustomer")) return {};
      const customer = prefill.customer ?? {};
      const parts = {};
      for (const key of ["address1", "address2", "city", "province", "zip", "country", "company"]) {
        const part = customer[`default_address.${key}`];
        if (typeof part === "string" && part !== "") parts[key] = part;
      }
      return parts;
    }
    if (element.type === "range-slider" || element.type === "quantity") {
      return readNumber(
        element.settings,
        "defaultValue",
        readNumber(element.settings, "min", 0)
      );
    }
    if (MULTI_VALUE_TYPES.has(element.type)) {
      if (OPTION_TYPES.has(element.type)) {
        const preselected = matchingOptionValues(
          element,
          readString(element.settings, "defaultValue").split("\n")
        );
        if (preselected.length > 0) return preselected;
      }
      return [];
    }
    const flaggedDefault = readRawOptions(element.settings).find(
      (option) => option.isDefault === true
    );
    if (flaggedDefault) return optionSubmittedValue(flaggedDefault);
    if (OPTION_TYPES.has(element.type)) {
      const [match] = matchingOptionValues(element, [
        readString(element.settings, "defaultValue")
      ]);
      if (match) return match;
    }
    return resolveTokens(readString(element.settings, "defaultValue"), prefill);
  }
  function buildEntry2(element, context, search = currentSearch()) {
    const prefill = buildPrefillContext(context, search);
    const entry = {};
    for (const child of element.children ?? []) {
      const initial = initialValueFor(child, prefill, context, search);
      if (initial !== void 0) entry[child.id] = initial;
    }
    return entry;
  }
  function computeInitialValues(document2, context, search = currentSearch()) {
    const prefill = buildPrefillContext(context, search);
    const values = {};
    for (const page of document2?.pages ?? []) {
      for (const element of page.elements ?? []) {
        if (element.type === "repeater") {
          const minimum = Math.max(0, readNumber(element.settings, "minEntries", 1));
          values[element.id] = Array.from(
            { length: minimum },
            () => buildEntry2(element, context, search)
          );
          continue;
        }
        const initial = initialValueFor(element, prefill, context, search);
        if (initial !== void 0) values[element.id] = initial;
      }
    }
    return values;
  }

  // dev-template/src/core/store.js
  function createStore(initialValues = {}) {
    const state = {
      values: { ...initialValues },
      errors: {},
      page: 0
    };
    const listeners = /* @__PURE__ */ new Set();
    function notify(change) {
      for (const listener of listeners) {
        try {
          listener(change, state);
        } catch (error) {
          console.error("[rfbd] store listener failed", error);
        }
      }
    }
    return {
      /** Live references , treat as read-only outside the store. */
      get values() {
        return state.values;
      },
      get errors() {
        return state.errors;
      },
      get page() {
        return state.page;
      },
      get(id) {
        return state.values[id];
      },
      set(id, value) {
        if (state.values[id] === value) return;
        state.values[id] = value;
        if (state.errors[id]) {
          const rest = { ...state.errors };
          delete rest[id];
          state.errors = rest;
        }
        notify({ kind: "value", id });
      },
      /** Replaces all answers at once (reset, draft restore). */
      replaceValues(values) {
        state.values = { ...values };
        state.errors = {};
        notify({ kind: "values" });
      },
      setErrors(errors) {
        state.errors = { ...errors };
        notify({ kind: "errors" });
      },
      setPage(page) {
        if (state.page === page) return;
        state.page = page;
        notify({ kind: "page", page });
      },
      subscribe(listener) {
        listeners.add(listener);
        return () => listeners.delete(listener);
      }
    };
  }

  // dev-template/src/core/tokens.js
  var COLOR_SCHEMES = {
    "default": { background: "#ffffff", surface: "#f2f2f2", text: "#1c1c1c", textSubdued: "#5c5c5c", border: "#767676", button: "#1c1c1c", buttonLabel: "#ffffff", outlineButton: "#1c1c1c", accent: "#1c1c1c", critical: "#a8200d" },
    "forest-light": { background: "#f4f8f2", surface: "#e7f0e4", text: "#17301c", textSubdued: "#4a5f4d", border: "#6f8471", button: "#1f4d2c", buttonLabel: "#f4f8f2", outlineButton: "#1f4d2c", accent: "#2a6b3c", critical: "#9a2114" },
    "cool-ocean": { background: "#f2f7fa", surface: "#e3eef4", text: "#12262f", textSubdued: "#42606c", border: "#6b838d", button: "#0f4a5e", buttonLabel: "#f2f7fa", outlineButton: "#0f4a5e", accent: "#12657f", critical: "#9c2415" },
    "matcha-calm": { background: "#f6f8ee", surface: "#eaefd9", text: "#242b12", textSubdued: "#525a35", border: "#7c8560", button: "#3c4a18", buttonLabel: "#f6f8ee", outlineButton: "#3c4a18", accent: "#556b21", critical: "#95230f" },
    "arctic-light": { background: "#f3f6fc", surface: "#e4ebf8", text: "#141d33", textSubdued: "#455170", border: "#6d7994", button: "#1b3d78", buttonLabel: "#f3f6fc", outlineButton: "#1b3d78", accent: "#1d4ed8", critical: "#9d2213" },
    "golden-dusk": { background: "#fdf8ef", surface: "#f7ecd7", text: "#2e2110", textSubdued: "#5e4a2a", border: "#8a7550", button: "#5a3f12", buttonLabel: "#fdf8ef", outlineButton: "#5a3f12", accent: "#8a5a12", critical: "#9c2a12" },
    "coral-bloom": { background: "#fdf4f3", surface: "#fae5e2", text: "#33150f", textSubdued: "#6b4038", border: "#966a62", button: "#8a2a1c", buttonLabel: "#fdf4f3", outlineButton: "#8a2a1c", accent: "#a83422", critical: "#8a1f10" },
    "forest-dark": { background: "#12241a", surface: "#1b3325", text: "#e8f2e9", textSubdued: "#a8c2ae", border: "#6d8c76", button: "#a9dcb6", buttonLabel: "#0d1f14", outlineButton: "#a9dcb6", accent: "#7ec894", critical: "#ffb3a7" },
    "sunset-dark": { background: "#2a1712", surface: "#3a231b", text: "#f8e8e0", textSubdued: "#d0aa9a", border: "#9e7566", button: "#f0b48c", buttonLabel: "#2a1712", outlineButton: "#f0b48c", accent: "#e59a6c", critical: "#ffb0a3" },
    "midnight-blue": { background: "#101a2e", surface: "#1a2740", text: "#e6ecf8", textSubdued: "#a8b8d4", border: "#6a7da1", button: "#9fc2f5", buttonLabel: "#0c1526", outlineButton: "#9fc2f5", accent: "#7aa8ea", critical: "#ffb1a6" },
    "cosmic-purple": { background: "#1a1226", surface: "#271a37", text: "#eee6f8", textSubdued: "#bda9d4", border: "#826ba1", button: "#c9aaf0", buttonLabel: "#170f21", outlineButton: "#c9aaf0", accent: "#a984e0", critical: "#ffb0a8" },
    "deep-sea": { background: "#0c2229", surface: "#143038", text: "#e2f1f4", textSubdued: "#a2c3cb", border: "#638892", button: "#8fd4e0", buttonLabel: "#08191e", outlineButton: "#8fd4e0", accent: "#63bccd", critical: "#ffb2a6" }
  };
  var INPUT_STYLES = {
    "material-filled": { labelPlacement: "floating", fill: "surface", borderEdges: "bottom", radius: 8 },
    "material-outlined": { labelPlacement: "floating", fill: "transparent", borderEdges: "all", radius: 8 },
    "classic": { labelPlacement: "above", fill: "surface", borderEdges: "all", radius: 8 },
    "classic-rounded": { labelPlacement: "above", fill: "surface", borderEdges: "all", radius: 999 },
    "flat": { labelPlacement: "above", fill: "surface", borderEdges: "none", radius: 8 },
    "flat-rounded": { labelPlacement: "above", fill: "surface", borderEdges: "none", radius: 999 },
    "line": { labelPlacement: "below", fill: "transparent", borderEdges: "bottom", radius: 0 },
    "line-rounded": { labelPlacement: "below", fill: "transparent", borderEdges: "bottom", radius: 999 }
  };
  var FONT_STACKS = {
    theme: "inherit",
    system: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    serif: 'Georgia, Cambria, "Times New Roman", Times, serif',
    mono: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace'
  };
  var FONT_SIZE_SCALE = { small: 14, medium: 16, large: 18 };
  function getColorScheme(id) {
    return COLOR_SCHEMES[id] ?? COLOR_SCHEMES.default;
  }
  function getInputStyle(id) {
    return INPUT_STYLES[id] ?? INPUT_STYLES["material-filled"];
  }
  function borderWidths(edges) {
    if (edges === "all") return "1px";
    if (edges === "bottom") return "0 0 1px 0";
    return "0";
  }
  function override(appearance, id, fallback) {
    const value = readString(appearance, id).trim();
    return value === "" ? fallback : value;
  }
  function computeTokens(appearance) {
    const scheme = getColorScheme(readString(appearance, "colorScheme", "default"));
    const colors = {
      ...scheme,
      background: override(appearance, "colorBackground", scheme.background),
      text: override(appearance, "colorText", scheme.text),
      button: override(appearance, "colorButton", scheme.button),
      buttonLabel: override(appearance, "colorButtonLabel", scheme.buttonLabel),
      outlineButton: override(appearance, "colorOutlineButton", scheme.outlineButton)
    };
    const inputStyle = getInputStyle(readString(appearance, "inputStyle", "material-filled"));
    const baseFontSize = FONT_SIZE_SCALE[readString(appearance, "fontSize", "medium")] ?? FONT_SIZE_SCALE.medium;
    const fontStack = FONT_STACKS[readString(appearance, "fontFamily", "theme")] ?? FONT_STACKS.theme;
    const width = readNumber(appearance, "width", 600);
    const isTransparent = readString(appearance, "backgroundStyle", "color") === "transparent";
    return {
      "--rfb-color-background": isTransparent ? "transparent" : colors.background,
      "--rfb-color-surface": colors.surface,
      "--rfb-color-text": colors.text,
      "--rfb-color-text-subdued": colors.textSubdued,
      "--rfb-color-border": colors.border,
      "--rfb-color-button": colors.button,
      "--rfb-color-button-label": colors.buttonLabel,
      "--rfb-color-outline-button": colors.outlineButton,
      "--rfb-color-accent": colors.accent,
      "--rfb-color-critical": colors.critical,
      "--rfb-font-family": fontStack,
      "--rfb-font-size": `${baseFontSize}px`,
      "--rfb-font-size-small": `${Math.round(baseFontSize * 0.875)}px`,
      "--rfb-font-size-large": `${Math.round(baseFontSize * 1.25)}px`,
      "--rfb-input-radius": `${inputStyle.radius}px`,
      "--rfb-input-fill": inputStyle.fill === "surface" ? colors.surface : "transparent",
      "--rfb-input-border-width": borderWidths(inputStyle.borderEdges),
      "--rfb-input-padding-block": "10px",
      "--rfb-form-padding": isTransparent ? "0px" : "clamp(20px, 4vw, 32px)",
      "--rfb-form-radius": isTransparent ? "0px" : "12px",
      "--rfb-form-shadow": isTransparent ? "none" : "0 1px 2px rgba(26, 26, 26, 0.12), 0 4px 16px rgba(26, 26, 26, 0.06)",
      "--rfb-form-width": `${width}px`
    };
  }
  function labelPlacement(appearance) {
    return getInputStyle(readString(appearance, "inputStyle", "material-filled")).labelPlacement;
  }

  // dev-template/src/runtime/events.js
  function emit(instance, name, detail = {}, options = {}) {
    const event = new CustomEvent(name, {
      bubbles: true,
      cancelable: options.cancelable === true,
      detail: { ...detail, source: "template", handle: instance.handle }
    });
    return instance.host.dispatchEvent(event);
  }

  // dev-template/src/runtime/layouts.js
  var FREQUENCY_HOURS = {
    always: 0,
    "1h": 1,
    "4h": 4,
    "12h": 12,
    "1d": 24,
    "3d": 72,
    "1w": 168,
    "2w": 336,
    "4w": 672,
    "365d": 8760
  };
  function readCookie(name) {
    for (const entry of document.cookie.split(";")) {
      const [key, ...rest] = entry.trim().split("=");
      if (key === name) return decodeURIComponent(rest.join("="));
    }
    return null;
  }
  function cookieName(kind, handle) {
    return `rfb-${kind}-${encodeURIComponent(handle)}`;
  }
  function mayAutoOpen(handle, frequencyKey) {
    if (frequencyKey === "always") return true;
    return readCookie(cookieName("shown", handle)) !== frequencyKey;
  }
  function isTimerSuppressed(handle) {
    return readCookie(cookieName("dismissed", handle)) !== null;
  }
  function recordAutoOpen(handle, frequencyKey) {
    const hours = FREQUENCY_HOURS[frequencyKey] ?? FREQUENCY_HOURS["365d"];
    document.cookie = `${cookieName("shown", handle)}=${encodeURIComponent(frequencyKey)}; path=/; max-age=${hours * 3600}; SameSite=Lax`;
  }
  function recordDismissal(handle) {
    document.cookie = `${cookieName("dismissed", handle)}=1; path=/; max-age=${24 * 3600}; SameSite=Lax`;
  }
  var EXIT_INTENT_ARM_DELAY_MS = 500;
  function scrolledFraction() {
    const root = document.documentElement;
    const scrollable = root.scrollHeight - root.clientHeight;
    if (scrollable <= 0) return 1;
    return (window.scrollY || root.scrollTop) / scrollable;
  }
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  function applyLayout(instance) {
    const controller = new AbortController();
    instance.abortController = controller;
    if (instance.layout !== "float" && instance.layout !== "popup") {
      instance.host.append(instance.root);
      return;
    }
    const handle = instance.handle;
    if (window.RichFormBuilder?.forms?.().includes(handle)) {
      console.warn(
        `[rfbd] the app widget already provides a launcher for "${handle}"; the template will not add a second one. Remove one of the two placements.`
      );
      return;
    }
    buildLauncher(instance, controller.signal);
  }
  function buildLauncher(instance, signal) {
    const appearance = instance.document.panels?.appearance ?? {};
    const { locale, host } = instance;
    const variant = instance.layout;
    const position = readString(appearance, "position", "bottom-right");
    const iconName = readString(appearance, "launcherIcon", "envelope");
    const label = readText(appearance, "launcherLabel", locale).trim();
    const accessibleName = label !== "" ? label : "Open form";
    const dialogName = readText(instance.document.panels?.header ?? {}, "title", locale) || accessibleName;
    const iconPaths = LAUNCHER_ICONS[iconName];
    const icon = iconPaths ? svgIcon(iconPaths, { size: 24 }) : null;
    if (icon) icon.classList.add("rfbd-launcher__icon");
    const button = h(
      "button",
      {
        type: "button",
        class: "rfbd-launcher__button",
        "aria-expanded": "false",
        "aria-label": label === "" ? accessibleName : null,
        onclick: () => isOpen ? close() : open()
      },
      icon,
      label !== "" ? h("span", { class: "rfbd-launcher__label" }, label) : null
    );
    const backdrop = h("button", {
      type: "button",
      class: "rfbd-launcher__backdrop",
      "aria-label": "Close form",
      hidden: true,
      onclick: () => close()
    });
    const panel = h(
      "div",
      {
        class: "rfbd-launcher__panel",
        role: "dialog",
        "aria-modal": "true",
        "aria-label": dialogName,
        hidden: true
      },
      h(
        "button",
        {
          type: "button",
          class: "rfbd-launcher__close",
          "aria-label": "Close form",
          onclick: () => close()
        },
        h("span", { "aria-hidden": "true" }, "\xD7")
      ),
      instance.root
    );
    const launcher = h(
      "div",
      { class: cx("rfbd-launcher", `rfbd-launcher--${variant}`, `rfbd-launcher--${position}`) },
      button,
      backdrop,
      panel
    );
    host.append(launcher);
    signal.addEventListener("abort", () => launcher.remove(), { once: true });
    let isOpen = false;
    let wasAutoOpened = false;
    panel.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape") {
          close();
          return;
        }
        if (event.key !== "Tab") return;
        const focusable = [...panel.querySelectorAll(FOCUSABLE)];
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      },
      { signal }
    );
    backdrop.addEventListener(
      "keydown",
      (event) => {
        if (event.key === "Escape") close();
      },
      { signal }
    );
    function open() {
      if (isOpen) return;
      isOpen = true;
      launcher.dataset.open = "true";
      button.setAttribute("aria-expanded", "true");
      backdrop.hidden = false;
      panel.hidden = false;
      panel.querySelector(FOCUSABLE)?.focus();
      emit(instance, "rfb:open");
    }
    function close() {
      if (!isOpen) return;
      isOpen = false;
      delete launcher.dataset.open;
      button.setAttribute("aria-expanded", "false");
      backdrop.hidden = true;
      panel.hidden = true;
      button.focus();
      if (wasAutoOpened) {
        recordDismissal(instance.handle);
        wasAutoOpened = false;
      }
      emit(instance, "rfb:close");
    }
    instance.open = open;
    instance.close = close;
    armTriggers(instance, appearance, signal, () => {
      if (!host.isConnected) return;
      wasAutoOpened = true;
      open();
    });
  }
  function armTriggers(instance, appearance, signal, onFire) {
    const handle = instance.handle;
    const frequencyKey = readString(appearance, "reopenFrequency", "365d");
    if (!mayAutoOpen(handle, frequencyKey)) return;
    const controller = new AbortController();
    const triggerSignal = controller.signal;
    signal.addEventListener("abort", () => controller.abort(), { once: true });
    const fire = () => {
      controller.abort();
      recordAutoOpen(handle, frequencyKey);
      onFire();
    };
    let armedAny = false;
    if (readBoolean(appearance, "openAfterTimerEnabled") && !isTimerSuppressed(handle)) {
      const seconds = Math.max(0, readNumber(appearance, "openAfterTimerSeconds", 5));
      const timer = setTimeout(fire, seconds * 1e3);
      triggerSignal.addEventListener("abort", () => clearTimeout(timer), { once: true });
      armedAny = true;
    }
    if (readBoolean(appearance, "openAfterScrollEnabled")) {
      const threshold = Math.min(100, Math.max(0, readNumber(appearance, "openAfterScrollPercent", 50))) / 100;
      window.addEventListener(
        "scroll",
        () => {
          if (scrolledFraction() >= threshold) fire();
        },
        { signal: triggerSignal, passive: true }
      );
      armedAny = true;
    }
    if (readBoolean(appearance, "openOnExitIntent")) {
      const armedAt = Date.now();
      document.addEventListener(
        "mouseleave",
        (event) => {
          if (Date.now() - armedAt < EXIT_INTENT_ARM_DELAY_MS) return;
          if (event.clientY <= 0) fire();
        },
        { signal: triggerSignal }
      );
      armedAny = true;
    }
    if (!armedAny) controller.abort();
  }

  // dev-template/src/runtime/merchant-script.js
  function runMerchantScript(instance, code) {
    if (!code || typeof code !== "string") return;
    try {
      const script = document.createElement("script");
      script.text = code;
      script.dataset.rfbdMerchantScript = instance.id;
      document.body.append(script);
      script.remove();
    } catch (error) {
      console.error("[rfbd] merchant script failed", error);
    }
  }

  // dev-template/src/core/dynamic-hidden.js
  function answerMatches(answer, expected) {
    if (typeof answer === "string") return answer === expected;
    if (Array.isArray(answer)) {
      return answer.some(
        (entry) => typeof entry === "string" && entry === expected
      );
    }
    if (typeof answer === "boolean") {
      const normalized = expected.trim().toLowerCase();
      if (normalized === "yes" || normalized === "true") return answer;
      if (normalized === "no" || normalized === "false") return !answer;
      return false;
    }
    if (typeof answer === "number") return String(answer) === expected;
    return false;
  }
  function readValueRules(bag, id) {
    const value = bag?.[id];
    if (!Array.isArray(value)) return [];
    return value.filter(
      (entry) => typeof entry === "object" && entry !== null && !Array.isArray(entry) && typeof entry.when === "string" && typeof entry.is === "string" && typeof entry.value === "string"
    );
  }
  function computeDynamicHiddenValues(formDocument, values) {
    const elements = (formDocument.pages ?? []).flatMap(
      (page) => page.elements ?? []
    );
    const hiddenIds = new Set(
      elements.filter((element) => element.type === "hidden").map((element) => element.id)
    );
    const computed = {};
    for (const element of elements) {
      if (element.type !== "hidden") continue;
      const settings = element.settings ?? {};
      if (readString(settings, "source", "fixed") !== "dynamic") continue;
      const rules = readValueRules(settings, "dynamicRules");
      const matched = rules.find(
        (rule) => rule.when !== "" && !hiddenIds.has(rule.when) && answerMatches(values[rule.when], rule.is)
      );
      computed[element.id] = matched ? matched.value : readString(settings, "defaultValue");
    }
    return computed;
  }

  // dev-template/src/runtime/after-submit.js
  async function showOutcome(instance, outcome) {
    if (!outcome?.ok) return;
    const afterSubmit = instance.document.panels?.afterSubmit ?? {};
    const settings = instance.document.panels?.settings ?? {};
    const willRedirect = Boolean(outcome.redirectUrl);
    const fired = fireAnalytics(instance, afterSubmit, outcome);
    if (fired && willRedirect) {
      await new Promise((resolve) => setTimeout(resolve, 300));
    }
    if (readBoolean(settings, "afterSubmitScriptEnabled")) {
      runMerchantScript(instance, readString(settings, "afterSubmitScript"));
    }
    if (willRedirect) {
      setTimeout(() => window.location.assign(outcome.redirectUrl), 0);
      return;
    }
    const card = buildSuccessCard(instance, afterSubmit, outcome);
    const action = readString(afterSubmit, "action", "clear");
    if (action === "hide") {
      instance.root.replaceChildren(card);
      return;
    }
    instance.store.replaceValues(
      computeInitialValues(instance.document, instance.context)
    );
    instance.store.setPage(0);
    emit(instance, "rfb:reset");
    const bodyWrap = instance.root.querySelector(".rfbd-body-wrap");
    if (bodyWrap) bodyWrap.prepend(card);
    else instance.root.prepend(card);
    card.scrollIntoView({ block: "nearest" });
  }
  function buildSuccessCard(instance, afterSubmit, outcome) {
    const title = outcome.title ?? readText(afterSubmit, "title", instance.locale);
    const message = outcome.message ?? readText(afterSubmit, "message", instance.locale);
    const iconName = readString(afterSubmit, "successIcon", "check-circle");
    const iconPath = SUCCESS_ICONS[iconName];
    return h(
      "div",
      { class: "rfbd-status rfbd-status--success", role: "status" },
      iconPath ? h(
        "span",
        { class: "rfbd-status__icon" },
        svgIcon(iconPath, { size: 28 })
      ) : null,
      title ? h("p", { class: "rfbd-status__title" }, title) : null,
      message ? h("div", { class: "rfbd-status__body" }, renderRichText(message, instance)) : null,
      outcome.ticket ? h(
        "p",
        { class: "rfbd-status__body" },
        "Your ticket reference is ",
        h("strong", null, outcome.ticket),
        "."
      ) : null,
      outcome.discountCode ? discountLine(outcome.discountCode) : null,
      outcome.warning ? h("p", { class: "rfbd-status__warning", role: "alert" }, outcome.warning) : null
    );
  }
  function discountLine(code) {
    const button = h(
      "button",
      {
        type: "button",
        class: "rfbd-status__copy",
        "aria-label": `Copy discount code ${code}`,
        onclick: () => {
          navigator.clipboard?.writeText(code).then(() => {
            button.textContent = "Copied";
            setTimeout(() => {
              button.textContent = "Copy";
            }, 2e3);
          }).catch(() => {
          });
        }
      },
      "Copy"
    );
    return h(
      "p",
      { class: "rfbd-status__body" },
      "Use code ",
      h("strong", null, code),
      " at checkout. ",
      button
    );
  }
  function fireAnalytics(instance, afterSubmit, outcome) {
    const variables = analyticsVariables(instance);
    let fired = false;
    try {
      if (readBoolean(afterSubmit, "gaEnabled")) {
        fired = fireGoogleEvent(afterSubmit, variables) || fired;
      }
    } catch (error) {
      console.error("[rfbd] GA event failed", error);
    }
    try {
      if (readBoolean(afterSubmit, "metaEnabled")) {
        fired = fireMetaEvents(afterSubmit, variables, outcome.submissionId ?? "") || fired;
      }
    } catch (error) {
      console.error("[rfbd] Meta event failed", error);
    }
    try {
      if (readBoolean(afterSubmit, "shopifyIdentifyEnabled")) {
        identifyToShopify(instance);
      }
    } catch (error) {
      console.error("[rfbd] Shopify identify failed", error);
    }
    return fired;
  }
  function analyticsVariables(instance) {
    const header = instance.document.panels?.header ?? {};
    return {
      "form.name": readText(header, "title", instance.locale),
      "form.handle": instance.handle,
      "page.title": instance.context.pageTitle || document.title,
      "page.url": instance.context.pageUrl || window.location.href
    };
  }
  function expandTokens(template, variables) {
    return template.replace(
      /\{\{\s*([\w.-]+)\s*\}\}/g,
      (token, name) => name in variables ? variables[name] : token
    );
  }
  function fireGoogleEvent(afterSubmit, variables) {
    const page = window;
    const name = expandTokens(readString(afterSubmit, "gaEventName", "form_submit"), variables) || "form_submit";
    const payload = {
      event_category: expandTokens(
        readString(afterSubmit, "gaEventCategory", "Forms"),
        variables
      ),
      event_action: expandTokens(
        readString(afterSubmit, "gaEventAction", "submit"),
        variables
      ),
      event_label: expandTokens(readString(afterSubmit, "gaEventLabel", ""), variables)
    };
    if (typeof page.gtag === "function") {
      const conversionId = page.trekkie?.config?.["Google Gtag Pixel"]?.conversionId;
      page.gtag("event", name, {
        ...payload,
        ...conversionId ? { send_to: conversionId } : {}
      });
      return true;
    }
    if (typeof page.ga === "function") {
      page.ga(
        "send",
        "event",
        payload.event_category,
        payload.event_action,
        payload.event_label
      );
      return true;
    }
    if (Array.isArray(page.dataLayer)) {
      page.dataLayer.push({ event: name, ...payload });
      return true;
    }
    return false;
  }
  function fireMetaEvents(afterSubmit, variables, submissionId) {
    const page = window;
    if (typeof page.fbq !== "function") return false;
    page.fbq(
      "track",
      "Lead",
      {
        content_name: variables["form.name"] ?? "",
        page_location: window.location.href,
        page_title: document.title
      },
      { eventID: submissionId }
    );
    const customName = expandTokens(
      readString(afterSubmit, "metaEventName", "FormSubmit"),
      variables
    );
    if (customName !== "") {
      page.fbq("trackCustom", customName, {
        pageTitle: document.title,
        pageHref: window.location.href,
        formTitle: variables["form.name"] ?? ""
      });
    }
    return true;
  }
  function firstAnswerOfType(instance, type) {
    for (const page of instance.document.pages ?? []) {
      for (const element of page.elements ?? []) {
        if (element.type !== type) continue;
        const answer = instance.store.get(element.id);
        if (typeof answer === "string" && answer.trim() !== "") return answer.trim();
      }
    }
    return null;
  }
  function identifyToShopify(instance) {
    const visitor = window.Shopify?.analytics?.visitor;
    if (typeof visitor !== "function") return;
    const email = firstAnswerOfType(instance, "email");
    const phone = firstAnswerOfType(instance, "phone");
    if (!email && !phone) return;
    visitor({
      ...email ? { email } : {},
      ...phone ? { phone } : {}
    });
  }

  // dev-template/src/runtime/captcha.js
  var TIMEOUT_MS = 15e3;
  var PROVIDERS = {
    turnstile: {
      id: "rfb-turnstile",
      src: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
      api: () => window.turnstile
    },
    recaptcha: {
      id: "rfb-recaptcha",
      src: "https://www.google.com/recaptcha/api.js?render=explicit",
      api: () => window.grecaptcha
    },
    hcaptcha: {
      id: "rfb-hcaptcha",
      src: "https://js.hcaptcha.com/1/api.js?render=explicit",
      api: () => window.hcaptcha
    }
  };
  var scriptPromises = /* @__PURE__ */ new Map();
  function waitForApi(provider, resolve) {
    const startedAt = Date.now();
    const poll = () => {
      const api = PROVIDERS[provider].api();
      if (api && typeof api.render === "function") {
        resolve(api);
      } else if (Date.now() - startedAt > TIMEOUT_MS) {
        resolve(null);
      } else {
        window.setTimeout(poll, 50);
      }
    };
    poll();
  }
  function loadScript(provider) {
    const cached = scriptPromises.get(provider);
    if (cached) return cached;
    const promise = new Promise((resolve) => {
      const definition = PROVIDERS[provider];
      if (!definition) {
        resolve(null);
        return;
      }
      if (definition.api()) {
        waitForApi(provider, resolve);
        return;
      }
      const existing = document.getElementById(definition.id);
      const script = existing instanceof HTMLScriptElement ? existing : Object.assign(document.createElement("script"), {
        id: definition.id,
        src: definition.src,
        async: true,
        defer: true
      });
      script.addEventListener("load", () => waitForApi(provider, resolve));
      script.addEventListener("error", () => resolve(null));
      if (!existing) document.head.appendChild(script);
    });
    scriptPromises.set(provider, promise);
    return promise;
  }
  function resolveConfig(instance) {
    const configured = instance.config?.captcha;
    if (configured !== void 0) {
      return Promise.resolve(isUsable(configured) ? configured : null);
    }
    if (!instance._captchaConfigPromise) {
      instance._captchaConfigPromise = getJson(
        instance,
        `/forms/${encodeURIComponent(instance.handle)}`
      ).then((payload) => isUsable(payload?.captcha) ? payload.captcha : null).catch(() => null);
    }
    return instance._captchaConfigPromise;
  }
  function isUsable(config) {
    return Boolean(
      config && typeof config.provider === "string" && PROVIDERS[config.provider] && typeof config.siteKey === "string" && config.siteKey !== ""
    );
  }
  function captchaHost(instance) {
    const sibling = instance.root.nextElementSibling;
    if (sibling && sibling.classList.contains("rfbd-captcha")) return sibling;
    const container = document.createElement("div");
    container.className = "rfbd-captcha";
    instance.root.insertAdjacentElement("afterend", container);
    return container;
  }
  async function getCaptchaToken(instance) {
    const config = await resolveConfig(instance);
    if (!config) return null;
    const challenge = await loadScript(config.provider);
    if (!challenge) return null;
    const container = captchaHost(instance);
    return new Promise((resolve) => {
      let isSettled = false;
      const settle = (token) => {
        if (isSettled) return;
        isSettled = true;
        resolve(typeof token === "string" && token !== "" ? token : null);
      };
      const timer = window.setTimeout(() => settle(null), TIMEOUT_MS);
      try {
        const slot = document.createElement("div");
        container.replaceChildren(slot);
        challenge.render(slot, {
          sitekey: config.siteKey,
          // Turnstile-only: reCAPTCHA has its own fixed size vocabulary.
          ...config.provider === "turnstile" ? { size: "flexible" } : {},
          callback: (token) => {
            window.clearTimeout(timer);
            settle(token);
          },
          "error-callback": () => {
            window.clearTimeout(timer);
            settle(null);
          }
        });
      } catch (error) {
        window.clearTimeout(timer);
        console.error("[rfbd] captcha could not render", error);
        settle(null);
      }
    });
  }

  // dev-template/src/runtime/submit.js
  var FALLBACK_FAILURE = "Something went wrong. Please try again.";
  var inFlight = /* @__PURE__ */ new WeakSet();
  async function submitForm(instance) {
    if (inFlight.has(instance)) return;
    const gateMessage = scheduleGateMessage(instance);
    if (gateMessage) {
      renderStatusError(instance, gateMessage);
      return;
    }
    const checked = validateAll(instance);
    if (hasErrors(checked.errors)) {
      surfaceErrors(instance, checked.errors, checked.firstErrorPage);
      return;
    }
    const values = {
      ...instance.store.values,
      ...computeDynamicHiddenValues(instance.document, instance.store.values)
    };
    if (!emit(instance, "rfb:before-submit", { values }, { cancelable: true })) return;
    inFlight.add(instance);
    emit(instance, "rfb:submitting");
    instance.root.classList.add("is-submitting");
    instance.render();
    let outcome;
    try {
      const captchaToken = await getCaptchaToken(instance);
      const bulkAddPromise = addBulkOrderLines(instance);
      try {
        outcome = await postJson(instance, "/submit", {
          handle: instance.handle,
          // The full value map, confirm keys and all , matching the widget:
          // answers are only pruned (inactive elements dropped, `:confirm`
          // stripped) server-side, where the live document rules them.
          values,
          locale: instance.locale,
          pageUrl: instance.context.pageUrl || window.location.href,
          pageTitle: instance.context.pageTitle || document.title,
          // Two cheap bot signals the server checks; a scripted post simply
          // omits them.
          elapsedMs: Date.now() - instance.mountedAt,
          honeypot: instance.honeypotValue ?? "",
          ...captchaToken ? { captchaToken } : {},
          // Attribution, filtered through the shared allow-list on both sides.
          trackingParams: collectTrackingParams()
        });
      } catch (error) {
        if ((error.status === 422 || error.status === 409) && error.body) {
          outcome = error.body;
        } else {
          console.error("[rfbd] submission failed", error);
          outcome = { ok: false, message: "" };
        }
      }
      const bulkResult = await bulkAddPromise;
      if (outcome.ok && bulkResult.ok === false) {
        outcome = { ...outcome, warning: bulkResult.description };
      }
      if (outcome.ok && outcome.submissionId) {
        if (collectCartItems(instance).length > 0) {
          await addConfiguredLineToCart(instance, outcome.submissionId);
        }
        clearDraftSafely(instance);
        emit(instance, "rfb:submitted", {
          outcome,
          submissionId: outcome.submissionId
        });
      }
    } finally {
      inFlight.delete(instance);
      instance.root.classList.remove("is-submitting");
    }
    if (outcome.ok) {
      await showOutcome(instance, outcome);
      return;
    }
    if (hasErrors(outcome.errors)) {
      surfaceErrors(instance, outcome.errors, firstErrorPageFor(instance, outcome.errors));
      return;
    }
    instance.render();
    renderErrorSummary(
      instance,
      outcome.message || errorMessage(instance, "errorSubmit", {}) || FALLBACK_FAILURE
    );
  }
  function hasErrors(errors) {
    return Boolean(errors) && Object.keys(errors).length > 0;
  }
  function surfaceErrors(instance, errors, firstErrorPage) {
    instance.store.setErrors(errors);
    if (typeof firstErrorPage === "number" && firstErrorPage !== instance.store.page) {
      instance.store.setPage(firstErrorPage);
    }
    focusFirstInvalid(instance);
  }
  function firstErrorPageFor(instance, errors) {
    const failing = new Set(
      Object.keys(errors).map((key) => {
        const dot = key.indexOf(".");
        return dot === -1 ? key : key.slice(0, dot);
      })
    );
    const pages = instance.document.pages ?? [];
    const index = pages.findIndex(
      (page) => (page.elements ?? []).some((element) => failing.has(element.id))
    );
    return index === -1 ? null : index;
  }
  function focusFirstInvalid(instance) {
    requestAnimationFrame(() => {
      const field = instance.root.querySelector(".rfbd-field.is-invalid");
      if (!field) return;
      const prefersReducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      ).matches;
      field.scrollIntoView({
        block: "center",
        behavior: prefersReducedMotion ? "auto" : "smooth"
      });
      const control = field.querySelector("input, select, textarea, button");
      control?.focus({ preventScroll: true });
    });
  }
  function scheduleGateMessage(instance) {
    const settings = instance.document.panels?.settings ?? {};
    if (!readBoolean(settings, "scheduleEnabled")) return "";
    const now = /* @__PURE__ */ new Date();
    const text = (id, fallback) => readText(settings, id, instance.locale) || fallback;
    const start2 = resolveTranslatable(readTranslatable(settings, "scheduleStart"));
    const opensAt = start2 ? new Date(start2) : null;
    if (opensAt && !Number.isNaN(opensAt.getTime()) && now < opensAt) {
      return text("scheduleNotOpenMessage", "This form isn't open yet.");
    }
    const end = resolveTranslatable(readTranslatable(settings, "scheduleEnd"));
    const closesAt = end ? new Date(end) : null;
    if (closesAt && !Number.isNaN(closesAt.getTime()) && now > closesAt) {
      return text("scheduleClosedMessage", "This form is closed.");
    }
    return "";
  }
  function clearNotices(instance) {
    instance.root.querySelectorAll(":scope .rfbd-status--error, :scope .rfbd-error-summary").forEach((node) => node.remove());
  }
  function renderStatusError(instance, message) {
    clearNotices(instance);
    instance.root.prepend(
      h(
        "div",
        { class: "rfbd-status rfbd-status--error", role: "alert" },
        h("p", { class: "rfbd-status__body" }, message)
      )
    );
  }
  function renderErrorSummary(instance, message) {
    clearNotices(instance);
    const body = instance.root.querySelector(".rfbd-body");
    const summary = h("p", { class: "rfbd-error-summary", role: "alert" }, message);
    if (body) body.prepend(summary);
    else instance.root.prepend(summary);
  }
  function clearDraftSafely(instance) {
    try {
      clearDraft(instance);
    } catch (error) {
      console.error("[rfbd] draft could not be cleared", error);
    }
  }

  // dev-template/src/runtime/pagination.js
  function checkmarkGlyph() {
    const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
    svg.setAttribute("class", "rfbd-stepper__check");
    svg.setAttribute("viewBox", "0 0 12 12");
    svg.setAttribute("width", "12");
    svg.setAttribute("height", "12");
    svg.setAttribute("fill", "none");
    svg.setAttribute("stroke", "currentColor");
    svg.setAttribute("stroke-width", "2");
    svg.setAttribute("stroke-linecap", "round");
    svg.setAttribute("stroke-linejoin", "round");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
    path.setAttribute("d", "M2.2 6.4l2.5 2.6 5.1-6");
    svg.append(path);
    return svg;
  }
  function renderStepper(instance) {
    const pages = instance.document.pages ?? [];
    if (pages.length <= 1) return null;
    const pageIndex = instance.store.page;
    const stepLabels = pages.map(
      (page) => resolveTranslatable(page.title, instance.locale)
    );
    const announced = stepLabels[pageIndex] || "Step";
    const stepper = h("div", {
      class: "rfbd-stepper",
      role: "progressbar",
      "aria-valuemin": "1",
      "aria-valuemax": String(stepLabels.length),
      "aria-valuenow": String(pageIndex + 1),
      "aria-valuetext": `${announced} ${pageIndex + 1} of ${stepLabels.length}`
    });
    stepLabels.forEach((stepLabel, index) => {
      const state = index < pageIndex ? "complete" : index === pageIndex ? "active" : "upcoming";
      if (index > 0) {
        stepper.append(
          h("span", {
            class: cx(
              "rfbd-stepper__line",
              state !== "upcoming" && "rfbd-stepper__line--complete"
            ),
            "aria-hidden": "true"
          })
        );
      }
      stepper.append(
        h(
          "span",
          { class: `rfbd-stepper__step rfbd-stepper__step--${state}`, "aria-hidden": "true" },
          h(
            "span",
            { class: "rfbd-stepper__marker" },
            state === "complete" ? checkmarkGlyph() : String(index + 1)
          ),
          stepLabel ? h("span", { class: "rfbd-stepper__label" }, stepLabel) : null
        )
      );
    });
    return stepper;
  }
  function handleNext(instance) {
    const errors = validatePage(instance, instance.store.page) ?? {};
    if (Object.keys(errors).length > 0) {
      instance.store.setErrors(errors);
      focusFirstInvalid2(instance);
      return;
    }
    instance.store.setErrors({});
    instance.store.setPage(instance.store.page + 1);
  }
  function focusFirstInvalid2(instance) {
    const field = instance.root.querySelector(".rfbd-field.is-invalid");
    if (!field) return;
    const control = field.querySelector("input, select, textarea, button");
    (control ?? field).focus?.({ preventScroll: true });
    field.scrollIntoView({ behavior: "smooth", block: "center" });
  }
  function handleReset(instance) {
    instance.store.replaceValues(
      computeInitialValues(instance.document, instance.context)
    );
    instance.store.setPage(0);
    emit(instance, "rfb:reset");
  }
  function renderFooter(instance) {
    const footer = instance.document.panels?.footer ?? {};
    const { store, locale } = instance;
    const pages = instance.document.pages ?? [];
    const isFirstPage = store.page === 0;
    const isLastPage = store.page >= pages.length - 1;
    const isSubmitting = instance.root.classList.contains("is-submitting");
    const text = (id, fallback) => readText(footer, id, locale) || fallback;
    const alignment = readString(footer, "alignment", "left");
    const modifier = readBoolean(footer, "fullWidthButton") ? "full" : alignment;
    const nodes = [];
    const footerText = footer.text;
    if (resolveTranslatable(footerText, locale)) {
      nodes.push(
        h(
          "div",
          { class: "rfbd-form__description" },
          renderRichText(footerText, instance)
        )
      );
    }
    const row = h("div", { class: `rfbd-form__footer rfbd-form__footer--${modifier}` });
    if (!isFirstPage) {
      row.append(
        h(
          "button",
          {
            type: "button",
            class: "rfbd-button rfbd-button--secondary",
            disabled: isSubmitting,
            onclick: () => store.setPage(store.page - 1)
          },
          text("previousLabel", "Previous")
        )
      );
    }
    if (isLastPage) {
      row.append(
        h(
          "button",
          {
            type: "button",
            class: "rfbd-button",
            disabled: isSubmitting,
            onclick: () => submitForm(instance)
          },
          isSubmitting ? "Submitting\u2026" : text("submitLabel", "Submit")
        )
      );
    } else {
      row.append(
        h(
          "button",
          {
            type: "button",
            class: "rfbd-button",
            disabled: isSubmitting,
            onclick: () => handleNext(instance)
          },
          text("nextLabel", "Next")
        )
      );
    }
    if (readBoolean(footer, "showResetButton")) {
      row.append(
        h(
          "button",
          {
            type: "button",
            class: "rfbd-button rfbd-button--secondary",
            disabled: isSubmitting,
            onclick: () => handleReset(instance)
          },
          "Reset"
        )
      );
    }
    nodes.push(row);
    return nodes;
  }

  // dev-template/src/runtime/renderer.js
  var instanceCounter = 0;
  function createInstance(host, payload) {
    const { config, context } = payload;
    const doc = config.document;
    const appearance = doc.panels?.appearance ?? {};
    const settingsPanel = doc.panels?.settings ?? {};
    const instance = {
      id: `rfbd-${instanceCounter += 1}`,
      host,
      handle: host.dataset.rfbdForm ?? "",
      config,
      document: doc,
      context,
      locale: host.dataset.rfbdLocale || context.locale || "en",
      labelPlacement: labelPlacement(appearance),
      mountedAt: Date.now(),
      visitorId: ensureVisitorId(),
      store: null,
      root: null,
      render,
      destroy,
      // Populated by layouts.js for float/popup; inline layouts keep the no-ops.
      open() {
      },
      close() {
      }
    };
    instance.store = createStore(computeInitialValues(doc, context));
    const layoutOverride = host.dataset.rfbdLayout || "";
    instance.layout = layoutOverride || readString(appearance, "layout", "default");
    instance.root = h("div", {
      class: cx("rfbd-form", `rfbd-form--${instance.layout}`),
      style: computeTokens(appearance),
      dataset: { rfbdInstance: instance.id }
    });
    let isDestroyed = false;
    let detachDrafts = () => {
    };
    function render() {
      if (isDestroyed) return;
      const focus = captureFocus(instance.root);
      instance.root.replaceChildren();
      instance.root.append(...renderBody(instance));
      restoreFocus(instance.root, focus);
    }
    function destroy() {
      isDestroyed = true;
      detachDrafts();
      instance.abortController?.abort();
      instance.root.remove();
      delete host.dataset.rfbdMounted;
    }
    instance.store.subscribe((change) => {
      if (change.kind === "value" || change.kind === "values") render();
      if (change.kind === "errors") render();
      if (change.kind === "page") {
        render();
        emit(instance, "rfb:page-change", { page: instance.store.page });
        const rect = instance.root.getBoundingClientRect();
        if (rect.bottom <= 0 || rect.top >= window.innerHeight) {
          instance.root.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }
    });
    render();
    applyLayout(instance);
    detachDrafts = attachDrafts(instance);
    host.dataset.rfbdMounted = "true";
    emit(instance, "rfb:loaded", { formId: config.formId });
    const afterLoad = readBoolean(settingsPanel, "afterLoadScriptEnabled") ? readString(settingsPanel, "afterLoadScript") : "";
    if (afterLoad) runMerchantScript(instance, afterLoad);
    return instance;
  }
  function renderBody(instance) {
    const doc = instance.document;
    const pages = doc.pages ?? [];
    const page = pages[Math.min(instance.store.page, pages.length - 1)] ?? { elements: [] };
    const header = doc.panels?.header ?? {};
    const nodes = [];
    if (readBoolean(header, "show", true)) {
      const alignment = readString(header, "alignment", "left");
      const eyebrow = readText(header, "eyebrow", instance.locale) || "DEALER WORDEN";
      const title = readText(header, "title", instance.locale);
      const description = header.description;
      nodes.push(
        h(
          "header",
          { class: cx("rfbd-header", alignment !== "left" && `rfbd-header--${alignment}`) },
          eyebrow ? h("p", { class: "rfbd-header__eyebrow" }, eyebrow) : null,
          title ? h("h2", { class: "rfbd-header__title" }, title) : null,
          description ? h(
            "div",
            { class: "rfbd-header__description" },
            renderRichText(description, instance)
          ) : null
        )
      );
    }
    if (pages.length > 1) nodes.push(renderStepper(instance));
    const fields = h("div", {
      class: "rfbd-fields",
      dataset: { labelPlacement: instance.labelPlacement }
    });
    for (const element of page.elements ?? []) {
      const rendered = renderField(element, instance);
      if (rendered) fields.append(rendered);
    }
    const footerNodes = renderFooter(instance);
    const isLastPage = instance.store.page >= pages.length - 1;
    let footerPlacement = footerNodes;
    const consentFields = fields.querySelectorAll('[data-rfbd-type="consent"]');
    const agreementField = consentFields[consentFields.length - 1];
    if (isLastPage && agreementField) {
      fields.append(
        h(
          "div",
          { class: "rfbd-agreement-submit-row" },
          agreementField,
          ...footerNodes
        )
      );
      footerPlacement = [];
    }
    const form = h(
      "form",
      {
        class: "rfbd-body",
        novalidate: true,
        onsubmit: (event) => event.preventDefault()
      },
      fields,
      honeypot(instance),
      ...footerPlacement
    );
    const bodyWrap = h("div", { class: "rfbd-body-wrap" }, form);
    nodes.push(withMedia(instance, bodyWrap));
    return [
      h(
        "div",
        { class: "rfbd-form__content" },
        ...nodes
      )
    ];
  }
  function honeypot(instance) {
    const input = h("input", {
      class: "rfbd-hp",
      type: "text",
      name: "rfb-company-website",
      tabindex: "-1",
      autocomplete: "off",
      "aria-hidden": "true"
    });
    input.addEventListener("input", () => {
      instance.honeypotValue = input.value;
    });
    return input;
  }
  function withMedia(instance, form) {
    const appearance = instance.document.panels?.appearance ?? {};
    const position = readString(appearance, "image", "none");
    const url = readString(appearance, "imageUrl", "");
    if (position === "none" || !/^https:\/\//i.test(url)) return form;
    return h(
      "div",
      { class: cx("rfbd-media", `rfbd-media--${position}`) },
      h(
        "div",
        { class: "rfbd-media__image" },
        h("img", { src: url, alt: "", loading: "lazy" })
      ),
      h("div", { class: "rfbd-media__content" }, form)
    );
  }
  function activeElements(instance) {
    const values = instance.store.values;
    const active = [];
    for (const page of instance.document.pages ?? []) {
      for (const element of page.elements ?? []) {
        if (isElementActive(element, values)) active.push(element);
      }
    }
    return active;
  }
  function ensureVisitorId() {
    try {
      const KEY = "rfb-visitor-id";
      let id = window.localStorage.getItem(KEY);
      if (!id) {
        id = uuid();
        window.localStorage.setItem(KEY, id);
      }
      return id;
    } catch {
      return "";
    }
  }
  function captureFocus(root) {
    const active = document.activeElement;
    if (!active || !root.contains(active)) return null;
    const field = active.closest("[data-rfbd-el]");
    return {
      elementId: field?.dataset.rfbdEl ?? "",
      name: active.getAttribute("name") ?? "",
      selectionStart: "selectionStart" in active ? active.selectionStart : null,
      selectionEnd: "selectionEnd" in active ? active.selectionEnd : null
    };
  }
  function restoreFocus(root, focus) {
    if (!focus || !focus.elementId) return;
    const field = root.querySelector(`[data-rfbd-el="${CSS.escape(focus.elementId)}"]`);
    if (!field) return;
    const target = focus.name ? field.querySelector(`[name="${CSS.escape(focus.name)}"]`) : field.querySelector("input, textarea, select, button");
    if (!target) return;
    target.focus({ preventScroll: true });
    try {
      if (focus.selectionStart !== null && "setSelectionRange" in target) {
        target.setSelectionRange(focus.selectionStart, focus.selectionEnd);
      }
    } catch {
    }
  }

  // dev-template/src/runtime/cart.js
  function toProductSelections(value) {
    if (!Array.isArray(value)) return [];
    return value.flatMap((entry) => {
      if (typeof entry === "string" && entry !== "") {
        return [{ variantId: entry, quantity: 1 }];
      }
      if (typeof entry === "object" && entry !== null && typeof entry.variantId === "string") {
        const quantity = Number(entry.quantity);
        return [
          {
            variantId: entry.variantId,
            quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : 1
          }
        ];
      }
      return [];
    });
  }
  function cartAddUrl(instance) {
    return instance.context.cartAddUrl || "/cart/add.js";
  }
  function toNumericId(gid) {
    return gid.match(/\/ProductVariant\/(\d+)/)?.[1] ?? null;
  }
  function collectCartItems(instance) {
    const items = [];
    for (const element of activeElements(instance)) {
      if (element.type !== "product") continue;
      if (!readBoolean(element.settings, "addToCart")) continue;
      for (const line of toProductSelections(instance.store.get(element.id))) {
        items.push({ id: line.variantId, quantity: line.quantity });
      }
    }
    return items;
  }
  async function addConfiguredLineToCart(instance, submissionId) {
    const items = collectCartItems(instance);
    if (items.length === 0) return false;
    const id = toNumericId(items[0].id);
    if (!id) return false;
    try {
      const response = await fetch(cartAddUrl(instance), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: Number(id),
              quantity: 1,
              properties: {
                // Underscore-prefixed so Shopify treats it as private: it is
                // machine data, and a JSON blob in the cart would confuse a
                // shopper rather than inform them.
                _rfb: JSON.stringify({ submissionId, items })
              }
            }
          ]
        })
      });
      return response.ok;
    } catch (error) {
      console.error("[rfbd] could not add configured line", error);
      return false;
    }
  }
  async function addBulkOrderLines(instance) {
    const items = [];
    for (const element of activeElements(instance)) {
      if (element.type !== "bulk-order") continue;
      if (!readBoolean(element.settings, "addToCart", true)) continue;
      for (const line of toProductSelections(instance.store.get(element.id))) {
        items.push({ id: line.variantId, quantity: line.quantity });
      }
    }
    const lines = items.flatMap((item) => {
      const id = toNumericId(item.id);
      return id ? [{ id: Number(id), quantity: item.quantity }] : [];
    });
    if (lines.length === 0) return { ok: true };
    try {
      const response = await fetch(cartAddUrl(instance), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ items: lines })
      });
      if (response.ok) return { ok: true };
      const body = await response.json().catch(() => ({}));
      return {
        ok: false,
        description: body.description || body.message || "The items could not be added to the cart."
      };
    } catch {
      return { ok: false, description: "The items could not be added to the cart." };
    }
  }

  // dev-template/src/elements/product.js
  function clampLimit(value, fallback = 12) {
    const parsed = typeof value === "number" ? value : typeof value === "string" && value.trim() !== "" ? Number(value) : Number.NaN;
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(50, Math.max(1, Math.trunc(parsed)));
  }
  var states = /* @__PURE__ */ new WeakMap();
  function elementState(instance, elementId) {
    let byElement = states.get(instance);
    if (!byElement) {
      byElement = /* @__PURE__ */ new Map();
      states.set(instance, byElement);
    }
    let state = byElement.get(elementId);
    if (!state) {
      state = {
        query: "",
        products: [],
        status: "loading",
        key: null,
        controller: null,
        timer: 0
      };
      byElement.set(elementId, state);
    }
    return state;
  }
  function ensureFetch(ctx, state, merchantQuery, limit, delayMs) {
    const key = `${merchantQuery}\0${state.query}\0${limit}`;
    if (state.key === key) return;
    state.key = key;
    state.status = "loading";
    window.clearTimeout(state.timer);
    state.controller?.abort();
    state.timer = window.setTimeout(() => {
      const controller = new AbortController();
      state.controller = controller;
      const params = new URLSearchParams();
      const query = [merchantQuery, state.query].filter(Boolean).join(" ");
      if (query) params.set("q", query);
      params.set("limit", String(limit));
      proxyFetch(ctx.instance, `/products?${params.toString()}`, {
        headers: { Accept: "application/json" },
        signal: controller.signal
      }).then(async (response) => {
        if (!response.ok) throw new Error(`Product search failed with ${response.status}`);
        const body = await response.json();
        state.products = Array.isArray(body.products) ? body.products : [];
        state.status = "ready";
        ctx.requestRender();
      }).catch((cause) => {
        if (controller.signal.aborted) return;
        console.error("[rfbd] product search failed", cause);
        state.status = "failed";
        ctx.requestRender();
      });
    }, delayMs);
  }
  function capVariantTiles(products, limit) {
    return products.flatMap(
      (product) => (product.variants ?? []).filter((variant) => variant.isAvailable).map((variant) => ({ product, variant }))
    ).slice(0, limit);
  }
  function toSelected3(value) {
    return toProductSelections(value).map((line) => line.variantId);
  }
  define("product", {
    render(ctx) {
      const { instance, element } = ctx;
      const state = elementState(instance, element.id);
      const controlId = `${instance.id}-${element.id}`;
      const allowsMultiple = ctx.bool("allowMultiple", true);
      const allowsSearch = ctx.bool("enableSearch", true);
      const allowsQuantity = ctx.bool("enableQuantity");
      const perRow = readString(ctx.settings, "optionsPerLine", "3");
      const merchantQuery = readString(ctx.settings, "searchQuery");
      const limit = clampLimit(ctx.settings.limit);
      ensureFetch(ctx, state, merchantQuery, limit, 0);
      const selected = toSelected3(ctx.value);
      const store = (variantIds) => {
        if (!allowsQuantity) {
          ctx.emitChange(variantIds);
          return;
        }
        const quantities = new Map(
          toProductSelections(ctx.value).map((line) => [line.variantId, line.quantity])
        );
        ctx.emitChange(
          variantIds.map((variantId) => ({
            variantId,
            quantity: quantities.get(variantId) ?? 1
          }))
        );
      };
      const choose = (variantId) => {
        if (!allowsMultiple) {
          store(selected.includes(variantId) ? [] : [variantId]);
          return;
        }
        store(
          selected.includes(variantId) ? selected.filter((entry) => entry !== variantId) : [...selected, variantId]
        );
      };
      const setQuantity = (variantId, quantity) => {
        const clamped = Number.isFinite(quantity) && quantity > 0 ? Math.floor(quantity) : 1;
        ctx.emitChange(
          toProductSelections(ctx.value).map(
            (line) => line.variantId === variantId ? { ...line, quantity: clamped } : line
          )
        );
      };
      const tiles = capVariantTiles(state.products, limit);
      const container = h("div", { class: "rfbd-product" });
      if (allowsSearch) {
        const search = h("input", {
          type: "search",
          class: "rfbd-input rfbd-product__search",
          name: "product-search",
          placeholder: "Search products",
          value: state.query,
          "aria-label": `Search within ${ctx.chrome.label}`
        });
        search.addEventListener("input", () => {
          state.query = search.value;
          ensureFetch(ctx, state, merchantQuery, limit, 250);
        });
        container.append(search);
      }
      container.append(
        h(
          "p",
          { class: "rfbd-visually-hidden", role: "status" },
          state.status === "loading" ? "Loading products" : state.status === "failed" ? "Products could not be loaded" : `${tiles.length} products available`
        )
      );
      if (state.status === "failed") {
        container.append(
          h(
            "p",
            { class: "rfbd-field__error" },
            "Products could not be loaded. Please refresh and try again."
          )
        );
        return container;
      }
      if (state.status === "ready" && tiles.length === 0) {
        container.append(
          h("p", { class: "rfbd-field__description" }, "No products match that search.")
        );
        return container;
      }
      const grid = h("div", {
        class: "rfbd-visual rfbd-visual--image",
        style: { "--rfbd-visual-columns": perRow },
        "aria-busy": state.status === "loading" ? "true" : null
      });
      for (const { product, variant } of tiles) {
        const optionId = `${controlId}-${variant.id}`;
        const name = (product.variants ?? []).length > 1 ? `${product.title} \u2014 ${variant.title}` : product.title;
        const isSelected = selected.includes(variant.id);
        const quantityInput = allowsQuantity && isSelected ? h("input", {
          class: "rfbd-input rfbd-visual__quantity",
          type: "number",
          min: "1",
          name: `qty-${variant.id}`,
          "aria-label": `Quantity of ${name}`,
          value: String(
            toProductSelections(ctx.value).find(
              (line) => line.variantId === variant.id
            )?.quantity ?? 1
          ),
          // Inside the tile's label: a click must adjust the number,
          // not toggle the selection underneath it.
          onclick: (event) => event.preventDefault(),
          oninput: (event) => setQuantity(variant.id, Number(event.currentTarget.value))
        }) : null;
        grid.append(
          h(
            "label",
            { class: "rfbd-visual__option", for: optionId },
            h("input", {
              id: optionId,
              class: "rfbd-visual__input",
              type: allowsMultiple ? "checkbox" : "radio",
              name: `${controlId}-product`,
              value: variant.id,
              checked: isSelected,
              onchange: () => choose(variant.id)
            }),
            h(
              "span",
              { class: "rfbd-visual__media" },
              product.imageUrl ? h("img", { src: product.imageUrl, alt: "", loading: "lazy" }) : h("span", { class: "rfbd-visual__placeholder", "aria-hidden": "true" })
            ),
            h("span", { class: "rfbd-visual__label" }, name),
            quantityInput
          )
        );
      }
      container.append(grid);
      return container;
    },
    /**
     * The selection and quantity limits, mirroring form-core's
     * `validateProductLimits` — same gates, same message keys, so a document
     * validates identically here and on the server.
     */
    validate(value, ctx) {
      const selections = toProductSelections(value);
      if (selections.length === 0) return "";
      const { instance } = ctx;
      if (ctx.bool("limitSelections")) {
        const min = ctx.optNum("minSelections");
        const max = ctx.optNum("maxSelections");
        if (min !== null && selections.length < min) {
          return errorMessage(instance, "errorMinSelections", { min });
        }
        if (max !== null && selections.length > max) {
          return errorMessage(instance, "errorMaxSelections", { max });
        }
      }
      if (ctx.bool("limitTotalQuantity")) {
        const total = selections.reduce((sum, line) => sum + line.quantity, 0);
        const min = ctx.optNum("minTotalQuantity");
        const max = ctx.optNum("maxTotalQuantity");
        if (min !== null && total < min) {
          return errorMessage(instance, "errorMinTotalQuantity", { min });
        }
        if (max !== null && total > max) {
          return errorMessage(instance, "errorMaxTotalQuantity", { max });
        }
      }
      if (ctx.bool("limitPerProductQuantity")) {
        const min = ctx.optNum("minPerProductQuantity");
        const max = ctx.optNum("maxPerProductQuantity");
        for (const line of selections) {
          if (min !== null && line.quantity < min) {
            return errorMessage(instance, "errorMinPerProduct", { min });
          }
          if (max !== null && line.quantity > max) {
            return errorMessage(instance, "errorMaxPerProduct", { max });
          }
        }
      }
      return "";
    }
  });

  // dev-template/src/elements/bulk-order.js
  var DECREASE_PATH = "M5 12h14";
  var INCREASE_PATH = "M12 5v14M5 12h14";
  var states2 = /* @__PURE__ */ new WeakMap();
  function elementState2(instance, elementId) {
    let byElement = states2.get(instance);
    if (!byElement) {
      byElement = /* @__PURE__ */ new Map();
      states2.set(instance, byElement);
    }
    let state = byElement.get(elementId);
    if (!state) {
      state = {
        status: "loading",
        products: [],
        cartQuantities: /* @__PURE__ */ new Map(),
        started: false
      };
      byElement.set(elementId, state);
    }
    return state;
  }
  function readSelectedProducts(settings) {
    const value = settings?.products;
    if (!Array.isArray(value)) return [];
    return value.filter(
      (entry) => typeof entry === "object" && entry !== null && typeof entry.id === "string" && typeof entry.handle === "string" && typeof entry.title === "string"
    );
  }
  async function fetchLiveProducts(instance, handles) {
    const response = await proxyFetch(
      instance,
      `/products?handles=${encodeURIComponent(handles.join(","))}`,
      { headers: { Accept: "application/json" } }
    );
    if (!response.ok) throw new Error(`products ${response.status}`);
    const body = await response.json();
    return (body.products ?? []).filter((product) => Boolean(product.handle)).map((product) => ({
      handle: product.handle,
      title: product.title ?? "",
      imageUrl: product.imageUrl ?? null,
      variants: (product.variants ?? []).filter((variant) => Boolean(variant.id)).map((variant) => ({
        id: variant.id,
        title: variant.title ?? "",
        isAvailable: variant.isAvailable !== false,
        price: Number(variant.price ?? "0"),
        currency: variant.currency ?? "",
        quantityAvailable: typeof variant.quantityAvailable === "number" ? variant.quantityAvailable : null
      }))
    }));
  }
  async function fetchCartQuantities() {
    try {
      const response = await fetch("/cart.js", {
        headers: { Accept: "application/json" }
      });
      const body = await response.json();
      const quantities = /* @__PURE__ */ new Map();
      for (const item of body.items ?? []) {
        if (typeof item.variant_id !== "number") continue;
        const key = String(item.variant_id);
        quantities.set(key, (quantities.get(key) ?? 0) + (item.quantity ?? 0));
      }
      return quantities;
    } catch {
      return /* @__PURE__ */ new Map();
    }
  }
  function numericVariantId(gid) {
    return gid.split("/").pop() ?? gid;
  }
  function remainingStock(quantityAvailable, inCart) {
    if (quantityAvailable === null) return null;
    return Math.max(0, quantityAvailable - inCart);
  }
  function clampQuantity(raw, remaining) {
    const wanted = Number.isFinite(raw) && raw > 0 ? Math.floor(raw) : 1;
    if (remaining === null) return wanted;
    return Math.max(1, Math.min(wanted, Math.max(1, remaining)));
  }
  function computeSubtotal(selections, priceByVariant) {
    return selections.reduce((sum, line) => {
      const price = priceByVariant.get(line.variantId);
      return price === void 0 ? sum : sum + price * line.quantity;
    }, 0);
  }
  function ensureData(ctx, state) {
    if (state.started) return;
    state.started = true;
    const chosen = readSelectedProducts(ctx.settings);
    if (chosen.length === 0) {
      state.products = [];
      state.status = "ready";
      return;
    }
    Promise.all([
      fetchLiveProducts(ctx.instance, chosen.map((product) => product.handle)),
      fetchCartQuantities()
    ]).then(([live, cart]) => {
      state.products = live;
      state.cartQuantities = cart;
      state.status = "ready";
      ctx.requestRender();
    }).catch((cause) => {
      console.error("[rfbd] bulk-order data failed", cause);
      state.status = "failed";
      ctx.requestRender();
    });
  }
  define("bulk-order", {
    render(ctx) {
      const { instance, element } = ctx;
      const state = elementState2(instance, element.id);
      const controlId = `${instance.id}-${element.id}`;
      ensureData(ctx, state);
      if (state.status === "loading") {
        return h(
          "p",
          { class: "rfbd-field__description", role: "status" },
          "Loading products\u2026"
        );
      }
      if (state.status === "failed") {
        return h(
          "p",
          { class: "rfbd-field__error" },
          "Products could not be loaded. Please refresh and try again."
        );
      }
      if (state.products.length === 0) {
        return h(
          "p",
          { class: "rfbd-field__description" },
          "No products are available right now."
        );
      }
      const showPrices = readBoolean(ctx.settings, "showPrices", true);
      const hideUnavailable = readBoolean(ctx.settings, "hideUnavailableVariants");
      const selections = toProductSelections(ctx.value);
      const selectionByVariant = new Map(
        selections.map((line) => [line.variantId, line.quantity])
      );
      const priceByVariant = new Map(
        state.products.flatMap(
          (product) => product.variants.map((variant) => [variant.id, variant.price])
        )
      );
      const currency = state.products[0]?.variants[0]?.currency || "USD";
      const money = new Intl.NumberFormat(instance.locale, {
        style: "currency",
        currency
      });
      const store = (next) => ctx.emitChange(next.length > 0 ? next : []);
      const toggle = (variant) => {
        if (selectionByVariant.has(variant.id)) {
          store(selections.filter((line) => line.variantId !== variant.id));
        } else {
          store([...selections, { variantId: variant.id, quantity: 1 }]);
        }
      };
      const setQuantity = (variant, raw) => {
        const remaining = remainingStock(
          variant.quantityAvailable,
          state.cartQuantities.get(numericVariantId(variant.id)) ?? 0
        );
        const quantity = clampQuantity(raw, remaining);
        store(
          selections.map(
            (line) => line.variantId === variant.id ? { ...line, quantity } : line
          )
        );
      };
      const table = h("div", { class: "rfbd-bulk" });
      for (const product of state.products) {
        const card = h(
          "div",
          { class: "rfbd-bulk__product" },
          h(
            "div",
            { class: "rfbd-bulk__product-head" },
            product.imageUrl ? h("img", {
              class: "rfbd-bulk__image",
              src: product.imageUrl,
              alt: "",
              loading: "lazy"
            }) : null,
            h("span", { class: "rfbd-bulk__title" }, product.title)
          )
        );
        for (const variant of product.variants) {
          if (hideUnavailable && !variant.isAvailable) continue;
          const remaining = remainingStock(
            variant.quantityAvailable,
            state.cartQuantities.get(numericVariantId(variant.id)) ?? 0
          );
          const isSoldOut = !variant.isAvailable || remaining === 0;
          const quantity = selectionByVariant.get(variant.id);
          const isChosen = quantity !== void 0;
          const rowId = `${controlId}-${numericVariantId(variant.id)}`;
          const variantName = `${product.title} ${variant.title}`;
          const row = h(
            "div",
            {
              class: `rfbd-bulk__row${isSoldOut ? " rfbd-bulk__row--soldout" : ""}`
            },
            h(
              "label",
              { class: "rfbd-bulk__pick", for: rowId },
              h("input", {
                id: rowId,
                type: "checkbox",
                name: `pick-${numericVariantId(variant.id)}`,
                checked: isChosen,
                disabled: isSoldOut,
                onchange: () => toggle(variant)
              }),
              h("span", { class: "rfbd-bulk__variant" }, variant.title || product.title)
            )
          );
          if (isSoldOut) {
            row.append(
              h("span", { class: "rfbd-bulk__soldout" }, ctx.text("soldOutLabel", "Sold out"))
            );
          } else {
            if (isChosen) {
              const stepButton = (direction) => h(
                "button",
                {
                  type: "button",
                  class: "rfbd-bulk__step",
                  "aria-label": `${direction === "increase" ? "Increase" : "Decrease"} quantity of ${variantName}`,
                  disabled: direction === "increase" ? remaining !== null && quantity >= remaining : quantity <= 1,
                  onclick: () => setQuantity(
                    variant,
                    direction === "increase" ? quantity + 1 : quantity - 1
                  )
                },
                iconSvg(direction === "increase" ? INCREASE_PATH : DECREASE_PATH)
              );
              row.append(
                h(
                  "div",
                  { class: "rfbd-bulk__stepper" },
                  stepButton("decrease"),
                  h("input", {
                    class: "rfbd-input rfbd-bulk__quantity",
                    type: "number",
                    min: "1",
                    max: remaining !== null ? String(remaining) : null,
                    name: `qty-${numericVariantId(variant.id)}`,
                    value: String(quantity),
                    "aria-label": `Quantity of ${variantName}`,
                    oninput: (event) => setQuantity(variant, Number(event.currentTarget.value))
                  }),
                  stepButton("increase")
                )
              );
            }
            if (showPrices) {
              row.append(
                h(
                  "span",
                  { class: "rfbd-bulk__price" },
                  money.format(isChosen ? variant.price * quantity : variant.price)
                )
              );
            }
          }
          card.append(row);
        }
        table.append(card);
      }
      if (showPrices && selections.length > 0) {
        table.append(
          h(
            "div",
            { class: "rfbd-bulk__subtotal" },
            h("span", null, ctx.text("subtotalLabel", "Subtotal")),
            h("span", null, money.format(computeSubtotal(selections, priceByVariant)))
          )
        );
      }
      return table;
    }
  });

  // dev-template/src/runtime/api.js
  var instancesByHandle = /* @__PURE__ */ new Map();
  function registerInstance(instance) {
    const set = instancesByHandle.get(instance.handle) ?? /* @__PURE__ */ new Set();
    set.add(instance);
    instancesByHandle.set(instance.handle, set);
  }
  function unregisterInstance(instance) {
    instancesByHandle.get(instance.handle)?.delete(instance);
  }
  function instancesOf(handle) {
    return [...instancesByHandle.get(handle) ?? []];
  }
  function firstInstance(handle) {
    return instancesOf(handle)[0] ?? null;
  }
  function isLauncher(instance) {
    return instance.layout === "float" || instance.layout === "popup";
  }
  function resetInstance(instance) {
    instance.store.replaceValues(
      computeInitialValues(instance.document, instance.context)
    );
    instance.store.setPage(0);
    emit(instance, "rfb:reset");
  }
  function validateAll2(instance) {
    const pages = instance.document.pages ?? [];
    const merged = {};
    pages.forEach((_page, index) => {
      Object.assign(merged, validatePage(instance, index) ?? {});
    });
    instance.store.setErrors(merged);
    return Object.keys(merged).length === 0;
  }
  function installPublicApi() {
    if (window.RFBD) return;
    window.RFBD = Object.freeze({
      version: 1,
      /** Handles of every form currently mounted on the page. */
      forms: () => [...instancesByHandle.entries()].filter(([, set]) => set.size > 0).map(([handle]) => handle),
      /**
       * Opens a launcher form's panel. On an inline layout there is no panel
       * to open, so the honest equivalent is bringing the form into view.
       */
      open(handle) {
        for (const instance of instancesOf(handle)) {
          if (isLauncher(instance)) {
            instance.open();
          } else {
            instance.root.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }
      },
      close(handle) {
        for (const instance of instancesOf(handle)) instance.close();
      },
      /** Back to initial answers and page one, announcing `rfb:reset`. */
      reset(handle) {
        for (const instance of instancesOf(handle)) resetInstance(instance);
      },
      goToPage(handle, page) {
        const instance = firstInstance(handle);
        if (!instance) return;
        const pageCount = (instance.document.pages ?? []).length;
        instance.store.setPage(clamp(Number(page) || 0, 0, Math.max(0, pageCount - 1)));
      },
      /** A copy , mutating the return value never reaches the form. */
      getValues(handle) {
        const instance = firstInstance(handle);
        return instance ? { ...instance.store.values } : null;
      },
      setValue(handle, elementId, value) {
        firstInstance(handle)?.store.set(elementId, value);
      },
      /** Validates every page; errors render in place. True when clean. */
      validate(handle) {
        const instance = firstInstance(handle);
        return instance ? validateAll2(instance) : false;
      },
      /**
       * The element-registry extension point, re-exported. Because element
       * modules register at import time and boot runs after, calling this
       * before `DOMContentLoaded` (or before this script's own tag, via a
       * queued wrapper) replaces a built-in renderer for every form on the
       * page , the documented override point.
       */
      define,
      /** Introspection for debugging: one info record per mounted form. */
      instances: () => [...instancesByHandle.values()].flatMap(
        (set) => [...set].map((instance) => ({
          handle: instance.handle,
          id: instance.id,
          layout: instance.layout,
          locale: instance.locale,
          page: instance.store.page,
          mountedAt: instance.mountedAt
        }))
      )
    });
  }

  // dev-template/src/runtime/boot.js
  var HOST_SELECTOR = "[data-rfbd-form]";
  var WIDGET_HOST_ATTRIBUTE = "data-rich-form-builder";
  var mountedInstances = /* @__PURE__ */ new WeakMap();
  function isDesignMode(payload) {
    return payload?.context?.designMode === true || window.Shopify?.designMode === true;
  }
  function showDesignNotice(host, message, tone = "error") {
    const notice = h("p", { class: `rfbd-status rfbd-status--${tone}` }, message);
    notice.style.cssText = "margin:0 0 8px;padding:12px 16px;border:1px dashed currentColor;border-radius:8px;font-size:14px;line-height:1.4;opacity:0.7;";
    if (tone === "error") {
      host.replaceChildren(notice);
    } else {
      host.prepend(notice);
    }
  }
  function findConfigScript(host, handle) {
    const selector = `script[type="application/json"][data-rfbd-config="${CSS.escape(handle)}"]`;
    return host.querySelector(selector) ?? document.querySelector(selector);
  }
  function mountHost(host) {
    if (host.dataset.rfbdMounted) return;
    const handle = host.dataset.rfbdForm ?? "";
    const script = handle ? findConfigScript(host, handle) : null;
    const payload = parseJsonScript(script);
    if (!payload?.config?.document) {
      const message = `Rich Form Builder template: no readable config found for form "${handle}". Expected a <script type="application/json" data-rfbd-config="${handle}"> tag next to the host element.`;
      if (isDesignMode(payload)) {
        showDesignNotice(host, message, "error");
      } else {
        console.error(`[rfbd] ${message}`);
      }
      return;
    }
    const widgetHost = document.querySelector(
      `[${WIDGET_HOST_ATTRIBUTE}="${CSS.escape(handle)}"]`
    );
    if (widgetHost) {
      const warning = `Rich Form Builder: form "${handle}" is also placed via the app's own block on this page, so it will render twice. Remove one placement.`;
      if (isDesignMode(payload)) {
        showDesignNotice(host, warning, "warning");
      } else {
        console.warn(`[rfbd] ${warning}`);
      }
    }
    const instance = createInstance(host, payload);
    registerInstance(instance);
    mountedInstances.set(host, instance);
  }
  function mountAll(root) {
    root.querySelectorAll(HOST_SELECTOR).forEach((host) => mountHost(host));
  }
  function unmountWithin(root) {
    root.querySelectorAll(HOST_SELECTOR).forEach((host) => {
      const instance = mountedInstances.get(host);
      if (!instance) return;
      unregisterInstance(instance);
      instance.destroy();
      mountedInstances.delete(host);
    });
  }
  function start() {
    mountAll(document);
    document.addEventListener("shopify:section:load", (event) => {
      if (event.target instanceof HTMLElement) mountAll(event.target);
    });
    document.addEventListener("shopify:section:unload", (event) => {
      if (event.target instanceof HTMLElement) unmountWithin(event.target);
    });
    document.addEventListener("click", (event) => {
      const trigger = event.target instanceof Element && event.target.closest(".rfbd-open[data-rfbd-form]");
      if (!trigger) return;
      event.preventDefault();
      window.RFBD?.open(trigger.dataset.rfbdForm ?? "");
    });
  }
  function boot() {
    if (window.__rfbdBooted) return;
    window.__rfbdBooted = true;
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", start, { once: true });
    } else {
      start();
    }
  }

  // dev-template/src/index.js
  installPublicApi();
  boot();
})();
